import { useState, useEffect, useCallback, useMemo } from 'react';
import { format, addDays, startOfWeek, addMonths, isAfter, isBefore, startOfDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import axios from 'axios';
import { Calendar, Clock, User, Mail, Phone, Heart, CheckCircle2, AlertCircle, Plus, Trash2, Users, ArrowLeft, Edit2, MapPin, CreditCard, Loader2, Lock, ExternalLink, DollarSign, Tag, X, Info, Globe, Shield, CheckCircle, XCircle, AlertTriangle, ClipboardList, Check, Building2, FileText } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import { formatPhoneNumber } from '../utils/formatters';
import StripePaymentForm from '@/components/StripePaymentForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingPage = () => {
  const [classTypes, setClassTypes] = useState([]);
  const [courseCategories, setCourseCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedCourseType, setSelectedCourseType] = useState(null); // First step: course type group
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [availability, setAvailability] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [students, setStudents] = useState([{ 
    id: Date.now(), 
    firstName: '', 
    lastName: '', 
    email: '', 
    phone: '',
    ahaEcardCode: '',
    ahaVerificationStatus: null,
    ahaVerifying: false,
    manuallyVerified: false,
    errors: {}
  }]);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [userBookings, setUserBookings] = useState([]);
  const [isWithin48Hours, setIsWithin48Hours] = useState(false);
  
  // Address state
  const [classAddress, setClassAddress] = useState({
    street: '',
    city: '',
    state: 'NV',
    zip: ''
  });
  const [addressErrors, setAddressErrors] = useState({});
  const [showTravelFeeWarning, setShowTravelFeeWarning] = useState(false);
  const [isAddressValidated, setIsAddressValidated] = useState(false);
  const [formattedAddress, setFormattedAddress] = useState('');
  const [locationName, setLocationName] = useState(''); // Custom location name
  const [googlePlaceName, setGooglePlaceName] = useState(''); // Name from Google Places
  const [instructorNotes, setInstructorNotes] = useState('');
  const [renewalReminderOptIn, setRenewalReminderOptIn] = useState(true);  // Default to opted in
  
  // Edit mode state
  const [editingBookingId, setEditingBookingId] = useState(null);
  const [pendingBooking, setPendingBooking] = useState(null);
  const [showPendingBanner, setShowPendingBanner] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  
  // Pricing preview state
  const [pricingPreview, setPricingPreview] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [selectedClassPrice, setSelectedClassPrice] = useState(null); // Price per student for selected class
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null); // null = not applied, object = applied coupon data
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  
  // Reference code state for booking lookup
  const [referenceCode, setReferenceCode] = useState('');  // For new bookings
  const [lookupReferenceCode, setLookupReferenceCode] = useState('');  // For add-on lookup
  const [lookupMode, setLookupMode] = useState('code');  // 'datetime' or 'code' - default to code
  
  // Add to Previous Booking state
  const [isAddOnMode, setIsAddOnMode] = useState(false);
  const [addOnLookupDate, setAddOnLookupDate] = useState('');
  const [addOnLookupTime, setAddOnLookupTime] = useState('');
  const [foundBooking, setFoundBooking] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [addOnConfirmed, setAddOnConfirmed] = useState(false);
  const [addonPricing, setAddonPricing] = useState(null);
  const [addonPricingLoading, setAddonPricingLoading] = useState(false);
  
  // Product Add-Ons state (from WooCommerce)
  const [availableAddons, setAvailableAddons] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState({}); // { productId: { selected: bool, quantity: number } }
  const [addonsLoading, setAddonsLoading] = useState(false);
  
  // Stripe checkout state
  const [showStripeCheckout, setShowStripeCheckout] = useState(false);
  const [checkoutData, setCheckoutData] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentTermsAccepted, setPaymentTermsAccepted] = useState(false);
  
  // Payment config state
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [paymentConfigLoading, setPaymentConfigLoading] = useState(true);

  // Class Details modal state
  const [showClassDetails, setShowClassDetails] = useState(false);
  
  // AHA Mismatch Warning Modal state
  const [ahaMismatchModal, setAhaMismatchModal] = useState({
    show: false,
    studentId: null,
    enteredName: '',
    ahaName: '',
    verificationResult: null
  });
  
  // Terms and Conditions modal state
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsContent, setTermsContent] = useState('');
  const [termsTitle, setTermsTitle] = useState('Terms and Conditions');
  const [termsLoading, setTermsLoading] = useState(false);
  const [classDetails, setClassDetails] = useState(null);
  const [classDetailsLoading, setClassDetailsLoading] = useState(false);

  // Public Classes state
  const [publicClasses, setPublicClasses] = useState([]);
  const [publicClassesLoading, setPublicClassesLoading] = useState(false);
  const [selectedPublicClass, setSelectedPublicClass] = useState(null);
  const [publicClassPricing, setPublicClassPricing] = useState(null);
  const [publicClassPricingLoading, setPublicClassPricingLoading] = useState(false);
  const [publicClassConfirmed, setPublicClassConfirmed] = useState(false);
  const [joiningPublicClass, setJoiningPublicClass] = useState(false);

  // Waitlist/Interest List state
  const [showWaitlistForm, setShowWaitlistForm] = useState(false);
  const [waitlistStudents, setWaitlistStudents] = useState([{ first_name: '', last_name: '', email: '', phone: '' }]);
  const [waitlistClassTypes, setWaitlistClassTypes] = useState([]);
  const [waitlistNeededBy, setWaitlistNeededBy] = useState('');
  const [waitlistNotes, setWaitlistNotes] = useState('');
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);

  // Multi-step booking state (only for new bookings, not add-on)
  // Note: Now single page layout, step tracking removed

  // Monthly availability calendar state
  const [monthlyAvailability, setMonthlyAvailability] = useState({});
  const [availabilityMonth, setAvailabilityMonth] = useState(new Date());
  const [loadingMonthlyAvailability, setLoadingMonthlyAvailability] = useState(false);

  const today = startOfDay(new Date());
  const maxBookingDate = addDays(today, 60);

  // Check if a slot is within 48 hours of current time
  const checkIfWithin48Hours = (slotStart) => {
    if (!slotStart) return false;
    const now = new Date();
    const slotTime = new Date(slotStart);
    const hoursUntilSlot = (slotTime - now) / (1000 * 60 * 60);
    return hoursUntilSlot <= 48 && hoursUntilSlot > 0;
  };

  // Fetch monthly availability for the calendar view
  const fetchMonthlyAvailability = async (date, classTypeId) => {
    if (!classTypeId) return;
    setLoadingMonthlyAvailability(true);
    try {
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const response = await axios.get(`${API}/calendar/monthly-availability?year=${year}&month=${month}&class_type_id=${classTypeId}`);
      setMonthlyAvailability(response.data.availability || {});
    } catch (error) {
      console.error('Failed to fetch monthly availability:', error);
    } finally {
      setLoadingMonthlyAvailability(false);
    }
  };

  // Get availability color based on slot count
  const getAvailabilityColor = (slots) => {
    // Ensure slots is a number
    const slotCount = Number(slots) || 0;
    if (slotCount === 0) return 'bg-red-600';      // 0 = Red/None
    if (slotCount <= 15) return 'bg-amber-500';    // 1-15 = Yellow/Limited
    return 'bg-emerald-500';                        // 16+ = Green/High
  };

  // Fetch monthly availability when class or month changes
  useEffect(() => {
    if (selectedClass?.id) {
      fetchMonthlyAvailability(availabilityMonth, selectedClass.id);
    }
  }, [availabilityMonth, selectedClass?.id]);

  // Reno/Sparks area zip codes
  const renoSparksZipCodes = [
    '89501', '89502', '89503', '89504', '89505', '89506', '89507', '89508', '89509', '89510',
    '89511', '89512', '89513', '89515', '89519', '89520', '89521', '89523', '89533', '89555',
    '89557', '89570', '89595', '89599', // Reno
    '89431', '89432', '89434', '89435', '89436', '89441' // Sparks
  ];

  // Check if address is in Reno/Sparks area
  const isInRenoSparksArea = (city, state, zip) => {
    const normalizedCity = city.toLowerCase().trim();
    const normalizedState = state.toUpperCase().trim();
    
    // Must be in Nevada
    if (normalizedState !== 'NV' && normalizedState !== 'NEVADA') {
      return false;
    }
    
    // Check city name
    if (normalizedCity === 'reno' || normalizedCity === 'sparks') {
      return true;
    }
    
    // Check zip code
    if (renoSparksZipCodes.includes(zip.trim())) {
      return true;
    }
    
    return false;
  };

  // Handle Google Places address selection
  const handleAddressSelect = (addressData, formatted, placeName) => {
    if (addressData) {
      // Valid address selected from Google
      setClassAddress({
        street: addressData.street || '',
        city: addressData.city || '',
        state: addressData.state || 'NV',
        zip: addressData.zip || ''
      });
      setFormattedAddress(formatted || '');
      setIsAddressValidated(true);
      setAddressErrors({});
      
      // Store the Google Place name and set it as the default location name
      setGooglePlaceName(placeName || '');
      setLocationName(placeName || ''); // Auto-fill the location name
      
      // Check if in service area
      if (addressData.city && addressData.state && addressData.zip) {
        const inArea = isInRenoSparksArea(addressData.city, addressData.state, addressData.zip);
        setShowTravelFeeWarning(!inArea);
      }
    } else {
      // User cleared or is typing (not yet selected)
      setClassAddress({ street: '', city: '', state: 'NV', zip: '' });
      setFormattedAddress('');
      setIsAddressValidated(false);
      setShowTravelFeeWarning(false);
      setGooglePlaceName('');
      setLocationName('');
    }
  };

  // Validate address - now checks if address was validated by Google
  const validateAddress = () => {
    if (!isAddressValidated) {
      setAddressErrors({ address: 'Please select a valid address from the suggestions' });
      return false;
    }
    
    const errors = {};
    if (!classAddress.street.trim()) {
      errors.street = 'Street address is required';
    }
    if (!classAddress.city.trim()) {
      errors.city = 'City is required';
    }
    if (!classAddress.state.trim()) {
      errors.state = 'State is required';
    }
    if (!classAddress.zip.trim()) {
      errors.zip = 'ZIP code is required';
    }
    
    setAddressErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Validation functions
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePhone = (phone) => {
    const re = /^[\d\s\-\(\)\+]{10,}$/;
    return re.test(phone.replace(/\s/g, ''));
  };

  const addStudent = () => {
    setStudents([...students, { 
      id: Date.now(), 
      firstName: '', 
      lastName: '', 
      email: '', 
      phone: '',
      ahaEcardCode: '',
      ahaVerificationStatus: null,
      ahaVerifying: false,
      manuallyVerified: false,
      errors: {}
    }]);
  };

  const removeStudent = (id) => {
    if (students.length <= 1) {
      toast.error('At least one student is required');
      return;
    }
    setStudents(students.filter(s => s.id !== id));
  };

  const updateStudent = (id, field, value) => {
    setStudents(students.map(s => {
      if (s.id === id) {
        const updated = { ...s, [field]: value };
        // Clear error when user starts typing
        if (updated.errors[field]) {
          updated.errors = { ...updated.errors, [field]: null };
        }
        return updated;
      }
      return s;
    }));
  };

  const validateStudents = () => {
    let isValid = true;
    const updatedStudents = students.map(s => {
      const errors = {};
      if (!s.firstName.trim()) {
        errors.firstName = 'First name is required';
        isValid = false;
      }
      if (!s.lastName.trim()) {
        errors.lastName = 'Last name is required';
        isValid = false;
      }
      if (!s.email.trim()) {
        errors.email = 'Email is required';
        isValid = false;
      } else if (!validateEmail(s.email)) {
        errors.email = 'Invalid email format';
        isValid = false;
      }
      if (!s.phone.trim()) {
        errors.phone = 'Phone is required';
        isValid = false;
      } else if (!validatePhone(s.phone)) {
        errors.phone = 'Invalid phone format (min 10 digits)';
        isValid = false;
      }
      return { ...s, errors };
    });
    setStudents(updatedStudents);
    return isValid;
  };

  useEffect(() => {
    fetchClassTypes();
    fetchCourseCategories();
    fetchPaymentConfig();
    fetchAvailableAddons();
    // Remove any stale pending booking IDs from localStorage
    localStorage.removeItem('pendingBookingId');
  }, []);
  
  // Fetch available add-on products from WooCommerce
  const fetchAvailableAddons = async () => {
    setAddonsLoading(true);
    try {
      const response = await axios.get(`${API}/addons`);
      setAvailableAddons(response.data.products || []);
    } catch (error) {
      console.error('Error fetching add-ons:', error);
      setAvailableAddons([]);
    } finally {
      setAddonsLoading(false);
    }
  };

  // Toggle add-on selection
  const toggleAddon = (productId) => {
    setSelectedAddons(prev => {
      const current = prev[productId] || { selected: false, quantity: 1 };
      return {
        ...prev,
        [productId]: {
          selected: !current.selected,
          quantity: current.selected ? 0 : 1
        }
      };
    });
  };

  // Update add-on quantity
  const updateAddonQuantity = (productId, quantity) => {
    const qty = Math.max(1, parseInt(quantity) || 1);
    setSelectedAddons(prev => ({
      ...prev,
      [productId]: {
        selected: prev[productId]?.selected || false,
        quantity: qty
      }
    }));
  };

  // Get selected addons for API calls
  const getSelectedAddonsForApi = () => {
    return Object.entries(selectedAddons)
      .filter(([_, data]) => data.selected && data.quantity > 0)
      .map(([productId, data]) => {
        const product = availableAddons.find(p => p.id === parseInt(productId));
        return {
          product_id: parseInt(productId),
          name: product?.name || 'Unknown',
          quantity: data.quantity,
          price: product?.price || 0
        };
      });
  };

  // Calculate add-ons total for display
  const calculateAddonsTotal = () => {
    return Object.entries(selectedAddons)
      .filter(([_, data]) => data.selected && data.quantity > 0)
      .reduce((total, [productId, data]) => {
        const product = availableAddons.find(p => p.id === parseInt(productId));
        return total + (product?.price || 0) * data.quantity;
      }, 0);
  };
  
  const fetchPaymentConfig = async () => {
    try {
      setPaymentConfigLoading(true);
      const response = await axios.get(`${API}/payment-config`);
      setPaymentConfig(response.data);
    } catch (error) {
      console.error('Error fetching payment config:', error);
      // Default to sandbox mode if can't fetch config (safer)
      setPaymentConfig({ mode: 'sandbox', is_sandbox: true, booking_available: false, client_id: '' });
    } finally {
      setPaymentConfigLoading(false);
    }
  };

  // Check if selected class is a BLS Renewal class - works for all modes (regular, add-on, public class)
  const isRenewalClass = useMemo(() => {
    // Determine the active mode and check only that mode's class
    
    // Public class mode takes priority if a public class is selected
    if (selectedPublicClass && selectedPublicClass !== 'browse') {
      const publicClassId = selectedPublicClass.class_type_id?.toLowerCase() || '';
      const publicClassName = selectedPublicClass.class_type_name?.toLowerCase() || '';
      const isRenewal = publicClassId.includes('renewal') || publicClassName.includes('renewal');
      console.log('[isRenewalClass] Public class mode:', { publicClassId, publicClassName, isRenewal });
      return isRenewal;
    }
    
    // Add-on mode - check the found booking's class type
    if (isAddOnMode && foundBooking) {
      const bookingClassId = foundBooking.class_type_id?.toLowerCase() || '';
      const bookingClassName = foundBooking.class_type_name?.toLowerCase() || '';
      const isRenewal = bookingClassId.includes('renewal') || bookingClassName.includes('renewal');
      console.log('[isRenewalClass] AddOn mode:', { bookingClassId, bookingClassName, isRenewal });
      return isRenewal;
    }
    
    // Regular booking mode - check selected class
    if (selectedClass) {
      const classId = selectedClass.id?.toLowerCase() || '';
      const className = selectedClass.name?.toLowerCase() || '';
      const isRenewal = classId.includes('renewal') || className.includes('renewal');
      console.log('[isRenewalClass] Regular mode:', { classId, className, isRenewal });
      return isRenewal;
    }
    
    console.log('[isRenewalClass] FALSE - No class selected in any mode');
    return false;
  }, [selectedClass, isAddOnMode, foundBooking, selectedPublicClass]);

  // Get class date for AHA verification - works for all modes
  const getClassDateForVerification = () => {
    if (selectedSlot) {
      return selectedSlot.start.split('T')[0];
    }
    if (isAddOnMode && foundBooking?.start_time) {
      return foundBooking.start_time.split('T')[0];
    }
    if (selectedPublicClass && selectedPublicClass !== 'browse' && selectedPublicClass.start_time) {
      return selectedPublicClass.start_time.split('T')[0];
    }
    return null;
  };

  // Verify a single student's AHA eCard
  const verifyStudentAhaCard = async (studentId) => {
    const student = students.find(s => s.id === studentId);
    if (!student || !student.ahaEcardCode || !student.firstName || !student.lastName) {
      toast.error('Please enter student name and eCard code');
      return;
    }

    // Update student to show verifying state
    setStudents(prev => prev.map(s => 
      s.id === studentId ? { ...s, ahaVerifying: true, ahaVerificationStatus: null } : s
    ));
    
    try {
      // Get class date for expiration check - works for all modes
      const classDate = getClassDateForVerification();
      
      const response = await axios.post(`${API}/verify-aha-ecard`, {
        ecard_code: student.ahaEcardCode,
        first_name: student.firstName,
        last_name: student.lastName,
        class_date: classDate
      });
      
      const verificationResult = response.data;
      
      // Check if it's a partial match (verified but not exact)
      if (verificationResult.verified && verificationResult.is_exact_match === false) {
        // Show custom modal for partial match
        const enteredName = `${student.firstName} ${student.lastName}`;
        const ahaName = verificationResult.card_holder_name || verificationResult.aha_returned_name;
        
        // Stop verifying state but don't set result yet
        setStudents(prev => prev.map(s => 
          s.id === studentId ? { ...s, ahaVerifying: false } : s
        ));
        
        // Show the mismatch modal
        setAhaMismatchModal({
          show: true,
          studentId: studentId,
          enteredName: enteredName,
          ahaName: ahaName,
          verificationResult: verificationResult
        });
        return;
      }
      
      // Exact match or failed verification - proceed normally
      setStudents(prev => prev.map(s => 
        s.id === studentId ? { 
          ...s, 
          ahaVerifying: false, 
          ahaVerificationStatus: verificationResult 
        } : s
      ));
      
      if (verificationResult.verified) {
        if (verificationResult.is_exact_match) {
          toast.success(`✓ AHA Certificate verified - Exact Match for ${student.firstName}!`);
        } else {
          toast.success(`AHA Certificate verified for ${student.firstName}!`);
        }
      } else {
        toast.warning(`${student.firstName}: ${verificationResult.match_status}`);
      }
    } catch (error) {
      console.error('AHA verification error:', error);
      setStudents(prev => prev.map(s => 
        s.id === studentId ? { 
          ...s, 
          ahaVerifying: false, 
          ahaVerificationStatus: { verified: false, match_status: 'AHA ERROR', details: 'Verification failed' }
        } : s
      ));
      toast.error(`Failed to verify certificate for ${student.firstName}`);
    }
  };

  // Handle AHA mismatch modal actions
  const handleAhaMismatchAccept = () => {
    const { studentId, verificationResult } = ahaMismatchModal;
    const student = students.find(s => s.id === studentId);
    
    // User accepted - mark as needs review
    setStudents(prev => prev.map(s => 
      s.id === studentId ? { 
        ...s, 
        ahaVerificationStatus: {
          ...verificationResult,
          needs_review: true
        }
      } : s
    ));
    
    toast.warning(`${student?.firstName || 'Student'} added with name mismatch - marked for review`);
    setAhaMismatchModal({ show: false, studentId: null, enteredName: '', ahaName: '', verificationResult: null });
  };

  const handleAhaMismatchReject = () => {
    const { studentId } = ahaMismatchModal;
    
    // User wants to change - clear verification status
    setStudents(prev => prev.map(s => 
      s.id === studentId ? { 
        ...s, 
        ahaVerificationStatus: null
      } : s
    ));
    
    toast.info('Please correct the student name and verify again');
    setAhaMismatchModal({ show: false, studentId: null, enteredName: '', ahaName: '', verificationResult: null });
  };

  // Check if all students have valid AHA verification for renewal classes
  const allStudentsVerified = () => {
    if (!isRenewalClass) return true;
    return students.every(s => s.ahaVerificationStatus?.verified === true);
  };

  // Check if any student's cert is expired for the class date
  const hasExpiredCerts = () => {
    if (!isRenewalClass) return false;
    return students.some(s => s.ahaVerificationStatus?.is_expired === true);
  };

  // Fetch terms and conditions content
  const fetchTermsContent = async () => {
    setTermsLoading(true);
    try {
      const response = await axios.get(`${API}/terms-conditions`);
      if (response.data.success) {
        setTermsContent(response.data.content);
        setTermsTitle(response.data.title || 'Terms and Conditions');
      }
    } catch (error) {
      console.error('Failed to fetch terms:', error);
      // Show fallback message
      setTermsContent('<p>Unable to load terms and conditions. Please visit <a href="https://www.biggestlittlecpr.com/terms-conditions/" target="_blank" rel="noopener noreferrer">our website</a> to view them.</p>');
    } finally {
      setTermsLoading(false);
    }
  };

  // Open terms modal
  const openTermsModal = (e) => {
    e.preventDefault();
    setShowTermsModal(true);
    if (!termsContent) {
      fetchTermsContent();
    }
  };

  useEffect(() => {
    if (selectedClass && selectedDate) {
      fetchAvailability();
      const interval = setInterval(fetchAvailability, 180000);
      return () => clearInterval(interval);
    }
  }, [selectedClass, selectedDate]);

  // Fetch pricing preview when booking details change
  useEffect(() => {
    const fetchPricingPreview = async () => {
      if (!selectedClass || !selectedSlot || students.length === 0 || isAddOnMode) {
        setPricingPreview(null);
        return;
      }
      
      setPricingLoading(true);
      try {
        // Build addons array for pricing
        const addonsForPricing = Object.entries(selectedAddons)
          .filter(([_, data]) => data.selected && data.quantity > 0)
          .map(([productId, data]) => ({
            product_id: parseInt(productId),
            quantity: data.quantity
          }));
        
        const response = await axios.post(`${API}/pricing-preview`, {
          class_type_id: selectedClass.id,
          student_count: students.length,
          start_time: selectedSlot.start,
          class_address: showTravelFeeWarning ? {
            street: classAddress.street,
            city: classAddress.city,
            state: classAddress.state,
            zip: classAddress.zip,
            is_outside_service_area: true
          } : null,
          coupon_code: couponApplied?.code || null,
          customer_email: students[0]?.email || null,
          addons: addonsForPricing.length > 0 ? addonsForPricing : null
        });
        setPricingPreview(response.data);
        
        // Update coupon applied state if pricing includes coupon info
        if (response.data.coupon && !response.data.coupon.error) {
          setCouponApplied(response.data.coupon);
          setCouponError('');
        } else if (response.data.coupon?.error) {
          setCouponApplied(null);
          setCouponError(response.data.coupon.error);
        }
      } catch (error) {
        console.error('Error fetching pricing preview:', error);
        setPricingPreview(null);
      } finally {
        setPricingLoading(false);
      }
    };
    
    // Debounce the pricing fetch
    const timeoutId = setTimeout(fetchPricingPreview, 500);
    return () => clearTimeout(timeoutId);
  }, [selectedClass, selectedSlot, students.length, showTravelFeeWarning, classAddress, isAddOnMode, couponApplied?.code, students, selectedAddons]);

  // Apply coupon function
  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }
    
    // For add-on mode, use foundBooking data
    if (isAddOnMode) {
      if (!foundBooking) {
        setCouponError('Please find your booking first');
        return;
      }
      
      setCouponLoading(true);
      setCouponError('');
      
      try {
        const response = await axios.post(`${API}/validate-coupon`, {
          coupon_code: couponCode.trim(),
          class_type_id: foundBooking.class_type_id,
          student_count: students.length || 1,
          start_time: foundBooking.start_time,
          customer_email: students[0]?.email || null,
          is_addon: true
        });
        
        if (response.data.valid) {
          setCouponApplied(response.data);
          setCouponError('');
          toast.success(`Coupon applied! You save $${response.data.discount_amount.toFixed(2)}`);
        } else {
          setCouponError(response.data.error || 'Invalid coupon code');
          setCouponApplied(null);
        }
      } catch (error) {
        console.error('Error validating coupon:', error);
        setCouponError(error.response?.data?.detail || 'Failed to validate coupon');
        setCouponApplied(null);
      } finally {
        setCouponLoading(false);
      }
      return;
    }
    
    // Normal booking flow
    if (!selectedClass || !selectedSlot) {
      setCouponError('Please select a class and time first');
      return;
    }
    
    setCouponLoading(true);
    setCouponError('');
    
    try {
      const response = await axios.post(`${API}/validate-coupon`, {
        coupon_code: couponCode.trim(),
        class_type_id: selectedClass.id,
        student_count: students.length || 1,
        start_time: selectedSlot.start,
        class_address: showTravelFeeWarning ? {
          street: classAddress.street,
          city: classAddress.city,
          state: classAddress.state,
          zip: classAddress.zip,
          is_outside_service_area: true
        } : null,
        customer_email: students[0]?.email || null
      });
      
      if (response.data.valid) {
        setCouponApplied(response.data);
        setCouponError('');
        toast.success(`Coupon applied! You save $${response.data.discount_amount.toFixed(2)}`);
      } else {
        setCouponError(response.data.error || 'Invalid coupon code');
        setCouponApplied(null);
      }
    } catch (error) {
      console.error('Error validating coupon:', error);
      setCouponError(error.response?.data?.detail || 'Failed to validate coupon');
      setCouponApplied(null);
    } finally {
      setCouponLoading(false);
    }
  };

  // Remove coupon function
  const removeCoupon = () => {
    setCouponCode('');
    setCouponApplied(null);
    setCouponError('');
  };

  // Fetch addon pricing when in add-on mode and booking is found
  useEffect(() => {
    const fetchAddonPricing = async () => {
      if (!isAddOnMode || !foundBooking || students.length === 0) {
        setAddonPricing(null);
        return;
      }
      
      setAddonPricingLoading(true);
      try {
        // Prepare selected add-ons for pricing
        const addonsForPricing = Object.entries(selectedAddons)
          .filter(([_, data]) => data.selected)
          .map(([productId, data]) => ({
            product_id: parseInt(productId),
            quantity: data.quantity
          }));
        
        const response = await axios.post(`${API}/addon-pricing-preview`, {
          parent_booking_id: foundBooking.booking_id,
          student_count: students.length,
          addons: addonsForPricing.length > 0 ? addonsForPricing : undefined
        });
        setAddonPricing(response.data);
      } catch (error) {
        console.error('Error fetching addon pricing:', error);
        // Show detailed error message from backend if available (e.g., 24-hour cutoff)
        const errorMessage = error.response?.data?.detail;
        if (errorMessage) {
          toast.error(errorMessage, { duration: 6000 });
          // Reset add-on mode if blocked
          setFoundBooking(null);
          setAddOnConfirmed(false);
        }
        setAddonPricing(null);
      } finally {
        setAddonPricingLoading(false);
      }
    };
    
    const timeoutId = setTimeout(fetchAddonPricing, 500);
    return () => clearTimeout(timeoutId);
  }, [isAddOnMode, foundBooking, students.length, selectedAddons]);

  // Fetch public class pricing when students change (similar to addon pricing)
  useEffect(() => {
    const fetchPublicClassPricing = async () => {
      if (!selectedPublicClass || selectedPublicClass === 'browse' || students.length === 0) {
        setPublicClassPricing(null);
        return;
      }
      
      setPublicClassPricingLoading(true);
      try {
        // Prepare selected add-ons for pricing
        const addonsForPricing = Object.entries(selectedAddons)
          .filter(([_, data]) => data.selected)
          .map(([productId, data]) => ({
            product_id: parseInt(productId),
            quantity: data.quantity
          }));
        
        const response = await axios.post(`${API}/public-classes/pricing-preview`, {
          booking_id: selectedPublicClass.id,
          student_count: students.length,
          addons: addonsForPricing.length > 0 ? addonsForPricing : undefined
        });
        setPublicClassPricing(response.data);
      } catch (error) {
        console.error('Error fetching public class pricing:', error);
        const errorMessage = error.response?.data?.detail;
        if (errorMessage) {
          toast.error(errorMessage, { duration: 6000 });
        }
        setPublicClassPricing(null);
      } finally {
        setPublicClassPricingLoading(false);
      }
    };
    
    const timeoutId = setTimeout(fetchPublicClassPricing, 500);
    return () => clearTimeout(timeoutId);
  }, [selectedPublicClass, students.length, selectedAddons]);

  const checkPendingBooking = async () => {
    // Check if user has a pending booking they might want to continue editing
    const savedBookingId = localStorage.getItem('pendingBookingId');
    if (savedBookingId) {
      try {
        const response = await axios.get(`${API}/bookings/${savedBookingId}`);
        const booking = response.data;
        
        // Only show if booking is still pending and unpaid
        if (booking.status === 'pending' && booking.payment_status !== 'paid') {
          setPendingBooking(booking);
          setShowPendingBanner(true);
        } else {
          // Clear the saved booking if it's no longer editable
          localStorage.removeItem('pendingBookingId');
        }
      } catch (error) {
        // Booking not found or error - clear it
        localStorage.removeItem('pendingBookingId');
      }
    }
  };

  const loadPendingBooking = () => {
    if (!pendingBooking) return;
    
    // Find and set the class type
    const classType = classTypes.find(c => c.id === pendingBooking.class_type_id);
    if (classType) {
      setSelectedClass(classType);
    }
    
    // Set the date
    const bookingDate = new Date(pendingBooking.start_time);
    setSelectedDate(bookingDate);
    
    // Set the time slot
    setSelectedSlot({
      start: pendingBooking.start_time,
      end: pendingBooking.end_time
    });
    
    // Set students
    if (pendingBooking.students && pendingBooking.students.length > 0) {
      setStudents(pendingBooking.students.map((s, index) => ({
        id: Date.now() + index,
        firstName: s.first_name || '',
        lastName: s.last_name || '',
        email: s.email || '',
        phone: s.phone || '',
        errors: {}
      })));
    }
    
    // Set editing mode
    setEditingBookingId(pendingBooking.id);
    setShowPendingBanner(false);
    setAcceptedTerms(true); // They already accepted when creating
    
    toast.success('Booking loaded. Make your changes and submit.');
  };

  const startFresh = () => {
    localStorage.removeItem('pendingBookingId');
    setPendingBooking(null);
    setShowPendingBanner(false);
    setEditingBookingId(null);
  };

  const cancelEdit = () => {
    setEditingBookingId(null);
    setSelectedClass(null);
    setSelectedDate(undefined);
    setSelectedSlot(null);
    setStudents([{ id: Date.now(), firstName: '', lastName: '', email: '', phone: '', ahaEcardCode: '', ahaVerificationStatus: null, ahaVerifying: false, manuallyVerified: false, errors: {} }]);
    setAcceptedTerms(false);
    // Keep the pending booking available
    if (pendingBooking) {
      setShowPendingBanner(true);
    }
  };

  const resetFormState = () => {
    setStudents([{ id: Date.now(), firstName: '', lastName: '', email: '', phone: '', ahaEcardCode: '', ahaVerificationStatus: null, ahaVerifying: false, manuallyVerified: false, errors: {} }]);
    setSelectedSlot(null);
    setSelectedClass(null);
    setSelectedDate(undefined);
    setAcceptedTerms(false);
    setIsWithin48Hours(false);
    setEditingBookingId(null);
    setPendingBooking(null);
    localStorage.removeItem('pendingBookingId');
    // Reset public class state
    setSelectedPublicClass(null);
    setPublicClassPricing(null);
    setPublicClassConfirmed(false);
    // Reset addons
    setSelectedAddons({});
  };

  const fetchClassTypes = async () => {
    try {
      const response = await axios.get(`${API}/class-types`);
      setClassTypes(response.data);
    } catch (error) {
      toast.error('Failed to load class types');
    }
  };

  const fetchCourseCategories = async () => {
    try {
      const response = await axios.get(`${API}/course-categories`);
      setCourseCategories(response.data);
    } catch (error) {
      console.error('Failed to load course categories');
    }
  };

  // Smart category filtering - matches by course_category field OR by name prefix
  const filteredClassTypes = useMemo(() => {
    if (selectedCategory === 'all') return classTypes;
    
    return classTypes.filter(ct => {
      // First check if course_category matches
      if (ct.course_category === selectedCategory) return true;
      
      // If category field is empty/null, try to match by name prefix
      // Map category slugs to expected name prefixes
      const categoryNameMap = {
        'heartsaver': 'Heartsaver',
        'bls': 'Basic Life Support',
        'instructor': 'Instructor',
        'acls': 'ACLS',
        'pals': 'PALS',
      };
      
      const expectedPrefix = categoryNameMap[selectedCategory];
      if (expectedPrefix && ct.name.startsWith(expectedPrefix)) {
        return true;
      }
      
      return false;
    });
  }, [classTypes, selectedCategory]);

  // Group classes by course type for hierarchical selection
  // Extract course type from class name (e.g., "Heartsaver CPR/AED - Adult Only" -> "Heartsaver CPR/AED")
  const courseTypeGroups = useMemo(() => {
    const groups = {};
    
    filteredClassTypes.forEach(ct => {
      // Parse the class name to extract course type and variant
      let courseType = ct.name;
      let variant = null;
      
      // Check for common patterns with age options
      const agePatterns = [
        { pattern: / - Adult Only$/, variant: 'Adult Only' },
        { pattern: / - Adult & Child$/, variant: 'Adult & Child' },
        { pattern: / - Adult & Infant$/, variant: 'Adult & Infant' },
        { pattern: / - Adult, Child & Infant$/, variant: 'Adult, Child & Infant' },
        { pattern: / - Adult, Child, & Infant$/, variant: 'Adult, Child & Infant' },
        // Also check for "W/" patterns
        { pattern: / - Adult W\/Child$/, variant: 'Adult & Child' },
        { pattern: / - Adult W\/Infant$/, variant: 'Adult & Infant' },
        { pattern: / - Adult W\/ Infant & Child$/, variant: 'Adult, Child & Infant' },
        // Pediatric "+ Adult" pattern
        { pattern: / \+ Adult$/, variant: '+ Adult' },
      ];
      
      for (const { pattern, variant: v } of agePatterns) {
        if (pattern.test(ct.name)) {
          courseType = ct.name.replace(pattern, '');
          variant = v;
          break;
        }
      }
      
      // Check for certification type patterns (BLS New/Renewal)
      if (ct.name.includes('New Certification')) {
        courseType = ct.name.replace(' New Certification', '').replace(' - New', '');
        variant = 'New Certification';
      } else if (ct.name.includes('Renewal') && !ct.name.includes('Pediatric')) {
        courseType = ct.name.replace(' Renewal', '').replace(' - Renewal', '');
        variant = 'Renewal';
      }
      
      // For Pediatric classes without variants, set a default variant
      if (courseType === ct.name && ct.name.includes('Pediatric') && !variant) {
        variant = 'Standard';
      }
      
      if (!groups[courseType]) {
        groups[courseType] = {
          name: courseType,
          classes: [],
          hasVariants: false
        };
      }
      
      groups[courseType].classes.push({
        ...ct,
        variant: variant
      });
      
      if (variant) {
        groups[courseType].hasVariants = true;
      }
    });
    
    // Convert to array and sort
    return Object.values(groups).sort((a, b) => {
      // Sort by the first class's sort_order in each group
      const aOrder = a.classes[0]?.sort_order || 999;
      const bOrder = b.classes[0]?.sort_order || 999;
      return aOrder - bOrder;
    });
  }, [filteredClassTypes]);

  // Helper function to get color scheme based on class type
  const getClassTypeColors = (classTypeId, className) => {
    const id = classTypeId?.toLowerCase() || '';
    const name = className?.toLowerCase() || '';
    
    // BLS classes - Blue theme
    if (id.includes('bls') || name.includes('basic life support')) {
      return {
        bg: 'bg-blue-900/40',
        border: 'border-blue-500',
        text: 'text-blue-300',
        accent: 'text-blue-400',
        badge: 'bg-blue-600/30 text-blue-300 border-blue-500',
        gradient: 'from-blue-900/50 to-slate-800',
        icon: 'text-blue-400'
      };
    }
    
    // Heartsaver classes - Orange theme
    if (id.includes('heartsaver') || id.includes('hs-') || name.includes('heartsaver')) {
      return {
        bg: 'bg-orange-900/40',
        border: 'border-orange-500',
        text: 'text-orange-300',
        accent: 'text-orange-400',
        badge: 'bg-orange-600/30 text-orange-300 border-orange-500',
        gradient: 'from-orange-900/50 to-slate-800',
        icon: 'text-orange-400'
      };
    }
    
    // ACLS classes - Red/Rose theme
    if (id.includes('acls') || name.includes('acls') || name.includes('advanced cardiac')) {
      return {
        bg: 'bg-rose-900/40',
        border: 'border-rose-500',
        text: 'text-rose-300',
        accent: 'text-rose-400',
        badge: 'bg-rose-600/30 text-rose-300 border-rose-500',
        gradient: 'from-rose-900/50 to-slate-800',
        icon: 'text-rose-400'
      };
    }
    
    // PALS classes - Purple theme
    if (id.includes('pals') || name.includes('pals') || name.includes('pediatric')) {
      return {
        bg: 'bg-purple-900/40',
        border: 'border-purple-500',
        text: 'text-purple-300',
        accent: 'text-purple-400',
        badge: 'bg-purple-600/30 text-purple-300 border-purple-500',
        gradient: 'from-purple-900/50 to-slate-800',
        icon: 'text-purple-400'
      };
    }
    
    // Instructor courses - Green theme  
    if (id.includes('instructor') || name.includes('instructor')) {
      return {
        bg: 'bg-emerald-900/40',
        border: 'border-emerald-500',
        text: 'text-emerald-300',
        accent: 'text-emerald-400',
        badge: 'bg-emerald-600/30 text-emerald-300 border-emerald-500',
        gradient: 'from-emerald-900/50 to-slate-800',
        icon: 'text-emerald-400'
      };
    }
    
    // Default - Teal theme
    return {
      bg: 'bg-teal-900/40',
      border: 'border-teal-600',
      text: 'text-teal-300',
      accent: 'text-teal-400',
      badge: 'bg-teal-600/30 text-teal-300 border-teal-600',
      gradient: 'from-teal-900/50 to-slate-800',
      icon: 'text-teal-400'
    };
  };

  // Get variants for the selected course type
  const selectedCourseTypeVariants = useMemo(() => {
    if (!selectedCourseType) return [];
    const group = courseTypeGroups.find(g => g.name === selectedCourseType);
    return group ? group.classes : [];
  }, [selectedCourseType, courseTypeGroups]);

  const fetchClassDetails = async (classTypeId) => {
    setClassDetailsLoading(true);
    try {
      const response = await axios.get(`${API}/class-types/${classTypeId}/woo-details`);
      setClassDetails(response.data);
      setShowClassDetails(true);
    } catch (error) {
      toast.error('Failed to load class details');
    } finally {
      setClassDetailsLoading(false);
    }
  };

  const fetchAvailability = async () => {
    if (!selectedClass || !selectedDate) return;
    try {
      // Format date as YYYY-MM-DD to avoid timezone shifting issues
      // This ensures the same date is sent regardless of device timezone
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      const response = await axios.get(`${API}/calendar/availability`, {
        params: {
          date: dateStr,
          class_type_id: selectedClass.id
        }
      });
      setAvailability(response.data);
    } catch (error) {
      toast.error('Failed to fetch availability');
    }
  };

  const fetchUserBookings = async (email) => {
    if (!email) return;
    try {
      const response = await axios.get(`${API}/bookings`, {
        params: { email }
      });
      setUserBookings(response.data);
    } catch (error) {
      console.error('Failed to fetch user bookings:', error);
    }
  };

  // Fetch public classes
  const fetchPublicClasses = async () => {
    setPublicClassesLoading(true);
    try {
      const response = await axios.get(`${API}/public-classes`);
      setPublicClasses(response.data);
    } catch (error) {
      console.error('Failed to fetch public classes:', error);
    } finally {
      setPublicClassesLoading(false);
    }
  };

  // ============== Waitlist/Interest List Handlers ==============
  
  const resetWaitlistForm = () => {
    setWaitlistStudents([{ first_name: '', last_name: '', email: '', phone: '' }]);
    setWaitlistClassTypes([]);
    setWaitlistNeededBy('');
    setWaitlistNotes('');
    setShowWaitlistForm(false);
  };

  const addWaitlistStudent = () => {
    setWaitlistStudents([...waitlistStudents, { first_name: '', last_name: '', email: '', phone: '' }]);
  };

  const removeWaitlistStudent = (index) => {
    if (waitlistStudents.length > 1) {
      setWaitlistStudents(waitlistStudents.filter((_, i) => i !== index));
    }
  };

  const updateWaitlistStudent = (index, field, value) => {
    const updated = [...waitlistStudents];
    updated[index] = { ...updated[index], [field]: value };
    setWaitlistStudents(updated);
  };

  const handleWaitlistSubmit = async () => {
    // Validate
    if (waitlistStudents.length === 0) {
      toast.error('Please add at least one student');
      return;
    }
    
    for (const student of waitlistStudents) {
      if (!student.first_name?.trim() || !student.last_name?.trim() || !student.email?.trim() || !student.phone?.trim()) {
        toast.error('Please fill in all student information');
        return;
      }
    }
    
    if (waitlistClassTypes.length === 0) {
      toast.error('Please select at least one class type');
      return;
    }
    
    const primaryEmail = waitlistStudents[0].email;
    
    setWaitlistSubmitting(true);
    try {
      const response = await axios.post(`${API}/waitlist`, {
        students: waitlistStudents,
        class_type_ids: waitlistClassTypes,
        needed_by_date: waitlistNeededBy || null,
        contact_email: primaryEmail,
        contact_phone: waitlistStudents[0].phone,
        notes: waitlistNotes || null
      });
      
      toast.success(response.data.message || "You've been added to the waitlist!");
      resetWaitlistForm();
    } catch (error) {
      console.error('Waitlist submission error:', error);
      toast.error(error.response?.data?.detail || 'Failed to join waitlist');
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  // Handle public class checkout (with payment)
  const handlePublicClassCheckout = async () => {
    if (!selectedPublicClass || selectedPublicClass === 'browse' || students.length === 0 || !publicClassPricing) {
      toast.error('Please complete all required fields');
      return;
    }
    
    // Validate all students
    for (const student of students) {
      if (!student.firstName?.trim() || !student.lastName?.trim() || !student.email?.trim() || !student.phone?.trim()) {
        toast.error('Please fill in all student information');
        return;
      }
    }
    
    // Validate AHA verification for renewal public classes
    if (isRenewalClass) {
      // Check if all students have attempted verification
      const unverifiedStudents = students.filter(s => !s.ahaVerificationStatus && !s.ahaEcardCode);
      if (unverifiedStudents.length > 0) {
        const names = unverifiedStudents.map(s => `${s.firstName} ${s.lastName}`).join(', ');
        toast.error(`Please enter eCard code for: ${names}`);
        return;
      }
      
      // Check if any student needs to complete verification
      const pendingStudents = students.filter(s => s.ahaEcardCode && !s.ahaVerificationStatus);
      if (pendingStudents.length > 0) {
        const names = pendingStudents.map(s => `${s.firstName} ${s.lastName}`).join(', ');
        toast.error(`Please verify eCard for: ${names}`);
        return;
      }
      
      // Check if any student's verification failed (excluding manually verified)
      const failedStudents = students.filter(s => 
        s.ahaVerificationStatus && 
        !s.ahaVerificationStatus.verified &&
        s.ahaVerificationStatus.match_status !== 'MANUAL VERIFICATION REQUIRED'
      );
      if (failedStudents.length > 0) {
        const failedNames = failedStudents.map(s => `${s.firstName} ${s.lastName}`).join(', ');
        toast.error(`AHA verification failed for: ${failedNames}. Please check the eCard codes.`);
        return;
      }
      
      // Check if any student requires manual verification but hasn't confirmed
      const pendingManualStudents = students.filter(s => 
        s.ahaVerificationStatus?.match_status === 'MANUAL VERIFICATION REQUIRED' && 
        !s.manuallyVerified
      );
      if (pendingManualStudents.length > 0) {
        const pendingNames = pendingManualStudents.map(s => `${s.firstName} ${s.lastName}`).join(', ');
        toast.error(`Please manually verify and confirm eCard for: ${pendingNames} using the checkbox below the verification result.`);
        return;
      }
    }
    
    // Find the class type for this public class
    const classType = classTypes.find(ct => ct.id === selectedPublicClass.class_type_id);
    if (!classType) {
      toast.error('Class type not found');
      return;
    }
    
    setLoading(true);
    try {
      // Prepare students list - include AHA eCard data for renewal classes
      const studentsList = students.map(s => ({
        first_name: s.firstName.trim(),
        last_name: s.lastName.trim(),
        email: s.email.trim().toLowerCase(),
        phone: s.phone.trim(),
        // Add AHA eCard info for renewal classes
        ...(isRenewalClass && s.ahaEcardCode ? {
          aha_ecard: {
            ecard_code: s.ahaEcardCode,
            expiration_date: s.ahaVerificationStatus?.expiration_date || null,
            verification_status: s.ahaVerificationStatus?.match_status || 'Not Verified',
            card_holder_name: s.ahaVerificationStatus?.card_holder_name || null,
            verification_screenshot_url: s.ahaVerificationStatus?.screenshot_url || null,
            aha_returned_name: s.ahaVerificationStatus?.aha_returned_name || null,
            aha_returned_status: s.ahaVerificationStatus?.aha_returned_status || null,
            aha_returned_date: s.ahaVerificationStatus?.aha_returned_date || null,
            is_exact_match: s.ahaVerificationStatus?.is_exact_match || false,
            needs_review: s.ahaVerificationStatus?.needs_review || !s.ahaVerificationStatus?.is_exact_match,
            entered_name: s.ahaVerificationStatus?.entered_name || `${s.firstName} ${s.lastName}`
          }
        } : {})
      }));
      
      // Prepare addons with full details
      const addonsForOrder = getSelectedAddonsForApi();
      
      // Create Stripe order
      const orderResponse = await axios.post(`${API}/payments/create-order`, {
        class_type_id: selectedPublicClass.class_type_id,
        student_count: students.length,
        start_time: selectedPublicClass.start_time,
        end_time: selectedPublicClass.end_time,
        students: studentsList,
        class_address: selectedPublicClass.class_address,
        booking_type: "public_class",
        parent_booking_id: selectedPublicClass.id
      });
      
      if (orderResponse.data.success) {
        // Store order data for Stripe checkout
        const bookingData = {
          order_id: orderResponse.data.order_id,
          pricing: publicClassPricing,
          student_count: students.length,
          booking_type: "public_class",
          parent_booking_id: selectedPublicClass.id,
          class_type_id: selectedPublicClass.class_type_id,
          start_time: selectedPublicClass.start_time,
          end_time: selectedPublicClass.end_time,
          students: studentsList,
          class_address: selectedPublicClass.class_address,
          addons: addonsForOrder,
          total_price: publicClassPricing.grand_total,
          class_type_name: selectedPublicClass.class_type_name || 'CPR Class'
        };
        setCheckoutData(bookingData);
        
        // Check if total is $0 - skip Stripe and create booking directly
        if (publicClassPricing.grand_total === 0) {
          await createFreeBooking(bookingData);
        } else {
          setShowStripeCheckout(true);
        }
      }
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error(error.response?.data?.detail || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  // Add to Previous Booking functions
  const handleLookupBooking = async () => {
    // Determine lookup method
    if (lookupMode === 'code') {
      if (!lookupReferenceCode.trim()) {
        toast.error('Please enter a booking code');
        return;
      }
    } else {
      if (!addOnLookupDate || !addOnLookupTime) {
        toast.error('Please enter both date and start time');
        return;
      }
    }
    
    setLookupLoading(true);
    try {
      const requestData = lookupMode === 'code' 
        ? { reference_code: lookupReferenceCode.trim().toLowerCase() }
        : { date: addOnLookupDate, start_time: addOnLookupTime };
      
      const response = await axios.post(`${API}/bookings/lookup`, requestData);
      
      if (response.data.found) {
        setFoundBooking(response.data);
        toast.success(`Found class! Please confirm this is the correct booking.`);
      } else {
        setFoundBooking(null);
        const errorMsg = lookupMode === 'code' 
          ? 'No approved booking found with that code. Please check your entry.'
          : 'No approved and paid booking found for that date and time. Please check your entries.';
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Error looking up booking:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to look up booking. Please try again.';
      toast.error(errorMessage, { duration: 6000 });
      setFoundBooking(null);
    } finally {
      setLookupLoading(false);
    }
  };

  const handleAddStudentToBooking = async () => {
    if (!foundBooking || !addOnConfirmed) {
      toast.error('Please confirm the booking first');
      return;
    }
    
    if (students.length === 0) {
      toast.error('Please add at least one student');
      return;
    }
    
    if (!validateStudents()) {
      toast.error('Please fix the errors in student information');
      return;
    }
    
    // Validate AHA verification for renewal classes
    if (isRenewalClass) {
      // Check if all students have attempted verification
      const unverifiedStudents = students.filter(s => !s.ahaVerificationStatus && !s.ahaEcardCode);
      if (unverifiedStudents.length > 0) {
        const names = unverifiedStudents.map(s => `${s.firstName} ${s.lastName}`).join(', ');
        toast.error(`Please enter eCard code for: ${names}`);
        return;
      }
      
      // Check if any student needs to complete verification
      const pendingStudents = students.filter(s => s.ahaEcardCode && !s.ahaVerificationStatus);
      if (pendingStudents.length > 0) {
        const names = pendingStudents.map(s => `${s.firstName} ${s.lastName}`).join(', ');
        toast.error(`Please verify eCard for: ${names}`);
        return;
      }
      
      // Check if any student's verification failed (excluding manually verified)
      const failedStudents = students.filter(s => 
        s.ahaVerificationStatus && 
        !s.ahaVerificationStatus.verified &&
        s.ahaVerificationStatus.match_status !== 'MANUAL VERIFICATION REQUIRED'
      );
      if (failedStudents.length > 0) {
        const failedNames = failedStudents.map(s => `${s.firstName} ${s.lastName}`).join(', ');
        toast.error(`AHA verification failed for: ${failedNames}. Please check the eCard codes.`);
        return;
      }
      
      // Check if any student requires manual verification but hasn't confirmed
      const pendingManualStudents = students.filter(s => 
        s.ahaVerificationStatus?.match_status === 'MANUAL VERIFICATION REQUIRED' && 
        !s.manuallyVerified
      );
      if (pendingManualStudents.length > 0) {
        const pendingNames = pendingManualStudents.map(s => `${s.firstName} ${s.lastName}`).join(', ');
        toast.error(`Please manually verify and confirm eCard for: ${pendingNames} using the checkbox below the verification result.`);
        return;
      }
    }
    
    // Format students for Stripe order - include AHA eCard data for renewal classes
    const studentsData = students.map(student => ({
      first_name: student.firstName,
      last_name: student.lastName,
      email: student.email,
      phone: student.phone,
      // Add AHA eCard info for renewal classes
      ...(isRenewalClass && student.ahaEcardCode ? {
        aha_ecard: {
          ecard_code: student.ahaEcardCode,
          expiration_date: student.ahaVerificationStatus?.expiration_date || null,
          verification_status: student.ahaVerificationStatus?.match_status || 'Not Verified',
          card_holder_name: student.ahaVerificationStatus?.card_holder_name || null,
          verification_screenshot_url: student.ahaVerificationStatus?.screenshot_url || null,
          aha_returned_name: student.ahaVerificationStatus?.aha_returned_name || null,
          aha_returned_status: student.ahaVerificationStatus?.aha_returned_status || null,
          aha_returned_date: student.ahaVerificationStatus?.aha_returned_date || null,
          is_exact_match: student.ahaVerificationStatus?.is_exact_match || false,
          needs_review: student.ahaVerificationStatus?.needs_review || !student.ahaVerificationStatus?.is_exact_match,
          entered_name: student.ahaVerificationStatus?.entered_name || `${student.firstName} ${student.lastName}`
        }
      } : {})
    }));
    
    // Prepare selected add-ons
    const addonsData = getSelectedAddonsForApi();
    
    // Get parent booking details for Stripe order
    const bookingData = {
      class_type_id: foundBooking.class_type_id,
      student_count: students.length,
      start_time: foundBooking.start_time,
      end_time: foundBooking.end_time,
      students: studentsData,
      class_address: null, // No address for add-ons
      booking_type: "addon",
      parent_booking_id: foundBooking.booking_id,
      addons: addonsData.length > 0 ? addonsData : null,
      instructor_notes: instructorNotes.trim() || null,
      coupon_code: couponApplied ? couponApplied.code : null,
      total_price: couponApplied 
        ? Math.max(0, (addonPricing?.grand_total || 0) - couponApplied.discount_amount)
        : (addonPricing?.grand_total || 0),
      class_type_name: foundBooking.class_type_name || 'CPR Class Add-on'
    };
    setCheckoutData(bookingData);
    
    // Check if total is $0 - skip Stripe and create booking directly
    const finalTotal = couponApplied 
      ? Math.max(0, (addonPricing?.grand_total || 0) - couponApplied.discount_amount)
      : (addonPricing?.grand_total || 0);
    
    if (finalTotal === 0) {
      await createFreeBooking(bookingData);
    } else {
      setShowStripeCheckout(true);
    }
  };

  const resetAddOnMode = () => {
    setIsAddOnMode(false);
    setAddOnLookupDate('');
    setAddOnLookupTime('');
    setFoundBooking(null);
    setAddOnConfirmed(false);
    setStudents([{ 
      id: Date.now(), 
      firstName: '', 
      lastName: '', 
      email: '', 
      phone: '',
      errors: {}
    }]);
    setAcceptedTerms(false);
  };

  const handleBooking = async () => {
    // Validate all inputs before showing Stripe
    if (!selectedSlot) {
      toast.error('Please select a time slot');
      return;
    }
    
    if (students.length === 0) {
      toast.error('Please add at least one student');
      return;
    }

    if (!validateStudents()) {
      toast.error('Please fix the errors in student information');
      return;
    }

    if (!validateAddress()) {
      toast.error('Please enter a valid class address');
      return;
    }

    // Validate BLS renewal eligibility - Check per-student AHA verification
    if (isRenewalClass) {
      // Check if all students have attempted verification
      const unverifiedStudents = students.filter(s => !s.ahaVerificationStatus && !s.ahaEcardCode);
      if (unverifiedStudents.length > 0) {
        toast.error(`Please enter and verify AHA eCard for all students: ${unverifiedStudents.map(s => s.firstName || 'Student').join(', ')}`);
        return;
      }
      
      // Check if any student needs to complete verification
      const pendingStudents = students.filter(s => s.ahaEcardCode && !s.ahaVerificationStatus);
      if (pendingStudents.length > 0) {
        toast.error(`Please click "Verify" for: ${pendingStudents.map(s => s.firstName || 'Student').join(', ')}`);
        return;
      }
      
      // Check if any student's verification failed (excluding manually verified and pending manual)
      const failedStudents = students.filter(s => 
        s.ahaVerificationStatus && 
        !s.ahaVerificationStatus.verified &&
        s.ahaVerificationStatus.match_status !== 'MANUAL VERIFICATION REQUIRED'
      );
      if (failedStudents.length > 0) {
        const failedNames = failedStudents.map(s => s.firstName || 'Student').join(', ');
        toast.error(`AHA verification failed for: ${failedNames}. Please check the eCard codes or consider a full certification course.`);
        return;
      }
      
      // Check if any student requires manual verification but hasn't confirmed
      const manualPendingStudents = students.filter(s => 
        s.ahaVerificationStatus?.match_status === 'MANUAL VERIFICATION REQUIRED' && 
        !s.manuallyVerified
      );
      if (manualPendingStudents.length > 0) {
        const pendingNames = manualPendingStudents.map(s => s.firstName || 'Student').join(', ');
        toast.error(`Please manually verify and confirm eCard for: ${pendingNames} using the checkbox below the verification result.`);
        return;
      }
      
      // Check if any student has expired certification
      const expiredStudents = students.filter(s => s.ahaVerificationStatus?.is_expired);
      if (expiredStudents.length > 0) {
        const expiredNames = expiredStudents.map(s => s.firstName || 'Student').join(', ');
        toast.error(`Not qualified for renewal: ${expiredNames}. The course date is past the expiration date of current certification. Please select New Certification or select another date.`);
        return;
      }
    }

    // Calculate end time
    const classEnd = new Date(new Date(selectedSlot.start).getTime() + selectedClass.duration_hours * 3600000);
    
    // Store the booking data for Stripe - Include per-student AHA verification
    const studentsList = students.map(s => ({
      first_name: s.firstName,
      last_name: s.lastName,
      email: s.email,
      phone: s.phone,
      // Add AHA eCard info for renewal classes (per-student)
      ...(isRenewalClass && s.ahaEcardCode ? {
        aha_ecard: {
          ecard_code: s.ahaEcardCode,
          expiration_date: s.ahaVerificationStatus?.expiration_date || null,
          verification_status: s.ahaVerificationStatus?.match_status || 'Not Verified',
          card_holder_name: s.ahaVerificationStatus?.card_holder_name || null,
          verification_screenshot_url: s.ahaVerificationStatus?.screenshot_url || null,
          aha_returned_name: s.ahaVerificationStatus?.aha_returned_name || null,
          aha_returned_status: s.ahaVerificationStatus?.aha_returned_status || null,
          aha_returned_date: s.ahaVerificationStatus?.aha_returned_date || null,
          is_exact_match: s.ahaVerificationStatus?.is_exact_match || false,
          needs_review: s.ahaVerificationStatus?.needs_review || !s.ahaVerificationStatus?.is_exact_match,
          entered_name: s.ahaVerificationStatus?.entered_name || `${s.firstName} ${s.lastName}`
        }
      } : {})
    }));
    
    const addressData = {
      street: classAddress.street.trim(),
      city: classAddress.city.trim(),
      state: classAddress.state.trim(),
      zip: classAddress.zip.trim(),
      is_outside_service_area: showTravelFeeWarning,
      location_name: locationName.trim() || googlePlaceName || null
    };
    
    const bookingData = {
      class_type_id: selectedClass.id,
      student_count: students.length,
      start_time: selectedSlot.start,
      end_time: classEnd.toISOString(),
      students: studentsList,
      class_address: addressData,
      booking_type: "regular",
      coupon_code: couponApplied?.code || null,
      addons: getSelectedAddonsForApi(),
      instructor_notes: instructorNotes.trim() || null,
      renewal_reminder_opted_in: renewalReminderOptIn,
      location_name: locationName.trim() || googlePlaceName || null,
      reference_code: referenceCode.trim() || null,
      total_price: pricingPreview?.grand_total || 0,
      class_type_name: selectedClass.name || 'CPR Class'
    };
    setCheckoutData(bookingData);
    
    // Check if total is $0 - skip Stripe and create booking directly
    if (pricingPreview?.grand_total === 0) {
      await createFreeBooking(bookingData);
    } else {
      setShowStripeCheckout(true);
    }
  };

  // Stripe: Initiate checkout redirect
  const initiateStripeCheckout = async () => {
    if (!checkoutData) {
      toast.error('Missing checkout data. Please try again.');
      return;
    }
    
    if (!paymentTermsAccepted) {
      toast.error('Please accept the Terms and Conditions to continue');
      return;
    }
    
    setPaymentProcessing(true);
    try {
      toast.loading('Redirecting to secure checkout...', { id: 'stripe-checkout' });
      
      // Create a booking first (pending payment) and get Stripe checkout URL
      const response = await axios.post(`${API}/bookings/create-pending`, {
        ...checkoutData,
        payment_method: 'stripe'
      });
      
      if (response.data.success && response.data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else if (response.data.success && response.data.booking_id) {
        // Booking created, now create Stripe checkout session
        const stripeResponse = await axios.post(`${API}/payments/checkout`, {
          amount: checkoutData.total_price,
          description: `${checkoutData.class_type_name} - ${checkoutData.student_count} student(s)`,
          customer_email: checkoutData.students?.[0]?.email,
          metadata: {
            booking_id: response.data.booking_id,
            class_type_id: checkoutData.class_type_id,
            student_count: String(checkoutData.student_count),
            booking_type: checkoutData.booking_type || 'regular'
          },
          success_url: `${window.location.origin}/booking?payment=success&booking_id=${response.data.booking_id}`,
          cancel_url: `${window.location.origin}/booking?payment=cancelled&booking_id=${response.data.booking_id}`
        });
        
        if (stripeResponse.data.success && stripeResponse.data.checkout_url) {
          window.location.href = stripeResponse.data.checkout_url;
        } else {
          throw new Error('Failed to create checkout session');
        }
      } else {
        throw new Error(response.data.message || 'Failed to initiate checkout');
      }
    } catch (error) {
      toast.dismiss('stripe-checkout');
      console.error('Stripe checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate checkout. Please try again.');
    } finally {
      setPaymentProcessing(false);
    }
  };

  // Create booking directly when total is $0 (no payment required)
  const createFreeBooking = async (bookingData) => {
    setPaymentProcessing(true);
    try {
      toast.loading('Processing your booking...', { id: 'free-booking' });
      
      const params = new URLSearchParams({
        class_type_id: bookingData.class_type_id,
        student_count: bookingData.student_count.toString(),
        start_time: bookingData.start_time,
        end_time: bookingData.end_time,
        students: JSON.stringify(bookingData.students),
        class_address: JSON.stringify(bookingData.class_address),
        booking_type: bookingData.booking_type || "regular",
        parent_booking_id: bookingData.parent_booking_id || "",
        coupon_code: bookingData.coupon_code || "",
        addons: JSON.stringify(bookingData.addons || []),
        instructor_notes: bookingData.instructor_notes || "",
        renewal_reminder_opted_in: bookingData.renewal_reminder_opted_in ? "true" : "false",
        location_name: bookingData.location_name || "",
        reference_code: bookingData.reference_code || ""
      });
      
      const response = await axios.post(`${API}/payments/create-free-booking?${params.toString()}`);
      
      toast.dismiss('free-booking');
      
      if (response.data.success) {
        toast.success('🎉 Booking submitted! Your booking is confirmed and awaiting instructor approval.', { 
          duration: 8000 
        });
        setShowSuccess(true);
        setShowStripeCheckout(false);
        resetFormState();
        fetchAvailability();
      } else {
        throw new Error('Booking creation failed');
      }
    } catch (error) {
      toast.dismiss('free-booking');
      console.error('Free booking error:', error);
      toast.error('Failed to create booking. Please try again or contact support.');
    } finally {
      setPaymentProcessing(false);
    }
  };

  // Handle payment errors
  const onPaymentError = (err) => {
    console.error('Payment error:', err);
    const errorMessage = err?.message || 'Payment error. Please try again.';
    toast.error(errorMessage, { duration: 6000 });
    setShowStripeCheckout(false);
  };

  // Handle payment cancel
  const onPaymentCancel = () => {
    toast.info('Payment cancelled. Your booking was not created.');
    setShowStripeCheckout(false);
  };

  // Handle inline Stripe payment success
  const handleInlinePaymentSuccess = async (paymentResult) => {
    setPaymentProcessing(true);
    
    // Helper to reset form after successful booking
    const resetFormAfterSuccess = () => {
      setReferenceCode('');
      setStudents([{ 
        id: Date.now(), 
        firstName: '', 
        lastName: '', 
        email: '', 
        phone: '',
        ahaEcardCode: '',
        ahaVerificationStatus: null,
        ahaVerifying: false,
        manuallyVerified: false,
        errors: {}
      }]);
      setSelectedClass(null);
      setSelectedSlot(null);
      setSelectedDate(undefined);
      setCouponApplied(null);
      setCouponCode('');
      setPricingPreview(null);
    };
    
    // Helper to create booking with auto-generated reference code
    const createBookingWithAutoRef = async () => {
      const payload = {
        ...checkoutData,
        payment_method: 'stripe',
        payment_intent_id: paymentResult.paymentIntentId,
        payment_status: 'paid'
      };
      // Remove reference_code to force auto-generation
      delete payload.reference_code;
      return axios.post(`${API}/bookings/create-paid`, payload);
    };
    
    try {
      // Create the booking with payment confirmation
      const bookingPayload = {
        ...checkoutData,
        payment_method: 'stripe',
        payment_intent_id: paymentResult.paymentIntentId,
        payment_status: 'paid'
      };
      
      // Only include reference_code if user explicitly provided one AND it's not empty
      if (!bookingPayload.reference_code || bookingPayload.reference_code.trim() === '') {
        delete bookingPayload.reference_code;
      }
      
      const response = await axios.post(`${API}/bookings/create-paid`, bookingPayload);
      
      if (response.data.success) {
        toast.success('🎉 Payment successful! Your booking is confirmed and awaiting instructor approval.', { 
          duration: 8000 
        });
        setShowStripeCheckout(false);
        setCheckoutData(null);
        setShowSuccess(true);
        resetFormAfterSuccess();
      } else {
        throw new Error(response.data.message || 'Failed to create booking');
      }
    } catch (error) {
      console.error('Booking creation error:', error);
      const errorDetail = error.response?.data?.detail || '';
      
      // Provide user-friendly error messages
      let userMessage = 'Payment successful but booking failed. Please contact support.';
      
      if (errorDetail.includes('time slot') || errorDetail.includes('no longer available')) {
        userMessage = 'The selected time slot is no longer available. Your payment will be refunded. Please select a different time.';
      } else if (errorDetail.includes('reference code') || errorDetail.includes('already in use')) {
        // The custom reference code is already taken - retry with auto-generated one
        toast.info('Booking code in use, generating a new one...', { duration: 2000 });
        setReferenceCode('');
        
        try {
          const retryResponse = await createBookingWithAutoRef();
          if (retryResponse.data.success) {
            toast.success('🎉 Payment successful! Your booking is confirmed.', { duration: 8000 });
            setShowStripeCheckout(false);
            setCheckoutData(null);
            setShowSuccess(true);
            resetFormAfterSuccess();
            setPaymentProcessing(false);
            return; // Exit early on success
          }
        } catch (retryError) {
          console.error('Retry with auto-ref also failed:', retryError);
          userMessage = 'Booking failed after retry. Please contact support with your payment confirmation.';
        }
      } else if (errorDetail.includes('already exists') || error.response?.data?.duplicate) {
        userMessage = 'This booking has already been created. Check your email for confirmation.';
        setShowStripeCheckout(false);
        setShowSuccess(true);
      } else if (errorDetail) {
        userMessage = errorDetail;
      }
      
      toast.error(userMessage);
    } finally {
      setPaymentProcessing(false);
    }
  };

  // Check for payment status on page load (returning from Stripe)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment');
    const bookingId = params.get('booking_id');
    
    if (paymentStatus === 'success' && bookingId) {
      toast.success('🎉 Payment successful! Your booking is confirmed and awaiting instructor approval.', { 
        duration: 8000 
      });
      setShowSuccess(true);
      // Clean up URL
      window.history.replaceState({}, '', '/booking');
    } else if (paymentStatus === 'cancelled') {
      toast.info('Payment was cancelled. You can try again.');
      // Clean up URL
      window.history.replaceState({}, '', '/booking');
    }
  }, []);

  return (
    <div className="min-h-screen relative">
      {/* Background Image with Dark Overlay */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/11655091/pexels-photo-11655091.jpeg?auto=compress&cs=tinysrgb&w=1920')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-slate-900/85 backdrop-blur-sm"></div>
      </div>
      
      {/* Content Container */}
      <div className="relative z-10">
      {/* Sandbox Mode Warning Banner */}
      {paymentConfig?.is_sandbox && (
        <div className="bg-amber-500 text-amber-950 py-2 px-4 text-center font-semibold sticky top-0 z-[60]" data-testid="sandbox-banner">
          ⚠️ Bookings not currently being processed as our booking system is under construction
        </div>
      )}
      
      <nav className="bg-slate-800/90 backdrop-blur-md border-b border-slate-700 sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
                <Heart className="h-5 w-5 text-white" data-testid="logo-icon" />
              </div>
              <h1 className="text-xl md:text-2xl font-bold font-heading text-slate-100" data-testid="site-title">Biggest Little Class Scheduler</h1>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                onClick={() => window.location.href = '/signin'}
                variant="outline"
                size="sm"
                className="border-emerald-600 text-emerald-400 hover:bg-emerald-900/30"
                data-testid="class-signin-button"
              >
                <User className="h-4 w-4 mr-2" />
                Class Sign In
              </Button>
              <Button
                onClick={() => window.location.href = '/roster'}
                variant="outline"
                size="sm"
                className="border-teal-600 text-white hover:bg-blue-900/30"
                data-testid="check-roster-button"
              >
                <ClipboardList className="h-4 w-4 mr-2" />
                Check Roster
              </Button>
              <Button
                onClick={() => window.location.href = '/my-invoices'}
                variant="outline"
                size="sm"
                className="border-purple-600 text-purple-400 hover:bg-purple-900/30"
                data-testid="my-invoices-button"
              >
                <FileText className="h-4 w-4 mr-2" />
                My Invoices
              </Button>
              <Button
                onClick={() => window.location.href = '/clients'}
                variant="outline"
                size="sm"
                className="border-blue-600 text-blue-400 hover:bg-blue-900/30"
                data-testid="client-portal-button"
              >
                <Building2 className="h-4 w-4 mr-2" />
                Clients
              </Button>
              <Button
                onClick={() => window.location.href = '/shop'}
                variant="outline"
                size="sm"
                className="border-orange-600 text-orange-400 hover:bg-orange-900/30"
                data-testid="shop-button"
              >
                <Tag className="h-4 w-4 mr-2" />
                Product Store
              </Button>
            </div>
            {/* Admin icon button - positioned to the right */}
            <Button
              onClick={() => window.location.href = '/admin'}
              variant="ghost"
              size="icon"
              className="text-slate-400 hover:text-teal-400 hover:bg-slate-700/50 ml-2"
              data-testid="admin-link"
              title="Admin Dashboard"
            >
              <Shield className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </nav>

      <main className="container mx-auto px-4 md:px-6 max-w-7xl py-12">
        {showSuccess ? (
          <Card className="max-w-2xl mx-auto bg-slate-800 rounded-xl border border-slate-700 shadow-lg p-12 text-center" data-testid="success-card">
            <div className="flex justify-center mb-6">
              <div className="h-20 w-20 rounded-full bg-teal-900 flex items-center justify-center">
                <CheckCircle2 className="h-10 w-10 text-teal-400" />
              </div>
            </div>
            <h2 className="text-3xl font-bold text-slate-100 mb-4 font-heading">Booking Submitted!</h2>
            <p className="text-lg text-white mb-2">Your CPR class booking request has been received.</p>
            <p className="text-slate-400 mb-8">You'll receive a confirmation email once the instructor approves your booking.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => {
                  setShowSuccess(false);
                  resetFormState();
                  setInstructorNotes('');
                  setLocationName('');
                  setGooglePlaceName('');
                  setCouponCode('');
                  setCouponApplied(null);
                  setIsAddOnMode(false);
                  resetAddOnMode();
                }}
                className="bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold px-6 py-3"
                data-testid="return-to-booking-btn"
              >
                Return to Booking
              </Button>
              <Button
                onClick={() => window.location.href = '/shop'}
                variant="outline"
                className="border-orange-600 text-orange-400 hover:bg-orange-900/30 font-semibold px-6 py-3"
                data-testid="go-to-shop-btn"
              >
                <Tag className="h-4 w-4 mr-2" />
                Browse Training Equipment
              </Button>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8 space-y-6">
              <div>
                <h2 className="text-3xl font-bold text-slate-100 mb-2 font-heading" data-testid="page-heading">
                  {isAddOnMode ? 'Add Student to Existing Class' : 'Book Your Class'}
                </h2>
                <p className="text-slate-400">
                  {isAddOnMode 
                    ? 'Add a student to a previously scheduled and paid class.' 
                    : 'Select a class type, choose your preferred time, and complete your booking. If you are adding on to an already scheduled and approved class hit the add to previous booking button and enter the date and start time.'}
                </p>
              </div>

              {/* Step 1: Mode Toggle Buttons */}
              <Card className="bg-slate-800 rounded-xl border border-slate-700 shadow-sm p-4">
                <div className="flex items-center gap-3 mb-3">
                  <span className="flex items-center justify-center w-7 h-7 rounded-full bg-teal-500 text-slate-900 text-sm font-bold">1</span>
                  <h3 className="text-lg font-semibold text-slate-100">Choose Booking Type</h3>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={() => { resetAddOnMode(); setIsAddOnMode(false); setSelectedPublicClass(null); setShowWaitlistForm(false); }}
                    variant={!isAddOnMode && !selectedPublicClass && !showWaitlistForm ? 'default' : 'outline'}
                    className={!isAddOnMode && !selectedPublicClass && !showWaitlistForm
                      ? 'bg-teal-500 hover:bg-teal-400 text-slate-900' 
                      : 'border-teal-600 text-white hover:bg-slate-700'}
                    data-testid="new-booking-mode-btn"
                  >
                    Book Now
                  </Button>
                  <Button
                    onClick={() => { setIsAddOnMode(true); setSelectedClass(null); setSelectedPublicClass(null); setShowWaitlistForm(false); }}
                    variant={isAddOnMode ? 'default' : 'outline'}
                    className={isAddOnMode 
                      ? 'bg-teal-600 hover:bg-teal-500 text-white' 
                      : 'border-teal-600 text-white hover:bg-slate-700'}
                    data-testid="add-to-booking-mode-btn"
                    title="Add a student to an approved class here"
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Add A Student
                  </Button>
                  <Button
                    onClick={() => { setIsAddOnMode(false); setSelectedClass(null); fetchPublicClasses(); setSelectedPublicClass('browse'); setShowWaitlistForm(false); }}
                    variant={selectedPublicClass ? 'default' : 'outline'}
                    className={selectedPublicClass
                      ? 'bg-amber-500 hover:bg-amber-400 text-slate-900' 
                      : 'border-teal-600 text-white hover:bg-slate-700'}
                    data-testid="public-classes-mode-btn"
                  >
                    <Globe className="h-4 w-4 mr-2" />
                    Public Classes
                  </Button>
                  <Button
                    onClick={() => { setShowWaitlistForm(true); setIsAddOnMode(false); setSelectedClass(null); setSelectedPublicClass(null); }}
                    variant={showWaitlistForm ? 'default' : 'outline'}
                    className={showWaitlistForm
                      ? 'bg-purple-600 hover:bg-purple-500 text-white' 
                      : 'border-teal-600 text-white hover:bg-slate-700'}
                    data-testid="waitlist-mode-btn"
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    Interest List
                  </Button>
                </div>
              </Card>

              {/* Pending Booking Banner */}
              {showPendingBanner && pendingBooking && !isAddOnMode && (
                <Card className="bg-amber-900/30 rounded-xl border border-amber-600 shadow-sm p-4" data-testid="pending-booking-banner">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-amber-600/30 flex items-center justify-center">
                        <Edit2 className="h-5 w-5 text-amber-400" />
                      </div>
                      <div>
                        <h3 className="text-amber-300 font-semibold">Continue where you left off?</h3>
                        <p className="text-slate-400 text-sm">
                          You have an unpaid booking for {pendingBooking.class_type_name} on{' '}
                          {format(new Date(pendingBooking.start_time), 'MMM d, yyyy')}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={loadPendingBooking}
                        className="bg-amber-600 hover:bg-amber-500 text-white"
                        data-testid="continue-booking-btn"
                      >
                        Continue Editing
                      </Button>
                      <Button
                        onClick={startFresh}
                        variant="outline"
                        className="border-teal-600 text-white hover:bg-slate-700"
                        data-testid="start-fresh-btn"
                      >
                        Start Fresh
                      </Button>
                    </div>
                  </div>
                </Card>
              )}

              {/* Editing Mode Indicator */}
              {editingBookingId && !isAddOnMode && (
                <Card className="bg-blue-900/30 rounded-xl border border-teal-600 shadow-sm p-4" data-testid="editing-indicator">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-blue-600/30 flex items-center justify-center">
                        <Edit2 className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-blue-300 font-semibold">Editing Existing Booking</h3>
                        <p className="text-slate-400 text-sm">
                          Make your changes and click "Update Booking" below
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={cancelEdit}
                      variant="outline"
                      className="border-teal-600 text-white hover:bg-slate-700"
                      data-testid="cancel-edit-btn"
                    >
                      Cancel Edit
                    </Button>
                  </div>
                </Card>
              )}

              {/* Public Classes Section */}
              {selectedPublicClass && !isAddOnMode && (
                <Card className="bg-slate-800 rounded-xl border border-amber-600 shadow-sm p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-10 w-10 rounded-full bg-amber-600/30 flex items-center justify-center">
                      <Globe className="h-5 w-5 text-amber-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-amber-300">Public Classes</h3>
                      <p className="text-slate-400 text-sm">Join an open class with payment</p>
                    </div>
                  </div>

                  {publicClassesLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
                    </div>
                  ) : publicClasses.length === 0 ? (
                    <div className="text-center py-8">
                      <Globe className="h-12 w-12 mx-auto mb-3 text-slate-500 opacity-50" />
                      <p className="text-slate-400">No public classes available at this time</p>
                      <p className="text-sm text-slate-500 mt-1">Check back later or book a private class</p>
                    </div>
                  ) : selectedPublicClass === 'browse' ? (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {publicClasses.map(pc => {
                        const colors = getClassTypeColors(pc.class_type_id, pc.class_type_name);
                        return (
                        <div 
                          key={pc.id}
                          className={`p-4 ${colors.bg} rounded-lg border ${colors.border} hover:brightness-110 transition-all cursor-pointer`}
                          onClick={() => {
                            setSelectedPublicClass(pc);
                            setPublicClassConfirmed(false);
                            // Scroll to top so user can see the student form
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          data-testid={`public-class-${pc.id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className={`font-semibold ${colors.text}`}>{pc.class_type_name}</h4>
                              {pc.location_name && (
                                <p className="text-slate-300 text-sm mt-1">{pc.location_name}</p>
                              )}
                              <div className="flex items-center gap-4 mt-2 text-sm text-slate-300">
                                <span className="flex items-center gap-1">
                                  <Calendar className={`h-4 w-4 ${colors.icon}`} />
                                  {format(new Date(pc.start_time), 'MMM d, yyyy')}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className={`h-4 w-4 ${colors.icon}`} />
                                  {format(new Date(pc.start_time), 'h:mm a')}
                                </span>
                              </div>
                              {pc.class_address && (
                                <div className="flex items-center gap-1 mt-1 text-sm text-slate-400">
                                  <MapPin className="h-4 w-4" />
                                  {pc.class_address.city}, {pc.class_address.state}
                                </div>
                              )}
                            </div>
                            <div className="text-right">
                              <Badge className={`${pc.registration_closed ? 'bg-red-900/50 text-red-300 border-red-700' : colors.badge}`}>
                                {pc.registration_closed ? 'Registration Closed' : `${pc.spots_available} spots left`}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      )})}
                    </div>
                  ) : (
                    /* Selected a specific public class - show class info and confirmation */
                    (() => {
                      const selectedColors = getClassTypeColors(selectedPublicClass.class_type_id, selectedPublicClass.class_type_name);
                      return (
                    <div className="space-y-4">
                      <div className={`p-4 ${selectedColors.bg} rounded-lg border ${selectedColors.border}`}>
                        <h4 className={`font-semibold ${selectedColors.text}`}>{selectedPublicClass.class_type_name}</h4>
                        {selectedPublicClass.location_name && (
                          <p className="text-slate-300 text-sm mt-1">{selectedPublicClass.location_name}</p>
                        )}
                        <div className={`flex items-center gap-4 mt-2 text-sm ${selectedColors.text}`}>
                          <span className="flex items-center gap-1">
                            <Calendar className={`h-4 w-4 ${selectedColors.icon}`} />
                            {format(new Date(selectedPublicClass.start_time), 'MMMM d, yyyy')}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className={`h-4 w-4 ${selectedColors.icon}`} />
                            {format(new Date(selectedPublicClass.start_time), 'h:mm a')}
                          </span>
                        </div>
                        {selectedPublicClass.class_address && (
                          <div className="flex items-center gap-1 mt-2 text-sm text-slate-300">
                            <MapPin className={`h-4 w-4 ${selectedColors.icon}`} />
                            {selectedPublicClass.class_address.street}, {selectedPublicClass.class_address.city}, {selectedPublicClass.class_address.state} {selectedPublicClass.class_address.zip}
                          </div>
                        )}
                        <div className="mt-2 flex gap-2 flex-wrap">
                          <Badge className={selectedColors.badge}>
                            {selectedPublicClass.spots_available} of {selectedPublicClass.max_students} spots available
                          </Badge>
                          {selectedPublicClass.registration_closed && (
                            <Badge className="bg-red-800 text-red-200">
                              Registration Closed
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Pricing Preview for Public Class */}
                      {students.length > 0 && (
                        <div className={`p-3 ${selectedColors.bg} rounded-lg border ${selectedColors.border}`}>
                          <h5 className={`font-semibold text-sm ${selectedColors.text} mb-2`}>
                            Cost for {students.length} Student{students.length !== 1 ? 's' : ''}
                          </h5>
                          {publicClassPricingLoading ? (
                            <p className="text-sm text-amber-400">Calculating pricing...</p>
                          ) : publicClassPricing ? (
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between text-amber-200">
                                <span>
                                  {students.length} student{students.length !== 1 ? 's' : ''} × ${publicClassPricing.course.price_per_student.toFixed(2)}
                                </span>
                                <span className="font-medium">${publicClassPricing.course.subtotal.toFixed(2)}</span>
                              </div>
                              
                              {/* Rush Fee */}
                              {publicClassPricing.rush_fee && (
                                <div className="flex justify-between text-red-300">
                                  <span>Rush ({publicClassPricing.rush_fee.student_count} × ${publicClassPricing.rush_fee.per_student.toFixed(2)})</span>
                                  <span className="font-medium">${publicClassPricing.rush_fee.amount.toFixed(2)}</span>
                                </div>
                              )}
                              
                              {/* After-Hours Fee */}
                              {publicClassPricing.afterhours_fee && (
                                <div className="flex justify-between text-yellow-300">
                                  <span>After-Hours ({publicClassPricing.afterhours_fee.student_count} × ${publicClassPricing.afterhours_fee.per_student.toFixed(2)})</span>
                                  <span className="font-medium">${publicClassPricing.afterhours_fee.amount.toFixed(2)}</span>
                                </div>
                              )}
                              
                              {/* Addons */}
                              {publicClassPricing.addons && publicClassPricing.addons.length > 0 && (
                                <>
                                  {publicClassPricing.addons.map((addon, idx) => (
                                    <div key={idx} className="flex justify-between text-white">
                                      <span>{addon.name} ({addon.quantity}×)</span>
                                      <span className="font-medium">${(addon.subtotal || addon.total || 0).toFixed(2)}</span>
                                    </div>
                                  ))}
                                </>
                              )}
                              
                              <div className="flex justify-between text-amber-100 pt-2 border-t border-amber-600 font-semibold">
                                <span>Total</span>
                                <span>${publicClassPricing.grand_total.toFixed(2)}</span>
                              </div>
                              
                              {publicClassPricing.rush_fee && (
                                <p className="text-xs text-red-400 mt-2">
                                  ⚠️ Rush fee applies (class within 48 hours)
                                </p>
                              )}
                              {publicClassPricing.afterhours_fee && (
                                <p className="text-xs text-yellow-400 mt-1">
                                  ⚠️ After-hours fee applies (class 7pm-7am)
                                </p>
                              )}
                              <p className="text-xs text-slate-400 mt-1">
                                No travel fee for public classes
                              </p>
                            </div>
                          ) : null}
                        </div>
                      )}

                      {/* Confirm checkbox */}
                      <div className="flex items-center gap-3 mt-4">
                        <input
                          type="checkbox"
                          id="confirm-public-class"
                          checked={publicClassConfirmed}
                          onChange={(e) => setPublicClassConfirmed(e.target.checked)}
                          className="h-4 w-4 rounded border-amber-600 text-amber-500 focus:ring-amber-500 bg-slate-700"
                          data-testid="public-class-confirm-checkbox"
                        />
                        <label htmlFor="confirm-public-class" className="text-sm text-amber-200">
                          I want to enroll in this public class. Continue to enter student info and payment.
                        </label>
                      </div>

                      <div className="flex gap-3 pt-2">
                        <Button
                          onClick={() => {
                            setSelectedPublicClass('browse');
                            setPublicClassConfirmed(false);
                            setStudents([{ id: Date.now(), firstName: '', lastName: '', email: '', phone: '', ahaEcardCode: '', ahaVerificationStatus: null, ahaVerifying: false, manuallyVerified: false, errors: {} }]);
                          }}
                          variant="outline"
                          className="border-teal-600 text-white hover:bg-slate-700"
                        >
                          <ArrowLeft className="h-4 w-4 mr-2" />
                          Back to List
                        </Button>
                      </div>
                    </div>
                      );
                    })()
                  )}
                </Card>
              )}

              {/* Waitlist/Interest List Section */}
              {showWaitlistForm && (
                <Card className="bg-slate-800 rounded-xl border border-purple-600 shadow-sm p-6" data-testid="waitlist-form-card">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-purple-600/30 flex items-center justify-center">
                        <Clock className="h-5 w-5 text-purple-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-purple-300">Interest List / Waitlist</h3>
                        <p className="text-slate-400 text-sm">We'll contact you when a spot becomes available</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={resetWaitlistForm}
                      className="text-slate-400 hover:text-white"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="space-y-4">
                    {/* Class Type Selection */}
                    <div>
                      <label className="text-sm text-slate-300 block mb-2">Which class(es) are you interested in? *</label>
                      <div className="grid grid-cols-2 gap-2">
                        {classTypes.map(ct => {
                          const colors = getClassTypeColors(ct.id, ct.name);
                          const isSelected = waitlistClassTypes.includes(ct.id);
                          return (
                          <label
                            key={ct.id}
                            className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors border-l-4 ${colors.border} ${
                              isSelected
                                ? `${colors.bg} border-opacity-100`
                                : 'bg-slate-700/50 border-opacity-50 hover:border-opacity-100'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setWaitlistClassTypes([...waitlistClassTypes, ct.id]);
                                } else {
                                  setWaitlistClassTypes(waitlistClassTypes.filter(id => id !== ct.id));
                                }
                              }}
                              className="rounded border-slate-500"
                            />
                            <span className={`text-sm font-medium ${colors.text}`}>{ct.name}</span>
                          </label>
                        )})}
                      </div>
                    </div>

                    {/* Needed By Date */}
                    <div>
                      <label className="text-sm text-slate-300 block mb-2">Need certification by (optional)</label>
                      <Input
                        type="date"
                        value={waitlistNeededBy}
                        onChange={(e) => setWaitlistNeededBy(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="bg-slate-700 border-purple-600 text-white"
                        data-testid="waitlist-needed-by"
                      />
                    </div>

                    {/* Student Information */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm text-slate-300">Student Information *</label>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={addWaitlistStudent}
                          className="text-purple-400 hover:text-purple-300"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add Student
                        </Button>
                      </div>
                      
                      <div className="space-y-4">
                        {waitlistStudents.map((student, index) => (
                          <div key={index} className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-sm font-medium text-purple-300">Student {index + 1}</span>
                              {waitlistStudents.length > 1 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeWaitlistStudent(index)}
                                  className="text-red-400 hover:text-red-300 h-6 w-6 p-0"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <Input
                                placeholder="First Name"
                                value={student.first_name}
                                onChange={(e) => updateWaitlistStudent(index, 'first_name', e.target.value)}
                                className="bg-slate-600 border-slate-500 text-white"
                                data-testid={`waitlist-student-${index}-first-name`}
                              />
                              <Input
                                placeholder="Last Name"
                                value={student.last_name}
                                onChange={(e) => updateWaitlistStudent(index, 'last_name', e.target.value)}
                                className="bg-slate-600 border-slate-500 text-white"
                                data-testid={`waitlist-student-${index}-last-name`}
                              />
                              <Input
                                type="email"
                                placeholder="Email"
                                value={student.email}
                                onChange={(e) => updateWaitlistStudent(index, 'email', e.target.value)}
                                className="bg-slate-600 border-slate-500 text-white"
                                data-testid={`waitlist-student-${index}-email`}
                              />
                              <Input
                                type="tel"
                                placeholder="Phone"
                                value={student.phone}
                                onChange={(e) => updateWaitlistStudent(index, 'phone', formatPhoneNumber(e.target.value))}
                                className="bg-slate-600 border-slate-500 text-white"
                                data-testid={`waitlist-student-${index}-phone`}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Notes */}
                    <div>
                      <label className="text-sm text-slate-300 block mb-2">Additional notes (optional)</label>
                      <textarea
                        value={waitlistNotes}
                        onChange={(e) => setWaitlistNotes(e.target.value)}
                        placeholder="Any preferences or special requests..."
                        className="w-full bg-slate-700 border border-slate-600 rounded-lg p-3 text-white placeholder:text-slate-500 min-h-[80px]"
                        data-testid="waitlist-notes"
                      />
                    </div>

                    {/* Submit Button */}
                    <Button
                      onClick={handleWaitlistSubmit}
                      disabled={waitlistSubmitting || waitlistClassTypes.length === 0 || waitlistStudents.some(s => !s.first_name || !s.last_name || !s.email || !s.phone)}
                      className="w-full bg-purple-600 hover:bg-purple-500 text-white py-3"
                      data-testid="waitlist-submit-btn"
                    >
                      {waitlistSubmitting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Adding to Waitlist...
                        </>
                      ) : (
                        <>
                          <Clock className="h-4 w-4 mr-2" />
                          Join Waitlist
                        </>
                      )}
                    </Button>

                    <p className="text-xs text-slate-500 text-center">
                      We'll email you when a spot becomes available in one of your selected classes.
                    </p>
                  </div>
                </Card>
              )}

              {/* Pricing Summary - Always visible when we have pricing data */}
              {!isAddOnMode && (pricingPreview || (selectedClass && selectedSlot)) && (
                <Card className={`bg-gradient-to-r ${selectedClass ? getClassTypeColors(selectedClass.id, selectedClass.name).gradient : 'from-teal-900/50 to-slate-800'} rounded-xl border ${selectedClass ? getClassTypeColors(selectedClass.id, selectedClass.name).border : 'border-teal-700'} shadow-sm p-4`} data-testid="pricing-summary-card">
                  <div className="flex items-center gap-3 mb-3">
                    <DollarSign className={`h-6 w-6 ${selectedClass ? getClassTypeColors(selectedClass.id, selectedClass.name).icon : 'text-teal-400'}`} />
                    <div>
                      <h4 className={`${selectedClass ? getClassTypeColors(selectedClass.id, selectedClass.name).text : 'text-teal-300'} font-semibold`}>Booking Summary</h4>
                      <p className="text-sm text-slate-400">
                        {selectedClass?.name || 'Select a class'} 
                        {selectedSlot && ` • ${format(new Date(selectedSlot.start), 'MMM d, yyyy h:mm a')}`}
                      </p>
                    </div>
                  </div>
                  
                  {pricingPreview ? (
                    <div className="space-y-2 border-t border-teal-700/50 pt-3">
                      {/* Course Cost */}
                      <div className="flex justify-between text-sm">
                        <span className="text-white">
                          {pricingPreview.course.student_count} student{pricingPreview.course.student_count !== 1 ? 's' : ''} × ${pricingPreview.course.price_per_student.toFixed(2)}
                        </span>
                        <span className="text-teal-200 font-medium">${pricingPreview.course.subtotal.toFixed(2)}</span>
                      </div>
                      
                      {/* Rush Fee */}
                      {pricingPreview.rush_fee && (
                        <div className="flex justify-between text-sm">
                          <span className="text-red-300">
                            Rush Fee: {pricingPreview.rush_fee.student_count} × ${pricingPreview.rush_fee.per_student.toFixed(2)}
                          </span>
                          <span className="text-red-300 font-medium">${pricingPreview.rush_fee.amount.toFixed(2)}</span>
                        </div>
                      )}
                      
                      {/* After-Hours Fee */}
                      {pricingPreview.afterhours_fee && (
                        <div className="flex justify-between text-sm">
                          <span className="text-amber-300">
                            After-Hours: {pricingPreview.afterhours_fee.student_count} × ${pricingPreview.afterhours_fee.per_student.toFixed(2)}
                          </span>
                          <span className="text-amber-300 font-medium">${pricingPreview.afterhours_fee.amount.toFixed(2)}</span>
                        </div>
                      )}
                      
                      {/* Travel Fee */}
                      {pricingPreview.mileage && (
                        <div className="flex justify-between text-sm">
                          <span className="text-white">
                            Travel: {pricingPreview.mileage.miles} mi × ${pricingPreview.mileage.rate_per_mile.toFixed(2)}
                          </span>
                          <span className="text-teal-200 font-medium">${pricingPreview.mileage.subtotal.toFixed(2)}</span>
                        </div>
                      )}
                      
                      {/* Add-Ons */}
                      {pricingPreview.addons && pricingPreview.addons.length > 0 && (
                        <>
                          {pricingPreview.addons.map((addon, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span className="text-purple-300">
                                {addon.name} × {addon.quantity}
                              </span>
                              <span className="text-purple-300 font-medium">${(addon.subtotal || addon.total || 0).toFixed(2)}</span>
                            </div>
                          ))}
                        </>
                      )}
                      
                      {/* Total */}
                      <div className="flex justify-between text-base pt-2 border-t border-teal-600/50">
                        <span className="text-teal-100 font-semibold">Total</span>
                        <span className="text-teal-300 font-bold">${pricingPreview.grand_total.toFixed(2)}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-slate-400 text-sm border-t border-teal-700/50 pt-3">
                      Add students to see price breakdown
                    </div>
                  )}
                </Card>
              )}

              {/* Add-on Pricing Summary - only show if registration is NOT closed */}
              {isAddOnMode && addOnConfirmed && addonPricing && !foundBooking?.registration_closed && (
                <Card className="bg-gradient-to-r from-purple-900/50 to-slate-800 rounded-xl border border-purple-700 shadow-sm p-4" data-testid="addon-pricing-summary-card">
                  <div className="flex items-center gap-3 mb-3">
                    <DollarSign className="h-6 w-6 text-purple-400" />
                    <div>
                      <h4 className="text-purple-300 font-semibold">Add-on Summary</h4>
                      <p className="text-sm text-slate-400">
                        Adding to: {foundBooking?.class_name}
                      </p>
                    </div>
                  </div>
                  
                  {/* Payment Waived Message */}
                  {addonPricing.class_fee_waived && (
                    <div className="mb-3 p-2 bg-green-900/30 rounded-lg border border-green-600">
                      <div className="flex items-center gap-2 text-green-300 text-sm">
                        <Check className="h-4 w-4" />
                        <span>{addonPricing.class_fee_waived_message || 'Class fee is prepaid'}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Recurring Client Discount Badge */}
                  {addonPricing.recurring_client && (
                    <div className="mb-3 p-2 bg-blue-900/30 rounded-lg border border-blue-600">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-blue-300 text-sm">
                          <Users className="h-4 w-4" />
                          <span>{addonPricing.recurring_client.client_company || addonPricing.recurring_client.client_name} Rate</span>
                        </div>
                        <span className="text-green-400 text-sm font-medium">
                          Save ${(addonPricing.recurring_client_discount || 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-2 border-t border-purple-700/50 pt-3">
                    {/* Course Cost */}
                    <div className="flex justify-between text-sm">
                      <span className="text-white">
                        {students.length} student{students.length !== 1 ? 's' : ''} × ${addonPricing.course.price_per_student.toFixed(2)}
                        {addonPricing.recurring_client && (
                          <span className="text-slate-500 line-through ml-2">
                            ${addonPricing.course.default_price_per_student?.toFixed(2)}
                          </span>
                        )}
                      </span>
                      <span className="text-purple-200 font-medium">${addonPricing.course.subtotal.toFixed(2)}</span>
                    </div>
                    
                    {/* Rush Fee */}
                    {addonPricing.rush_fee && (
                      <div className="flex justify-between text-sm">
                        <span className="text-red-300">
                          Rush Fee: {addonPricing.rush_fee.student_count} × ${addonPricing.rush_fee.per_student.toFixed(2)}
                        </span>
                        <span className="text-red-300 font-medium">${addonPricing.rush_fee.amount.toFixed(2)}</span>
                      </div>
                    )}
                    
                    {/* After-Hours Fee */}
                    {addonPricing.afterhours_fee && (
                      <div className="flex justify-between text-sm">
                        <span className="text-amber-300">
                          After-Hours: {addonPricing.afterhours_fee.student_count} × ${addonPricing.afterhours_fee.per_student.toFixed(2)}
                        </span>
                        <span className="text-amber-300 font-medium">${addonPricing.afterhours_fee.amount.toFixed(2)}</span>
                      </div>
                    )}
                    
                    {/* Product Add-ons */}
                    {addonPricing.addons && addonPricing.addons.length > 0 && (
                      <>
                        <div className="pt-2 border-t border-purple-600/30">
                          <span className="text-xs text-teal-400 uppercase font-medium">Product Add-ons</span>
                        </div>
                        {addonPricing.addons.map((addon, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-teal-200">
                              {addon.name} × {addon.quantity}
                            </span>
                            <span className="text-teal-200 font-medium">${(addon.subtotal || addon.total || 0).toFixed(2)}</span>
                          </div>
                        ))}
                      </>
                    )}
                    
                    {/* Total */}
                    <div className="flex justify-between text-base pt-2 border-t border-purple-600/50">
                      <span className="text-purple-100 font-semibold">Total</span>
                      <span className="text-purple-300 font-bold">${addonPricing.grand_total.toFixed(2)}</span>
                    </div>
                  </div>
                </Card>
              )}

              {/* Add to Previous Booking UI */}
              {isAddOnMode && (
                <Card className="bg-purple-900/30 rounded-xl border border-purple-700 shadow-sm p-6" data-testid="addon-lookup-card">
                  <h3 className="text-xl font-semibold mb-4 font-heading flex items-center gap-2 text-purple-300">
                    <Users className="h-5 w-5" />
                    Find Your Existing Class
                  </h3>
                  <p className="text-sm text-purple-200 mb-4">
                    Look up your class using either the booking code or the date and time. The class must be approved before additional students can be added.
                  </p>
                  
                  {/* Lookup Mode Toggle */}
                  <div className="flex gap-2 mb-4">
                    <Button
                      variant={lookupMode === 'code' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => { setLookupMode('code'); setFoundBooking(null); }}
                      className={lookupMode === 'code' 
                        ? 'bg-teal-600 hover:bg-teal-500 text-white' 
                        : 'border-purple-600 text-purple-300 hover:bg-purple-900/50'}
                      data-testid="lookup-mode-code"
                    >
                      <Tag className="h-4 w-4 mr-1" />
                      Booking Code
                    </Button>
                    <Button
                      variant={lookupMode === 'datetime' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => { setLookupMode('datetime'); setFoundBooking(null); }}
                      className={lookupMode === 'datetime' 
                        ? 'bg-teal-600 hover:bg-teal-500 text-white' 
                        : 'border-purple-600 text-purple-300 hover:bg-purple-900/50'}
                      data-testid="lookup-mode-datetime"
                    >
                      <Calendar className="h-4 w-4 mr-1" />
                      Date & Time
                    </Button>
                  </div>
                  
                  {/* Lookup by Code */}
                  {lookupMode === 'code' && (
                    <div className="mb-4">
                      <Label className="text-purple-200 mb-2 block">Booking Code</Label>
                      <Input
                        type="text"
                        value={lookupReferenceCode}
                        onChange={(e) => setLookupReferenceCode(e.target.value.toLowerCase())}
                        placeholder="Enter your booking code (e.g., 1234 or teamname)"
                        className="bg-slate-700 border-purple-600 text-white lowercase"
                        data-testid="addon-code-input"
                      />
                      <p className="text-xs text-purple-400 mt-1">The code was provided when the class was booked</p>
                    </div>
                  )}
                  
                  {/* Lookup by Date/Time */}
                  {lookupMode === 'datetime' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <Label className="text-purple-200 mb-2 block">Class Date</Label>
                        <Input
                          type="date"
                          value={addOnLookupDate}
                          onChange={(e) => setAddOnLookupDate(e.target.value)}
                          className="bg-slate-700 border-purple-600 text-white"
                          data-testid="addon-date-input"
                        />
                      </div>
                      <div>
                        <Label className="text-purple-200 mb-2 block">Start Time</Label>
                        <Input
                          type="time"
                          value={addOnLookupTime}
                          onChange={(e) => setAddOnLookupTime(e.target.value)}
                          className="bg-slate-700 border-purple-600 text-white [&::-webkit-calendar-picker-indicator]:invert"
                          data-testid="addon-time-input"
                          placeholder="Enter start time"
                        />
                      </div>
                    </div>
                  )}
                  
                  <Button
                    onClick={handleLookupBooking}
                    disabled={lookupLoading || (lookupMode === 'code' ? !lookupReferenceCode.trim() : (!addOnLookupDate || !addOnLookupTime))}
                    className="bg-teal-600 hover:bg-teal-500 text-white"
                    data-testid="lookup-booking-btn"
                  >
                    {lookupLoading ? 'Searching...' : 'Find Booking'}
                  </Button>
                  
                  {foundBooking && (
                    <div className="mt-4 p-4 bg-slate-800 rounded-lg border border-purple-600">
                      <h4 className="text-lg font-semibold text-purple-300 mb-2">Booking Found!</h4>
                      <p className="text-white mb-2">
                        <strong>Class:</strong> {foundBooking.class_type_name}
                      </p>
                      <p className="text-white mb-2">
                        <strong>Date:</strong> {foundBooking.date}
                      </p>
                      {/* Location Information */}
                      {(foundBooking.location_name || foundBooking.location_address) && (
                        <div className="mb-2 p-2 bg-purple-900/30 rounded-lg">
                          {foundBooking.location_name && (
                            <p className="text-purple-200">
                              <strong>Location:</strong> {foundBooking.location_name}
                            </p>
                          )}
                          {foundBooking.location_address && (
                            <p className="text-purple-300 text-sm mt-1">
                              <MapPin className="inline-block h-4 w-4 mr-1" />
                              {foundBooking.location_address}
                            </p>
                          )}
                        </div>
                      )}
                      <p className="text-white mb-2">
                        <strong>Students ({foundBooking.student_count}):</strong>{' '}
                        <span className="text-teal-400">
                          {foundBooking.students_display || foundBooking.student_first_name}
                        </span>
                      </p>
                      
                      {/* Registration Closed Warning */}
                      {foundBooking.registration_closed && (
                        <div className="mt-4 p-4 bg-red-900/50 rounded-lg border border-red-600">
                          <div className="flex items-start gap-3">
                            <AlertCircle className="h-6 w-6 text-red-400 flex-shrink-0 mt-0.5" />
                            <div>
                              <h5 className="font-semibold text-red-300 mb-1">Registration Closed</h5>
                              <p className="text-red-200 text-sm">
                                {foundBooking.registration_closed_message || "Sorry, registration for this class is closed and students can no longer be added. Please contact Biggest Little CPR at classes@biggestlittlecpr.com or call 775-722-8056."}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Only show pricing and confirmation if registration is NOT closed */}
                      {!foundBooking.registration_closed && (
                        <>
                          {/* Addon Pricing Preview */}
                          {students.length > 0 && (
                        <div className="mt-4 p-3 bg-purple-900/30 rounded-lg border border-purple-700">
                          <h5 className="font-semibold text-sm text-purple-300 mb-2">Cost for Adding {students.length} Student{students.length !== 1 ? 's' : ''}</h5>
                          {addonPricingLoading ? (
                            <p className="text-sm text-purple-400">Calculating pricing...</p>
                          ) : addonPricing ? (
                            <div className="space-y-1 text-sm">
                              {/* Payment Waived Notice */}
                              {addonPricing.class_fee_waived && (
                                <div className="flex items-center gap-2 text-green-400 mb-2">
                                  <Check className="h-4 w-4" />
                                  <span className="text-xs">Prepaid class - no charge</span>
                                </div>
                              )}
                              
                              {/* Recurring Client Discount Notice */}
                              {addonPricing.recurring_client && (
                                <div className="flex items-center justify-between text-blue-300 mb-2 p-1.5 bg-blue-900/30 rounded">
                                  <span className="text-xs">{addonPricing.recurring_client.client_company || 'Client'} Rate</span>
                                  <span className="text-green-400 text-xs">-${(addonPricing.recurring_client_discount || 0).toFixed(2)}</span>
                                </div>
                              )}
                              
                              <div className="flex justify-between text-purple-200">
                                <span>
                                  {students.length} student{students.length !== 1 ? 's' : ''} × ${addonPricing.course.price_per_student.toFixed(2)}
                                  {addonPricing.recurring_client && addonPricing.course.default_price_per_student && (
                                    <span className="text-slate-500 line-through ml-1 text-xs">
                                      ${addonPricing.course.default_price_per_student.toFixed(2)}
                                    </span>
                                  )}
                                </span>
                                <span className="font-medium">${addonPricing.course.subtotal.toFixed(2)}</span>
                              </div>
                              
                              {/* Rush Fee - Per Student */}
                              {addonPricing.rush_fee && (
                                <div className="flex justify-between text-red-300">
                                  <span>Rush ({addonPricing.rush_fee.student_count} × ${addonPricing.rush_fee.per_student.toFixed(2)})</span>
                                  <span className="font-medium">${addonPricing.rush_fee.amount.toFixed(2)}</span>
                                </div>
                              )}
                              
                              {/* After-Hours Fee - Per Student */}
                              {addonPricing.afterhours_fee && (
                                <div className="flex justify-between text-amber-300">
                                  <span>After-Hours ({addonPricing.afterhours_fee.student_count} × ${addonPricing.afterhours_fee.per_student.toFixed(2)})</span>
                                  <span className="font-medium">${addonPricing.afterhours_fee.amount.toFixed(2)}</span>
                                </div>
                              )}
                              
                              <div className="flex justify-between text-purple-100 pt-2 border-t border-purple-600 font-semibold">
                                <span>Total</span>
                                <span>${addonPricing.grand_total.toFixed(2)}</span>
                              </div>
                              {addonPricing.rush_fee && (
                                <p className="text-xs text-red-400 mt-2">
                                  ⚠️ Rush fee applies (class within 48 hours)
                                </p>
                              )}
                              {addonPricing.afterhours_fee && (
                                <p className="text-xs text-amber-400 mt-1">
                                  ⚠️ After-hours fee applies (class 7pm-7am)
                                </p>
                              )}
                              <p className="text-xs text-slate-400 mt-1">
                                No travel fee for add-on students
                              </p>
                            </div>
                          ) : null}
                        </div>
                      )}
                      
                      {/* Only show confirmation if registration is NOT closed */}
                      {!foundBooking.registration_closed && (
                        <div className="flex items-center gap-3 mt-4">
                          <input
                            type="checkbox"
                            id="confirm-booking"
                            checked={addOnConfirmed}
                            onChange={(e) => setAddOnConfirmed(e.target.checked)}
                            className="h-4 w-4 rounded border-purple-600 text-purple-500 focus:ring-purple-500 bg-slate-700"
                            data-testid="addon-confirm-checkbox"
                          />
                          <label htmlFor="confirm-booking" className="text-sm text-purple-200">
                            Yes, this is my booking and I want to add student(s)
                          </label>
                        </div>
                      )}
                        </>
                      )}
                    </div>
                  )}
                </Card>
              )}

              {userBookings.length > 0 && !isAddOnMode && (
                <Card className="bg-slate-800 rounded-xl border border-slate-700 shadow-sm p-6" data-testid="user-bookings-card">
                  <h3 className="text-xl font-semibold mb-4 font-heading flex items-center gap-2 text-teal-400">
                    <AlertCircle className="h-5 w-5" />
                    Your Bookings
                  </h3>
                  <div className="space-y-3">
                    {userBookings.map((booking) => (
                      <div key={booking.id} className="bg-slate-700 rounded-lg p-4 border border-teal-600" data-testid={`user-booking-${booking.id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-slate-100">{booking.class_type_name}</h4>
                          <Badge className={
                            booking.status === 'pending' ? 'bg-amber-900/50 text-amber-400 border-amber-700' :
                            booking.status === 'approved' ? 'bg-teal-900/50 text-teal-400 border-teal-700' :
                            'bg-red-900/50 text-red-400 border-red-700'
                          } data-testid={`booking-status-${booking.id}`}>
                            {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                          </Badge>
                        </div>
                        <div className="text-sm text-white">
                          <p><strong>Date:</strong> {formatInTimeZone(new Date(booking.start_time), 'America/Los_Angeles', 'MMMM d, yyyy')}</p>
                          <p><strong>Time:</strong> {formatInTimeZone(new Date(booking.start_time), 'America/Los_Angeles', 'h:mm a')} - {formatInTimeZone(new Date(booking.end_time), 'America/Los_Angeles', 'h:mm a')}</p>
                          {booking.status === 'pending' && (
                            <p className="text-amber-400 mt-2 text-xs">⏳ Awaiting instructor approval</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Only show class selection and calendar for new bookings */}
              {!isAddOnMode && !selectedPublicClass && (
              <>
              {/* Class Type Selection */}
              <Card className="bg-slate-800 rounded-xl border border-slate-700 shadow-sm p-6" data-testid="class-selector-card">
                <h3 className="text-xl font-semibold mb-4 font-heading flex items-center gap-2 text-slate-100">
                  <Heart className="h-5 w-5 text-teal-400" />
                  Select Class Type
                </h3>
                
                {/* Category Filter */}
                {courseCategories.length > 0 && (
                  <div className="mb-4">
                    <label className="text-sm text-slate-400 mb-2 block">Filter by Category</label>
                    <Select
                      value={selectedCategory}
                      onValueChange={(value) => {
                        setSelectedCategory(value);
                        // Reset all selections when category changes
                        setSelectedCourseType(null);
                        setSelectedClass(null);
                        setSelectedDate(undefined);
                        setSelectedSlot(null);
                        setSelectedClassPrice(null);
                      }}
                    >
                      <SelectTrigger className="w-full h-10 bg-slate-700 text-white border-slate-600 hover:bg-slate-600" data-testid="category-filter-select">
                        <SelectValue placeholder="All Categories" />
                      </SelectTrigger>
                      <SelectContent className="dark-dropdown-content">
                        <SelectItem value="all">All Categories</SelectItem>
                        {courseCategories.map(cat => (
                          <SelectItem key={cat.id} value={cat.slug}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {/* Step 1: Course Type Selection */}
                <div className="mb-4">
                  <label className="text-sm text-slate-400 mb-2 block">1. Select Course Type</label>
                  <Select
                    value={selectedCourseType || ""}
                    onValueChange={(value) => {
                      setSelectedCourseType(value);
                      // Reset subsequent selections
                      setSelectedClass(null);
                      setSelectedDate(undefined);
                      setSelectedSlot(null);
                      setSelectedClassPrice(null);
                      
                      // If only one class in the group, auto-select it
                      const group = courseTypeGroups.find(g => g.name === value);
                      if (group && group.classes.length === 1) {
                        const classType = group.classes[0];
                        setSelectedClass(classType);
                        // Fetch price
                        axios.post(`${API}/pricing-preview`, {
                          class_type_id: classType.id,
                          student_count: 1,
                          start_time: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
                        }).then(response => {
                          if (response.data?.course?.price_per_student) {
                            setSelectedClassPrice(parseFloat(response.data.course.price_per_student));
                          }
                        }).catch(err => console.error('Error fetching class price:', err));
                      }
                    }}
                  >
                    <SelectTrigger 
                      className="w-full h-12 bg-slate-700 text-white border-teal-600 hover:bg-slate-600 [&>svg]:text-white" 
                      data-testid="course-type-select"
                    >
                      <SelectValue placeholder="Choose a course type..." />
                    </SelectTrigger>
                    <SelectContent className="dark-dropdown-content max-h-80">
                      {courseTypeGroups.length === 0 ? (
                        <SelectItem value="__none__" disabled className="text-slate-400">
                          No courses available
                        </SelectItem>
                      ) : (
                        courseTypeGroups.map((group) => {
                          const colors = getClassTypeColors(group.classes[0]?.id || '', group.name);
                          return (
                          <SelectItem 
                            key={group.name} 
                            value={group.name}
                            data-testid={`course-type-${group.name.replace(/\s+/g, '-').toLowerCase()}`}
                            className={`border-l-4 ${colors.border} mb-1 hover:${colors.bg}`}
                          >
                            <div className="flex items-center justify-between w-full gap-4">
                              <span className={`font-medium ${colors.text}`}>{group.name}</span>
                              {group.hasVariants && (
                                <span className="text-slate-400 text-xs">({group.classes.length} options)</span>
                              )}
                            </div>
                          </SelectItem>
                        )})
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Step 2: Age Option / Variant Selection - Only show if course type has variants */}
                {selectedCourseType && selectedCourseTypeVariants.length > 1 && (
                  <div className="mb-4">
                    <label className="text-sm text-slate-400 mb-2 block">2. Select Class Option</label>
                    <Select
                      value={selectedClass?.id || ""}
                      onValueChange={async (value) => {
                        const classType = selectedCourseTypeVariants.find(c => c.id === value);
                        setSelectedClass(classType);
                        // Reset subsequent selections when class changes
                        setSelectedDate(undefined);
                        setSelectedSlot(null);
                        setSelectedClassPrice(null);
                        
                        // Fetch price for the selected class
                        if (classType) {
                          try {
                            const response = await axios.post(`${API}/pricing-preview`, {
                              class_type_id: classType.id,
                              student_count: 1,
                              start_time: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
                            });
                            if (response.data?.course?.price_per_student) {
                              setSelectedClassPrice(parseFloat(response.data.course.price_per_student));
                            }
                          } catch (err) {
                            console.error('Error fetching class price:', err);
                          }
                        }
                      }}
                    >
                      <SelectTrigger 
                        className="w-full h-12 bg-slate-700 text-white border-teal-600 hover:bg-slate-600 [&>svg]:text-white" 
                        data-testid="class-variant-select"
                      >
                        <SelectValue placeholder="Choose an option..." />
                      </SelectTrigger>
                      <SelectContent className="dark-dropdown-content">
                        {selectedCourseTypeVariants.map((classType) => {
                          const colors = getClassTypeColors(classType.id, classType.name);
                          return (
                          <SelectItem 
                            key={classType.id} 
                            value={classType.id}
                            data-testid={`class-option-${classType.id}`}
                            className={`border-l-4 ${colors.border} mb-1 hover:${colors.bg}`}
                          >
                            <div className="flex items-center justify-between w-full gap-4">
                              <span className={`font-medium ${colors.text}`}>{classType.variant || classType.name}</span>
                              <span className="text-slate-400 text-sm">({classType.duration_hours}h)</span>
                            </div>
                          </SelectItem>
                        )})}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {selectedClass && (
                  <div className={`mt-4 p-3 ${getClassTypeColors(selectedClass.id, selectedClass.name).bg} rounded-lg border ${getClassTypeColors(selectedClass.id, selectedClass.name).border}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`flex items-center gap-2 ${getClassTypeColors(selectedClass.id, selectedClass.name).accent}`}>
                          <Clock className="h-4 w-4" />
                          <span className="text-sm font-medium">{selectedClass.duration_hours} hours</span>
                        </div>
                        {selectedClassPrice && (
                          <div className="flex items-center gap-1 text-green-400">
                            <DollarSign className="h-4 w-4" />
                            <span className="text-sm font-semibold">${selectedClassPrice.toFixed(2)} /Student</span>
                          </div>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => fetchClassDetails(selectedClass.id)}
                        disabled={classDetailsLoading}
                        className={`${getClassTypeColors(selectedClass.id, selectedClass.name).border} ${getClassTypeColors(selectedClass.id, selectedClass.name).accent} hover:${getClassTypeColors(selectedClass.id, selectedClass.name).bg}`}
                        data-testid="view-class-details-btn"
                      >
                        {classDetailsLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Info className="h-4 w-4 mr-1" />
                            View Details
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </Card>

              {/* Date Selection - Only show after class is selected */}
              <Card className={`bg-slate-800 rounded-xl border shadow-sm p-6 transition-all ${
                selectedClass ? 'border-slate-700' : 'border-slate-700/50 opacity-50 pointer-events-none'
              }`} data-testid="date-selector-card">
                <h3 className="text-xl font-semibold mb-4 font-heading flex items-center gap-2 text-slate-100">
                  <Calendar className="h-5 w-5 text-teal-400" />
                  Select Date
                  {!selectedClass && <span className="text-sm font-normal text-slate-500 ml-2">(Select class first)</span>}
                </h3>
                
                {/* Availability Calendar */}
                <div className="flex flex-col items-center">
                  {/* Month navigation */}
                  <div className="flex items-center justify-between w-full max-w-xs mb-4">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setAvailabilityMonth(new Date(availabilityMonth.getFullYear(), availabilityMonth.getMonth() - 1))}
                      className="h-8 w-8 p-0 text-slate-400 hover:text-white"
                      disabled={availabilityMonth.getMonth() === today.getMonth() && availabilityMonth.getFullYear() === today.getFullYear()}
                    >
                      <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <span className="text-lg font-semibold text-slate-100">
                      {format(availabilityMonth, 'MMMM yyyy')}
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setAvailabilityMonth(new Date(availabilityMonth.getFullYear(), availabilityMonth.getMonth() + 1))}
                      className="h-8 w-8 p-0 text-slate-400 hover:text-white"
                      disabled={availabilityMonth >= addMonths(today, 2)}
                    >
                      <ArrowLeft className="h-5 w-5 rotate-180" />
                    </Button>
                  </div>
                  
                  {/* Day headers */}
                  <div className="grid grid-cols-7 gap-2 mb-2 w-full max-w-xs">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d, i) => (
                      <div key={i} className="text-center text-sm text-slate-400 font-semibold">{d}</div>
                    ))}
                  </div>
                  
                  {/* Calendar grid */}
                  {loadingMonthlyAvailability ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-teal-400" />
                      <span className="ml-3 text-slate-400">Loading availability...</span>
                    </div>
                  ) : (
                    <div className="grid grid-cols-7 gap-2 w-full max-w-xs">
                      {(() => {
                        const firstDay = new Date(availabilityMonth.getFullYear(), availabilityMonth.getMonth(), 1);
                        const lastDay = new Date(availabilityMonth.getFullYear(), availabilityMonth.getMonth() + 1, 0);
                        const startPadding = firstDay.getDay();
                        const days = [];
                        
                        // Empty cells for padding
                        for (let i = 0; i < startPadding; i++) {
                          days.push(<div key={`pad-${i}`} className="w-10 h-10" />);
                        }
                        
                        // Days of the month
                        for (let d = 1; d <= lastDay.getDate(); d++) {
                          const dateStr = `${availabilityMonth.getFullYear()}-${String(availabilityMonth.getMonth() + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                          const dayDate = new Date(availabilityMonth.getFullYear(), availabilityMonth.getMonth(), d);
                          const slots = monthlyAvailability[dateStr];
                          const isToday = new Date().toDateString() === dayDate.toDateString();
                          const isPast = dayDate < today;
                          const isFuture = dayDate > maxBookingDate;
                          const isDisabled = isPast || isFuture;
                          const isSelected = selectedDate && dayDate.toDateString() === selectedDate.toDateString();
                          
                          days.push(
                            <button
                              key={d}
                              onClick={() => {
                                if (!isDisabled) {
                                  if (selectedDate && dayDate.getTime() === selectedDate.getTime()) {
                                    fetchAvailability();
                                  } else {
                                    setSelectedDate(dayDate);
                                    setSelectedSlot(null);
                                  }
                                }
                              }}
                              disabled={isDisabled}
                              className={`w-10 h-10 rounded-lg text-sm font-semibold transition-all ${
                                isDisabled 
                                  ? 'bg-slate-800 text-slate-600 cursor-not-allowed' 
                                  : slots === undefined 
                                    ? 'bg-slate-700 text-white hover:bg-slate-600'
                                    : slots === 0
                                      ? 'bg-red-600 text-white cursor-not-allowed'
                                      : `${getAvailabilityColor(slots)} text-white hover:opacity-80 cursor-pointer`
                              } ${isToday ? 'ring-2 ring-cyan-400' : ''} ${
                                isSelected ? 'ring-2 ring-teal-300 ring-offset-2 ring-offset-slate-800' : ''
                              }`}
                              title={isDisabled ? 'Not available' : slots === 0 ? 'No availability' : `${slots ?? '?'} time slots available`}
                              data-testid={`avail-day-${d}`}
                            >
                              {d}
                            </button>
                          );
                        }
                        
                        return days;
                      })()}
                    </div>
                  )}
                  
                  {/* Legend */}
                  <div className="flex flex-wrap items-center justify-center gap-4 mt-6 pt-4 border-t border-slate-700 w-full max-w-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-red-600" />
                      <span className="text-xs text-slate-400">No Availability</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-amber-500" />
                      <span className="text-xs text-slate-400">Limited</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-emerald-500" />
                      <span className="text-xs text-slate-400">High</span>
                    </div>
                  </div>
                  
                  <p className="text-xs text-slate-500 text-center mt-4">
                    You can book up to 60 days in advance
                  </p>
                </div>
              </Card>

              {/* Time Selection - Only show after date is selected */}
              <Card className={`bg-slate-800 rounded-xl border shadow-sm p-6 transition-all ${
                selectedClass && selectedDate ? 'border-slate-700' : 'border-slate-700/50 opacity-50 pointer-events-none'
              }`} data-testid="time-slots-card">
                <h3 className="text-xl font-semibold mb-4 font-heading flex items-center gap-2 text-slate-100">
                  <Clock className="h-5 w-5 text-teal-400" />
                  {selectedDate ? `Start Time - ${format(selectedDate, 'MMMM d, yyyy')}` : 'Select Start Time'}
                  {!selectedDate && <span className="text-sm font-normal text-slate-500 ml-2">(Select date first)</span>}
                </h3>
                {selectedClass && selectedDate && (
                  availability ? (
                    (() => {
                      const availableSlots = availability.slots.filter(slot => slot.available);
                      return availableSlots.length > 0 ? (
                        <div>
                          <p className="text-xs text-slate-400 mb-4">Select your preferred start time ({availableSlots.length} available)</p>
                          <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 max-h-96 overflow-y-auto">
                            {availableSlots.map((slot, index) => (
                              <button
                                key={index}
                                onClick={() => {
                                  setSelectedSlot(slot);
                                  setIsWithin48Hours(checkIfWithin48Hours(slot.start));
                                  // Scroll to top so user can see the student form
                                  window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                                className={`p-2 rounded-lg text-xs font-medium transition-all ${
                                  selectedSlot?.start === slot.start
                                    ? 'bg-teal-500 text-slate-900 shadow-md ring-2 ring-teal-400'
                                    : 'bg-teal-900/40 text-teal-300 hover:bg-teal-900/60 border border-teal-700'
                                }`}
                                data-testid={`time-slot-${index}`}
                              >
                                {formatInTimeZone(new Date(slot.start), 'America/Los_Angeles', 'h:mm a')}
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <p className="text-slate-400 text-center py-8">No available times for this date. Please select another date.</p>
                      );
                    })()
                  ) : (
                    <p className="text-slate-400 text-center py-8">Loading availability...</p>
                  )
                )}
              </Card>
              </>
              )}
              {/* End class selection for new booking mode */}
            </div>

            {/* Right Column: Student Information & Location - Always visible for new private bookings */}
            {!isAddOnMode && !selectedPublicClass && !showWaitlistForm && (
            <div className="lg:col-span-4">
              <Card className="bg-slate-800 rounded-xl border border-slate-700 shadow-sm p-6 sticky top-4" data-testid="booking-form-card">
                {/* Show selection summary when class/date/time selected */}
                {selectedSlot && (
                  <div className="mb-4 p-3 bg-teal-900/30 rounded-lg border border-teal-700">
                    <p className="text-sm text-teal-400 font-medium">{selectedClass?.name}</p>
                    <p className="text-xs text-slate-400">
                      {selectedDate && format(selectedDate, 'MMMM d, yyyy')} at {formatInTimeZone(new Date(selectedSlot.start), 'America/Los_Angeles', 'h:mm a')}
                    </p>
                  </div>
                )}
                
                {/* Prompt to select class/date/time if not yet selected */}
                {!selectedSlot && (
                  <div className="mb-4 p-4 bg-slate-700/50 rounded-lg border border-teal-600 text-center">
                    <Calendar className="h-8 w-8 text-slate-500 mx-auto mb-2" />
                    <p className="text-sm text-slate-400">Select a class, date, and time to continue</p>
                  </div>
                )}
                
                <h3 className="text-xl font-semibold mb-4 font-heading flex items-center gap-2 text-slate-100">
                  <Users className="h-5 w-5 text-teal-400" />
                  Student Information
                  <span className="text-xs font-normal text-red-400 ml-1">* Required</span>
                </h3>
                
                <div className="space-y-4">
                  {students.map((student, index) => (
                    <div key={student.id} className="p-4 bg-slate-700 rounded-lg border border-teal-600">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-semibold text-white">Student {index + 1}</span>
                        {students.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeStudent(student.id)}
                            className="text-red-400 hover:text-red-300 p-1"
                            data-testid={`remove-student-${index}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs font-medium text-slate-400 mb-1 block">
                            First Name <span className="text-red-400">*</span>
                          </Label>
                          <Input
                            placeholder="John"
                            value={student.firstName}
                            onChange={(e) => updateStudent(student.id, 'firstName', e.target.value)}
                            className={`h-9 text-sm bg-slate-800 border-teal-600 text-slate-100 placeholder:text-slate-500 ${student.errors?.firstName ? 'border-red-500' : ''}`}
                            data-testid={`student-${index}-firstname`}
                            required
                          />
                          {student.errors?.firstName && (
                            <p className="text-xs text-red-400 mt-1">{student.errors.firstName}</p>
                          )}
                        </div>
                        <div>
                          <Label className="text-xs font-medium text-slate-400 mb-1 block">
                            Last Name <span className="text-red-400">*</span>
                          </Label>
                          <Input
                            placeholder="Doe"
                            value={student.lastName}
                            onChange={(e) => updateStudent(student.id, 'lastName', e.target.value)}
                            className={`h-9 text-sm bg-slate-800 border-teal-600 text-slate-100 placeholder:text-slate-500 ${student.errors?.lastName ? 'border-red-500' : ''}`}
                            data-testid={`student-${index}-lastname`}
                            required
                          />
                          {student.errors?.lastName && (
                            <p className="text-xs text-red-400 mt-1">{student.errors.lastName}</p>
                          )}
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs font-medium text-slate-400 mb-1 block">
                            Email <span className="text-red-400">*</span>
                          </Label>
                          <Input
                            type="email"
                            placeholder="john@example.com"
                            value={student.email}
                            onChange={(e) => updateStudent(student.id, 'email', e.target.value)}
                            className={`h-9 text-sm bg-slate-800 border-teal-600 text-slate-100 placeholder:text-slate-500 ${student.errors?.email ? 'border-red-500' : ''}`}
                            data-testid={`student-${index}-email`}
                            required
                          />
                          {student.errors?.email && (
                            <p className="text-xs text-red-400 mt-1">{student.errors.email}</p>
                          )}
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs font-medium text-slate-400 mb-1 block">
                            Phone <span className="text-red-400">*</span>
                          </Label>
                          <Input
                            type="tel"
                            placeholder="(555) 123-4567"
                            value={student.phone}
                            onChange={(e) => updateStudent(student.id, 'phone', formatPhoneNumber(e.target.value))}
                            className={`h-9 text-sm bg-slate-800 border-teal-600 text-slate-100 placeholder:text-slate-500 ${student.errors?.phone ? 'border-red-500' : ''}`}
                            data-testid={`student-${index}-phone`}
                            required
                          />
                          {student.errors?.phone && (
                            <p className="text-xs text-red-400 mt-1">{student.errors.phone}</p>
                          )}
                        </div>
                        
                        {/* Per-Student AHA eCard Verification - Only for Renewal Classes */}
                        {isRenewalClass && (
                          <div className="col-span-2 mt-3 p-3 bg-amber-900/30 rounded-lg border border-amber-600/50" data-testid={`student-${index}-aha-section`}>
                            <Label className="text-xs font-medium text-amber-300 mb-2 block flex items-center gap-2">
                              <Shield className="h-3.5 w-3.5" />
                              AHA eCard Verification <span className="text-red-400">*</span>
                            </Label>
                            <div className="flex gap-2">
                              <Input
                                placeholder="Enter AHA eCard Code"
                                value={student.ahaEcardCode || ''}
                                onChange={(e) => updateStudent(student.id, 'ahaEcardCode', e.target.value)}
                                className="h-9 text-sm bg-slate-800 border-amber-600/50 text-slate-100 placeholder:text-slate-500 flex-1"
                                data-testid={`student-${index}-ecard-code`}
                                disabled={student.ahaVerifying}
                              />
                              <Button
                                type="button"
                                onClick={() => verifyStudentAhaCard(student.id)}
                                disabled={!student.ahaEcardCode || !student.firstName || !student.lastName || student.ahaVerifying}
                                className="h-9 px-3 bg-amber-600 hover:bg-amber-500 text-white text-sm disabled:opacity-50"
                                data-testid={`student-${index}-verify-btn`}
                              >
                                {student.ahaVerifying ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  'Verify'
                                )}
                              </Button>
                            </div>
                            
                            {/* Verification Result Display */}
                            {student.ahaVerificationStatus && (
                              <div className={`mt-2 p-2 rounded text-sm flex items-start gap-2 ${
                                student.ahaVerificationStatus.verified 
                                  ? student.ahaVerificationStatus.is_exact_match 
                                    ? 'bg-green-900/40 border border-green-600/50 text-green-300'
                                    : 'bg-amber-900/40 border border-amber-600/50 text-amber-300'
                                  : student.ahaVerificationStatus.match_status === 'AHA ERROR' || student.ahaVerificationStatus.match_status === 'MANUAL VERIFICATION REQUIRED'
                                    ? 'bg-amber-900/40 border border-amber-600/50 text-amber-300'
                                    : student.ahaVerificationStatus.is_expired || student.ahaVerificationStatus.match_status === 'NOT QUALIFIED FOR RENEWAL'
                                      ? 'bg-red-900/50 border-2 border-red-500 text-red-200'
                                      : 'bg-red-900/40 border border-red-600/50 text-red-300'
                              }`} data-testid={`student-${index}-aha-result`}>
                                {student.ahaVerificationStatus.verified ? (
                                  student.ahaVerificationStatus.is_exact_match ? (
                                    <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-green-400" />
                                  ) : (
                                    <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-400" />
                                  )
                                ) : student.ahaVerificationStatus.match_status === 'AHA ERROR' || student.ahaVerificationStatus.match_status === 'MANUAL VERIFICATION REQUIRED' ? (
                                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-400" />
                                ) : (
                                  <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-red-400" />
                                )}
                                <div className="flex-1">
                                  <div className="font-medium flex items-center gap-2">
                                    {student.ahaVerificationStatus.verified ? (
                                      student.ahaVerificationStatus.is_exact_match ? (
                                        <span className="text-green-300">✓ Exact Match</span>
                                      ) : (
                                        <span className="text-amber-300">⚠ Needs Review (Name Mismatch)</span>
                                      )
                                    ) : (
                                      student.ahaVerificationStatus.match_status
                                    )}
                                  </div>
                                  {student.ahaVerificationStatus.card_holder_name && !student.ahaVerificationStatus.is_expired && (
                                    <div className="text-xs mt-1">
                                      <span className="opacity-70">AHA Card Name:</span> <span className="font-medium">{student.ahaVerificationStatus.card_holder_name}</span>
                                    </div>
                                  )}
                                  {student.ahaVerificationStatus.verified && !student.ahaVerificationStatus.is_exact_match && student.ahaVerificationStatus.entered_name && (
                                    <div className="text-xs mt-0.5">
                                      <span className="opacity-70">You Entered:</span> <span className="font-medium">{student.ahaVerificationStatus.entered_name}</span>
                                    </div>
                                  )}
                                  {student.ahaVerificationStatus.expiration_date && !student.ahaVerificationStatus.is_expired && (
                                    <div className="text-xs mt-0.5">
                                      <span className="opacity-70">Expires:</span> <span className="font-medium">{student.ahaVerificationStatus.expiration_date}</span>
                                    </div>
                                  )}
                                  {student.ahaVerificationStatus.details && !student.ahaVerificationStatus.verified && (
                                    <div className={`text-xs mt-1 ${student.ahaVerificationStatus.is_expired ? 'font-medium' : 'opacity-80'}`}>
                                      {student.ahaVerificationStatus.details}
                                    </div>
                                  )}
                                  
                                  {/* Manual verification checkbox for MANUAL VERIFICATION REQUIRED status */}
                                  {student.ahaVerificationStatus.match_status === 'MANUAL VERIFICATION REQUIRED' && (
                                    <div className="mt-2 flex items-start gap-2">
                                      <input
                                        type="checkbox"
                                        id={`manual-verify-${student.id}`}
                                        checked={student.manuallyVerified || false}
                                        onChange={(e) => {
                                          setStudents(prev => prev.map(s => 
                                            s.id === student.id 
                                              ? { 
                                                  ...s, 
                                                  manuallyVerified: e.target.checked,
                                                  ahaVerificationStatus: e.target.checked 
                                                    ? { ...s.ahaVerificationStatus, verified: true, match_status: 'MANUALLY VERIFIED' }
                                                    : { ...s.ahaVerificationStatus, verified: false, match_status: 'MANUAL VERIFICATION REQUIRED' }
                                                } 
                                              : s
                                          ));
                                        }}
                                        className="mt-0.5 h-4 w-4 rounded border-amber-500 bg-slate-700 text-amber-500 focus:ring-amber-500"
                                        data-testid={`student-${index}-manual-verify-checkbox`}
                                      />
                                      <label htmlFor={`manual-verify-${student.id}`} className="text-xs">
                                        I confirm I have verified this eCard at ecards.heart.org and it belongs to {student.firstName} {student.lastName} with a valid expiration date
                                      </label>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            <p className="text-xs text-amber-400/70 mt-2">
                              Find your eCard code at{' '}
                              <a href="https://ecards.heart.org/student/myecards" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-300">
                                ecards.heart.org
                              </a>
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addStudent}
                    className={`w-full h-10 border-dashed border-2 ${isAddOnMode ? 'border-purple-600 text-purple-400 hover:bg-purple-900/30' : 'border-teal-600 text-teal-400 hover:bg-teal-900/30'} bg-transparent`}
                    data-testid="add-student-button"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Additional Student
                  </Button>

                  {/* Class Address Section - only for new private bookings (not add-ons, not public classes) */}
                  {!isAddOnMode && !selectedPublicClass && (
                  <div className="mt-6 p-4 bg-slate-700 rounded-lg border border-teal-600">
                    <h4 className="font-semibold text-sm text-slate-200 mb-3 flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-teal-400" />
                      Class Location <span className="text-xs font-normal text-red-400 ml-1">* Required</span>
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs font-medium text-slate-400 mb-2 block">
                          Start typing your address and select from suggestions
                        </Label>
                        <AddressAutocomplete
                          onAddressSelect={handleAddressSelect}
                          initialValue={formattedAddress}
                          placeholder="Enter your address..."
                          className="h-10 text-sm bg-slate-800 border-teal-600 text-slate-100 placeholder:text-slate-500"
                          error={addressErrors.address}
                          testId="address-autocomplete"
                        />
                      </div>
                      
                      {/* Show selected address details */}
                      {isAddressValidated && classAddress.street && (
                        <div className="space-y-3">
                          {/* Location Name - Prominent, Above Address */}
                          <div className="p-3 bg-amber-900/40 rounded-lg border-2 border-amber-500" data-testid="location-name-section">
                            <Label className="text-sm font-semibold text-amber-300 mb-2 block flex items-center gap-2">
                              <MapPin className="h-4 w-4" />
                              Location Name
                            </Label>
                            <Input
                              type="text"
                              placeholder="Enter location name"
                              value={locationName}
                              onChange={(e) => setLocationName(e.target.value)}
                              className="h-10 text-base bg-slate-800 border-amber-600 text-amber-100 placeholder:text-slate-500 font-medium"
                              data-testid="location-name-input"
                            />
                            <p className="text-xs text-amber-400/70 mt-1">
                              This name will appear on the calendar and in emails
                            </p>
                          </div>
                          
                          {/* Address Verified Section */}
                          <div className="p-3 bg-slate-800/50 rounded-lg border border-teal-600" data-testid="address-details">
                            <div className="flex items-center gap-2 text-sm text-teal-400 mb-2">
                              <CheckCircle2 className="h-4 w-4" />
                              <span className="font-medium">Address Verified</span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div>
                                <span className="text-slate-400">Street:</span>
                                <span className="text-slate-200 ml-2">{classAddress.street}</span>
                              </div>
                              <div>
                                <span className="text-slate-400">City:</span>
                                <span className="text-slate-200 ml-2">{classAddress.city}</span>
                              </div>
                              <div>
                                <span className="text-slate-400">State:</span>
                                <span className="text-slate-200 ml-2">{classAddress.state}</span>
                              </div>
                              <div>
                                <span className="text-slate-400">ZIP:</span>
                                <span className="text-slate-200 ml-2">{classAddress.zip}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Travel Fee Warning */}
                      {showTravelFeeWarning && (
                        <div className="mt-3 p-3 bg-amber-900/40 border border-amber-600 rounded-lg" data-testid="travel-fee-warning">
                          <p className="text-sm text-amber-300 flex items-start gap-2">
                            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                            <span>
                              <strong>Travel Fee Notice:</strong> Classes outside the Reno/Sparks area are subject to additional travel fees. Please contact us for more information.
                            </span>
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  )}
                  {/* End address conditional */}

                  {/* BLS Renewal Info - Only show for renewal classes */}
                  {isRenewalClass && (
                    <div className="mb-4 p-3 bg-amber-900/20 rounded-lg border border-amber-600/50" data-testid="bls-renewal-notice">
                      <p className="text-sm text-amber-200 flex items-center gap-2">
                        <Shield className="h-4 w-4 text-amber-400 shrink-0" />
                        <span>
                          <strong>BLS Renewal Class:</strong> Each student must provide their AHA eCard code for verification.
                          Find your code at{' '}
                          <a href="https://ecards.heart.org/student/myecards" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 underline">
                            ecards.heart.org
                          </a>
                        </span>
                      </p>
                    </div>
                  )}

                  {/* Reference Code Section - For easier class lookup when adding students later */}
                  {!isAddOnMode && !selectedPublicClass && (
                    <div className="mt-6 p-4 bg-slate-700 rounded-lg border border-teal-600">
                      <h4 className="font-semibold text-sm text-slate-200 mb-2 flex items-center gap-2">
                        <Tag className="h-4 w-4 text-teal-400" />
                        Booking Code
                        <span className="text-xs font-normal text-slate-400">(Optional)</span>
                      </h4>
                      <p className="text-xs text-slate-400 mb-3">
                        Create an easy-to-remember code for this class. Use this code to quickly add more students later.
                      </p>
                      <Input
                        type="text"
                        placeholder="e.g., team2025 or leave blank for auto-generated code"
                        value={referenceCode}
                        onChange={(e) => setReferenceCode(e.target.value.toLowerCase().replace(/\s/g, ''))}
                        className="bg-slate-800 border-teal-600 text-white lowercase"
                        maxLength={20}
                        data-testid="reference-code-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Letters and numbers only, no spaces. A 4-digit code will be auto-generated if left blank.
                      </p>
                    </div>
                  )}

                  {/* Notes for Instructor Section */}
                  <div className="mt-6 p-4 bg-slate-700 rounded-lg border border-teal-600">
                    <h4 className="font-semibold text-sm text-slate-200 mb-3 flex items-center gap-2">
                      <Edit2 className="h-4 w-4 text-amber-400" />
                      Notes for Instructor
                    </h4>
                    <textarea
                      placeholder="Notes for instructor (classroom info, entry info, student restrictions, etc.)"
                      value={instructorNotes}
                      onChange={(e) => setInstructorNotes(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 text-sm bg-slate-800 border border-teal-600 rounded-lg text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                      data-testid="instructor-notes"
                    />
                  </div>

                  {/* Add-Ons Section - Product add-ons from WooCommerce */}
                  {availableAddons.length > 0 && (
                    <div className="mt-6 p-4 bg-slate-800 border border-teal-600 rounded-lg" data-testid="addons-section">
                      <h4 className="font-semibold text-slate-200 mb-3 flex items-center gap-2">
                        <Plus className={`h-4 w-4 ${isAddOnMode ? 'text-purple-400' : 'text-teal-400'}`} />
                        Add-Ons {isAddOnMode && <span className="text-xs font-normal text-slate-400">(Optional)</span>}
                      </h4>
                      <p className="text-sm text-slate-400 mb-4">Optional items to enhance your class experience</p>
                      
                      <div className="space-y-3">
                        {availableAddons.map((addon) => {
                          const isSelected = selectedAddons[addon.id]?.selected || false;
                          const quantity = selectedAddons[addon.id]?.quantity || 1;
                          
                          return (
                            <div 
                              key={addon.id}
                              className={`p-3 rounded-lg border transition-all ${
                                isSelected 
                                  ? 'bg-teal-900/30 border-teal-600' 
                                  : 'bg-slate-700/50 border-teal-600 hover:border-slate-500'
                              }`}
                              data-testid={`addon-item-${addon.id}`}
                            >
                              <div className="flex items-start gap-3">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleAddon(addon.id)}
                                  className="mt-1 h-4 w-4 rounded border-slate-500 bg-slate-700 text-teal-500 focus:ring-teal-500 focus:ring-offset-slate-800"
                                  data-testid={`addon-checkbox-${addon.id}`}
                                />
                                
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between gap-2">
                                    <h5 className={`font-medium ${isSelected ? 'text-teal-200' : 'text-slate-200'}`}>
                                      {addon.name}
                                    </h5>
                                    <span className={`font-semibold ${isSelected ? 'text-teal-300' : 'text-white'}`}>
                                      ${parseFloat(addon.price || 0).toFixed(2)}
                                    </span>
                                  </div>
                                  
                                  {addon.permalink && (
                                    <a 
                                      href={addon.permalink} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-xs text-teal-400 hover:text-teal-300 mt-1 inline-block hover:underline"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      Visit BLCPR webpage for full description →
                                    </a>
                                  )}
                                  
                                  {/* Quantity selector - only show when selected */}
                                  {isSelected && (
                                    <div className="flex items-center gap-3 mt-3">
                                      <span className="text-sm text-slate-400">Quantity:</span>
                                      <div className="flex items-center gap-1">
                                        <button
                                          type="button"
                                          onClick={() => updateAddonQuantity(addon.id, quantity - 1)}
                                          disabled={quantity <= 1}
                                          className="w-8 h-8 flex items-center justify-center rounded bg-slate-700 text-white hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                          data-testid={`addon-qty-minus-${addon.id}`}
                                        >
                                          -
                                        </button>
                                        <input
                                          type="number"
                                          min="1"
                                          value={quantity}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            if (val === '') {
                                              updateAddonQuantity(addon.id, '');
                                            } else {
                                              updateAddonQuantity(addon.id, parseInt(val) || 1);
                                            }
                                          }}
                                          onBlur={(e) => {
                                            const val = parseInt(e.target.value);
                                            if (isNaN(val) || val < 1) updateAddonQuantity(addon.id, 1);
                                          }}
                                          className="w-14 h-8 text-center bg-slate-700 border border-teal-600 rounded text-slate-200 text-sm"
                                          data-testid={`addon-qty-input-${addon.id}`}
                                        />
                                        <button
                                          type="button"
                                          onClick={() => updateAddonQuantity(addon.id, quantity + 1)}
                                          className="w-8 h-8 flex items-center justify-center rounded bg-slate-700 text-white hover:bg-slate-600"
                                          data-testid={`addon-qty-plus-${addon.id}`}
                                        >
                                          +
                                        </button>
                                      </div>
                                      <span className="text-sm text-teal-400 font-medium">
                                        = ${(parseFloat(addon.price || 0) * quantity).toFixed(2)}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Add-ons subtotal */}
                      {calculateAddonsTotal() > 0 && (
                        <div className="mt-4 pt-3 border-t border-teal-600 flex justify-between items-center">
                          <span className="text-sm text-slate-400">Add-Ons Subtotal:</span>
                          <span className="text-teal-300 font-semibold">${calculateAddonsTotal().toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* 48-Hour Rush Fee Warning - only for new bookings */}
                  {!isAddOnMode && pricingPreview?.rush_fee && selectedSlot && (
                    <div className="mt-4 p-4 bg-red-900/40 border-2 border-red-500 rounded-lg" data-testid="rush-fee-warning">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-semibold text-red-300 mb-1">Rush Booking Fee Applies</h4>
                          <p className="text-sm text-red-200">
                            This class is within 48 hours of booking. A rush fee of ${pricingPreview.rush_fee.amount.toFixed(2)} has been added.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* After-Hours Fee Warning - only for new bookings */}
                  {!isAddOnMode && pricingPreview?.afterhours_fee && selectedSlot && (
                    <div className="mt-4 p-4 bg-amber-900/40 border-2 border-amber-500 rounded-lg" data-testid="afterhours-fee-warning">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-semibold text-amber-300 mb-1">After-Hours Fee Applies</h4>
                          <p className="text-sm text-amber-200">
                            This class is scheduled outside regular hours (before 7am or after 7pm). An after-hours fee of ${pricingPreview.afterhours_fee.amount.toFixed(2)} has been added.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Add-on Booking Summary - only show if registration is NOT closed */}
                  {isAddOnMode && foundBooking && addOnConfirmed && !foundBooking.registration_closed && (
                    <div className="mt-6 p-4 bg-purple-900/30 rounded-lg border border-purple-700" data-testid="addon-summary">
                      <h4 className="font-semibold text-sm text-purple-300 mb-2">Add-On Summary</h4>
                      <div className="space-y-1 text-sm text-purple-200">
                        <p><strong>Class:</strong> {foundBooking.class_type_name}</p>
                        <p><strong>Existing Students:</strong> {foundBooking.student_count}</p>
                        <p><strong>Adding:</strong> {students.length} new student{students.length !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                  )}

                  {/* Pricing Summary for Private Bookings - Shows after selecting class/time */}
                  {!isAddOnMode && !selectedPublicClass && selectedSlot && (
                    <div className="mt-4 p-4 bg-gradient-to-r from-teal-900/40 to-slate-800 rounded-lg border border-teal-700" data-testid="pricing-summary-sidebar">
                      <div className="flex items-center gap-2 mb-3">
                        <DollarSign className="h-5 w-5 text-teal-400" />
                        <h4 className="font-semibold text-teal-300">Price Summary</h4>
                      </div>
                      
                      {pricingLoading ? (
                        <div className="flex items-center gap-2 text-slate-400 text-sm">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Calculating price...
                        </div>
                      ) : pricingPreview ? (
                        <div className="space-y-2 text-sm">
                          {/* Course Cost */}
                          <div className="flex justify-between">
                            <span className="text-white">
                              {pricingPreview.course.student_count} student{pricingPreview.course.student_count !== 1 ? 's' : ''} × ${pricingPreview.course.price_per_student.toFixed(2)}
                            </span>
                            <span className="text-teal-200 font-medium">${pricingPreview.course.subtotal.toFixed(2)}</span>
                          </div>
                          
                          {/* Rush Fee */}
                          {pricingPreview.rush_fee && (
                            <div className="flex justify-between">
                              <span className="text-red-300">Rush Fee</span>
                              <span className="text-red-300 font-medium">${pricingPreview.rush_fee.amount.toFixed(2)}</span>
                            </div>
                          )}
                          
                          {/* After-Hours Fee */}
                          {pricingPreview.afterhours_fee && (
                            <div className="flex justify-between">
                              <span className="text-amber-300">After-Hours Fee</span>
                              <span className="text-amber-300 font-medium">${pricingPreview.afterhours_fee.amount.toFixed(2)}</span>
                            </div>
                          )}
                          
                          {/* Travel Fee */}
                          {pricingPreview.mileage && (
                            <div className="flex justify-between">
                              <span className="text-white">Travel ({pricingPreview.mileage.miles} mi)</span>
                              <span className="text-teal-200 font-medium">${pricingPreview.mileage.subtotal.toFixed(2)}</span>
                            </div>
                          )}
                          
                          {/* Add-Ons */}
                          {pricingPreview.addons && pricingPreview.addons.length > 0 && (
                            <>
                              {pricingPreview.addons.map((addon, idx) => (
                                <div key={idx} className="flex justify-between">
                                  <span className="text-purple-300">{addon.name} × {addon.quantity}</span>
                                  <span className="text-purple-300 font-medium">${(addon.subtotal || addon.total || 0).toFixed(2)}</span>
                                </div>
                              ))}
                            </>
                          )}
                          
                          {/* Coupon Discount */}
                          {couponApplied && (
                            <div className="flex justify-between">
                              <span className="text-green-400 flex items-center gap-1">
                                <Tag className="h-3 w-3" />
                                {couponApplied.code.toUpperCase()}
                              </span>
                              <span className="text-green-400 font-medium">-${couponApplied.discount_amount.toFixed(2)}</span>
                            </div>
                          )}
                          
                          {/* Total */}
                          <div className="flex justify-between pt-2 border-t border-teal-700/50 mt-2">
                            <span className="text-teal-100 font-semibold">Total</span>
                            <span className="text-teal-300 font-bold text-lg">${pricingPreview.grand_total.toFixed(2)}</span>
                          </div>
                        </div>
                      ) : (
                        <p className="text-slate-400 text-sm">Enter student info to see pricing</p>
                      )}
                      
                      {/* Coupon Input */}
                      <div className="pt-3 border-t border-teal-700/50 mt-3">
                        <label className="text-xs text-slate-400 mb-2 block">Have a promo code?</label>
                        {couponApplied ? (
                          <div className="flex items-center justify-between bg-green-900/30 rounded-lg px-3 py-2 border border-green-700/50">
                            <div className="flex items-center gap-2">
                              <Tag className="h-4 w-4 text-green-400" />
                              <span className="text-green-300 text-sm font-medium">{couponApplied.code.toUpperCase()}</span>
                            </div>
                            <Button
                              onClick={removeCoupon}
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-green-400 hover:text-red-400 hover:bg-transparent"
                              data-testid="remove-coupon-sidebar-btn"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="flex gap-2">
                              <Input
                                placeholder="Enter code"
                                value={couponCode}
                                onChange={(e) => {
                                  setCouponCode(e.target.value);
                                  setCouponError('');
                                }}
                                className="flex-1 h-9 text-sm bg-slate-800/50 border-teal-600 text-slate-100 placeholder:text-slate-500"
                                data-testid="coupon-sidebar-input"
                                onKeyDown={(e) => e.key === 'Enter' && applyCoupon()}
                              />
                              <Button
                                onClick={applyCoupon}
                                disabled={couponLoading || !couponCode.trim() || !selectedSlot}
                                size="sm"
                                className="h-9 bg-teal-600 hover:bg-teal-500 text-white px-3"
                                data-testid="apply-coupon-sidebar-btn"
                              >
                                {couponLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
                              </Button>
                            </div>
                            {couponError && (
                              <p className="text-red-400 text-xs flex items-center gap-1">
                                <AlertCircle className="h-3 w-3" />
                                {couponError}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Navigation Buttons for New Private Bookings (not add-ons, not public classes) */}
                  {!isAddOnMode && !selectedPublicClass && (
                    <div className="mt-4">
                      <Button
                        onClick={() => handleBooking()}
                        disabled={loading || !selectedSlot || students.length === 0}
                        className="w-full h-12 rounded-lg bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-500 hover:to-teal-400 text-slate-900 font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="pay-now-button"
                      >
                        <CreditCard className="w-5 h-5 mr-2" />
                        {loading 
                          ? 'Processing...' 
                          : editingBookingId 
                            ? `Update & Pay (${students.length} student${students.length !== 1 ? 's' : ''})`
                            : `Proceed to Payment (${students.length} student${students.length !== 1 ? 's' : ''})`
                        }
                      </Button>
                    </div>
                  )}
                  
                  {/* Payment Button for Add-on mode */}
                  {isAddOnMode && (
                    <div className="mt-4 space-y-3">
                      <Button
                        onClick={handleAddStudentToBooking}
                        disabled={loading || !foundBooking || !addOnConfirmed || students.length === 0}
                        className="w-full h-12 rounded-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="add-student-pay-button"
                      >
                        {loading ? 'Processing...' : `Add ${students.length} Student${students.length !== 1 ? 's' : ''} & Pay Now`}
                      </Button>
                    </div>
                  )}
                  
                  {/* Payment Button for Public Class mode */}
                  {selectedPublicClass && selectedPublicClass !== 'browse' && publicClassConfirmed && (
                    <div className="mt-4 space-y-3">
                      <Button
                        onClick={handlePublicClassCheckout}
                        disabled={loading || !publicClassPricing || students.length === 0}
                        className="w-full h-12 rounded-full bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-white font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="public-class-pay-button"
                      >
                        <CreditCard className="w-5 h-5 mr-2" />
                        {loading ? 'Processing...' : `Proceed to Payment (${students.length} student${students.length !== 1 ? 's' : ''})`}
                      </Button>
                    </div>
                  )}
                  
                  {!isAddOnMode && !selectedPublicClass && <p className="text-xs text-slate-500 text-center mt-2">Your booking requires instructor approval</p>}
                </div>
              </Card>
            </div>
            )}
            {/* End Step 2 wrapper */}
            
            {/* Right Column for Add-On Mode - Student Info & Checkout */}
            {isAddOnMode && foundBooking && (
            <div className="lg:col-span-4">
              <Card className="bg-purple-900/30 rounded-xl border border-purple-700 shadow-sm p-6 sticky top-4" data-testid="addon-booking-form-card">
                {/* Found booking summary */}
                <div className="mb-4 p-3 bg-purple-800/30 rounded-lg border border-purple-600">
                  <p className="text-sm text-purple-300 font-medium">{foundBooking.class_type_name}</p>
                  <p className="text-xs text-slate-400">{foundBooking.date}</p>
                  <p className="text-xs text-slate-400">Current students: {foundBooking.student_count}</p>
                </div>
                
                {/* Registration Closed Warning - Show instead of form */}
                {foundBooking.registration_closed ? (
                  <div className="p-4 bg-red-900/50 rounded-lg border border-red-600">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-6 w-6 text-red-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h5 className="font-semibold text-red-300 mb-2">Registration Closed</h5>
                        <p className="text-red-200 text-sm mb-3">
                          {foundBooking.registration_closed_message || "Sorry, registration for this class is closed and students can no longer be added."}
                        </p>
                        <p className="text-red-300 text-xs">
                          Please contact Biggest Little CPR at <a href="mailto:classes@biggestlittlecpr.com" className="text-red-200 underline">classes@biggestlittlecpr.com</a> or call <a href="tel:775-722-8056" className="text-red-200 underline">775-722-8056</a> to add students to this class.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <h3 className="text-xl font-semibold mb-4 font-heading flex items-center gap-2 text-purple-200">
                      <Users className="h-5 w-5 text-purple-400" />
                      Add New Student(s)
                      <span className="text-xs font-normal text-red-400 ml-1">* Required</span>
                    </h3>
                
                <div className="space-y-4">
                  {students.map((student, index) => (
                    <div key={student.id} className="p-4 bg-slate-800 rounded-lg border border-purple-600">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-semibold text-purple-300">New Student {index + 1}</span>
                        {students.length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeStudent(student.id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-900/30 h-8 w-8 p-0"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <Label className="text-xs text-slate-400">First Name *</Label>
                          <Input
                            value={student.firstName}
                            onChange={(e) => updateStudent(student.id, 'firstName', e.target.value)}
                            placeholder="First name"
                            className="bg-slate-700 border-teal-600 text-white h-9 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-400">Last Name *</Label>
                          <Input
                            value={student.lastName}
                            onChange={(e) => updateStudent(student.id, 'lastName', e.target.value)}
                            placeholder="Last name"
                            className="bg-slate-700 border-teal-600 text-white h-9 text-sm"
                          />
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <Label className="text-xs text-slate-400">Email *</Label>
                          <Input
                            type="email"
                            value={student.email}
                            onChange={(e) => updateStudent(student.id, 'email', e.target.value)}
                            placeholder="email@example.com"
                            className="bg-slate-700 border-teal-600 text-white h-9 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-400">Phone *</Label>
                          <Input
                            type="tel"
                            value={student.phone}
                            onChange={(e) => updateStudent(student.id, 'phone', formatPhoneNumber(e.target.value))}
                            placeholder="(555) 123-4567"
                            className="bg-slate-700 border-teal-600 text-white h-9 text-sm"
                          />
                        </div>
                        
                        {/* Per-Student AHA eCard Verification - Only for Renewal Classes in Add-On Mode */}
                        {isRenewalClass && (
                          <div className="mt-3 p-3 bg-amber-900/30 rounded-lg border border-amber-600/50" data-testid={`addon-student-${index}-aha-section`}>
                            <Label className="text-xs font-medium text-amber-300 mb-2 block flex items-center gap-2">
                              <Shield className="h-3.5 w-3.5" />
                              AHA eCard Verification <span className="text-red-400">*</span>
                            </Label>
                            <div className="flex gap-2">
                              <Input
                                placeholder="Enter AHA eCard Code"
                                value={student.ahaEcardCode || ''}
                                onChange={(e) => updateStudent(student.id, 'ahaEcardCode', e.target.value)}
                                className="h-9 text-sm bg-slate-800 border-amber-600/50 text-slate-100 placeholder:text-slate-500 flex-1"
                                data-testid={`addon-student-${index}-ecard-code`}
                                disabled={student.ahaVerifying}
                              />
                              <Button
                                type="button"
                                onClick={() => verifyStudentAhaCard(student.id)}
                                disabled={!student.ahaEcardCode || !student.firstName || !student.lastName || student.ahaVerifying}
                                className="h-9 px-3 bg-amber-600 hover:bg-amber-500 text-white text-sm disabled:opacity-50"
                                data-testid={`addon-student-${index}-verify-btn`}
                              >
                                {student.ahaVerifying ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  'Verify'
                                )}
                              </Button>
                            </div>
                            
                            {/* Verification Result Display */}
                            {student.ahaVerificationStatus && (
                              <div className={`mt-2 p-2 rounded text-sm flex items-start gap-2 ${
                                student.ahaVerificationStatus.verified 
                                  ? 'bg-green-900/40 border border-green-600/50 text-green-300'
                                  : student.ahaVerificationStatus.match_status === 'AHA ERROR' || student.ahaVerificationStatus.match_status === 'MANUAL VERIFICATION REQUIRED'
                                    ? 'bg-amber-900/40 border border-amber-600/50 text-amber-300'
                                    : student.ahaVerificationStatus.is_expired || student.ahaVerificationStatus.match_status === 'NOT QUALIFIED FOR RENEWAL'
                                      ? 'bg-red-900/50 border-2 border-red-500 text-red-200'
                                      : 'bg-red-900/40 border border-red-600/50 text-red-300'
                              }`} data-testid={`addon-student-${index}-aha-result`}>
                                {student.ahaVerificationStatus.verified ? (
                                  <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-green-400" />
                                ) : student.ahaVerificationStatus.match_status === 'AHA ERROR' || student.ahaVerificationStatus.match_status === 'MANUAL VERIFICATION REQUIRED' ? (
                                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-400" />
                                ) : (
                                  <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-red-400" />
                                )}
                                <div className="flex-1">
                                  <div className="font-medium">{student.ahaVerificationStatus.match_status}</div>
                                  {student.ahaVerificationStatus.card_holder_name && !student.ahaVerificationStatus.is_expired && (
                                    <div className="text-xs mt-1">
                                      <span className="opacity-70">Card Issued To:</span> <span className="font-medium">{student.ahaVerificationStatus.card_holder_name}</span>
                                    </div>
                                  )}
                                  {student.ahaVerificationStatus.expiration_date && !student.ahaVerificationStatus.is_expired && (
                                    <div className="text-xs mt-0.5">
                                      <span className="opacity-70">Expires:</span> <span className="font-medium">{student.ahaVerificationStatus.expiration_date}</span>
                                    </div>
                                  )}
                                  {student.ahaVerificationStatus.details && !student.ahaVerificationStatus.verified && (
                                    <div className={`text-xs mt-1 ${student.ahaVerificationStatus.is_expired ? 'font-medium' : 'opacity-80'}`}>
                                      {student.ahaVerificationStatus.details}
                                    </div>
                                  )}
                                  
                                  {/* Manual verification checkbox for MANUAL VERIFICATION REQUIRED status */}
                                  {student.ahaVerificationStatus.match_status === 'MANUAL VERIFICATION REQUIRED' && (
                                    <div className="mt-2 flex items-start gap-2">
                                      <input
                                        type="checkbox"
                                        id={`addon-manual-verify-${student.id}`}
                                        checked={student.manuallyVerified || false}
                                        onChange={(e) => {
                                          setStudents(prev => prev.map(s => 
                                            s.id === student.id 
                                              ? { 
                                                  ...s, 
                                                  manuallyVerified: e.target.checked,
                                                  ahaVerificationStatus: e.target.checked 
                                                    ? { ...s.ahaVerificationStatus, verified: true, match_status: 'MANUALLY VERIFIED' }
                                                    : { ...s.ahaVerificationStatus, verified: false, match_status: 'MANUAL VERIFICATION REQUIRED' }
                                                } 
                                              : s
                                          ));
                                        }}
                                        className="mt-0.5 h-4 w-4 rounded border-amber-500 bg-slate-700 text-amber-500 focus:ring-amber-500"
                                        data-testid={`addon-student-${index}-manual-verify-checkbox`}
                                      />
                                      <label htmlFor={`addon-manual-verify-${student.id}`} className="text-xs">
                                        I confirm I have verified this eCard at ecards.heart.org and it belongs to {student.firstName} {student.lastName} with a valid expiration date
                                      </label>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            <p className="text-xs text-amber-400/70 mt-2">
                              Find your eCard code at{' '}
                              <a href="https://ecards.heart.org/student/myecards" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-300">
                                ecards.heart.org
                              </a>
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  <Button
                    onClick={addStudent}
                    variant="outline"
                    className="w-full h-10 border-dashed border-2 border-purple-600 text-purple-400 hover:bg-purple-900/30 bg-transparent"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Another Student
                  </Button>
                </div>
                
                {/* Add-Ons Section for Add-On Mode */}
                {availableAddons.length > 0 && (
                  <div className="mt-4 p-4 bg-slate-800 border border-purple-600 rounded-lg" data-testid="addon-mode-addons-section">
                    <h4 className="font-semibold text-purple-200 mb-3 flex items-center gap-2">
                      <Plus className="h-4 w-4 text-purple-400" />
                      Add-Ons <span className="text-xs font-normal text-slate-400">(Optional)</span>
                    </h4>
                    <p className="text-sm text-slate-400 mb-4">Optional items to enhance your class experience</p>
                    
                    <div className="space-y-3">
                      {availableAddons.map((addon) => {
                        const isSelected = selectedAddons[addon.id]?.selected || false;
                        const quantity = selectedAddons[addon.id]?.quantity || 1;
                        
                        return (
                          <div 
                            key={addon.id}
                            className={`p-3 rounded-lg border transition-all ${
                              isSelected 
                                ? 'bg-purple-900/30 border-purple-600' 
                                : 'bg-slate-700/50 border-teal-600 hover:border-slate-500'
                            }`}
                            data-testid={`addon-mode-addon-item-${addon.id}`}
                          >
                            <div className="flex items-start gap-3">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleAddon(addon.id)}
                                className="mt-1 h-4 w-4 rounded border-slate-500 bg-slate-700 text-purple-500 focus:ring-purple-500 focus:ring-offset-slate-800"
                                data-testid={`addon-mode-addon-checkbox-${addon.id}`}
                              />
                              
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2">
                                  <h5 className={`font-medium ${isSelected ? 'text-purple-200' : 'text-slate-200'}`}>
                                    {addon.name}
                                  </h5>
                                  <span className={`font-semibold ${isSelected ? 'text-purple-300' : 'text-white'}`}>
                                    ${parseFloat(addon.price || 0).toFixed(2)}
                                  </span>
                                </div>
                                {addon.permalink && (
                                  <a 
                                    href={addon.permalink} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-xs text-purple-400 hover:text-purple-300 mt-1 inline-block hover:underline"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    Visit BLCPR webpage for full description →
                                  </a>
                                )}
                                
                                {/* Quantity selector when selected */}
                                {isSelected && (
                                  <div className="flex items-center gap-3 mt-2">
                                    <span className="text-xs text-slate-400">Quantity:</span>
                                    <div className="flex items-center gap-1">
                                      <button
                                        type="button"
                                        onClick={() => updateAddonQuantity(addon.id, Math.max(1, quantity - 1))}
                                        className="w-8 h-8 flex items-center justify-center rounded bg-slate-700 text-white hover:bg-slate-600"
                                        data-testid={`addon-mode-addon-qty-minus-${addon.id}`}
                                      >
                                        -
                                      </button>
                                      <input
                                        type="number"
                                        min="1"
                                        value={quantity}
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          if (val === '') {
                                            updateAddonQuantity(addon.id, '');
                                          } else {
                                            updateAddonQuantity(addon.id, parseInt(val) || 1);
                                          }
                                        }}
                                        onBlur={(e) => {
                                          const val = parseInt(e.target.value);
                                          if (isNaN(val) || val < 1) updateAddonQuantity(addon.id, 1);
                                        }}
                                        className="w-14 h-8 text-center bg-slate-700 border border-teal-600 rounded text-slate-200 text-sm"
                                        data-testid={`addon-mode-addon-qty-input-${addon.id}`}
                                      />
                                      <button
                                        type="button"
                                        onClick={() => updateAddonQuantity(addon.id, quantity + 1)}
                                        className="w-8 h-8 flex items-center justify-center rounded bg-slate-700 text-white hover:bg-slate-600"
                                        data-testid={`addon-mode-addon-qty-plus-${addon.id}`}
                                      >
                                        +
                                      </button>
                                    </div>
                                    <span className="text-sm text-purple-400 font-medium">
                                      = ${(parseFloat(addon.price || 0) * quantity).toFixed(2)}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Add-ons subtotal */}
                    {calculateAddonsTotal() > 0 && (
                      <div className="mt-4 pt-3 border-t border-teal-600 flex justify-between items-center">
                        <span className="text-sm text-slate-400">Add-Ons Subtotal:</span>
                        <span className="text-purple-300 font-semibold">${calculateAddonsTotal().toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Coupon Code */}
                <div className="mt-4 p-3 bg-slate-800/50 rounded-lg border border-purple-600">
                  <Label className="text-xs text-purple-300 mb-2 block">Discount Code</Label>
                  <div className="flex gap-2">
                    <Input
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="Enter code"
                      className="bg-slate-700 border-teal-600 text-white h-9 text-sm flex-1"
                      disabled={couponLoading || couponApplied}
                      data-testid="addon-coupon-input"
                    />
                    {couponApplied ? (
                      <Button
                        onClick={() => {
                          setCouponApplied(null);
                          setCouponCode('');
                          setCouponError('');
                        }}
                        variant="outline"
                        size="sm"
                        className="border-red-600 text-red-400 hover:bg-red-900/30 h-9"
                      >
                        Remove
                      </Button>
                    ) : (
                      <Button
                        onClick={applyCoupon}
                        disabled={couponLoading || !couponCode.trim()}
                        size="sm"
                        className="bg-teal-600 hover:bg-teal-500 text-white h-9"
                        data-testid="addon-apply-coupon"
                      >
                        {couponLoading ? 'Checking...' : 'Apply'}
                      </Button>
                    )}
                  </div>
                  {couponError && (
                    <p className="text-xs text-red-400 mt-1">{couponError}</p>
                  )}
                  {couponApplied && (
                    <p className="text-xs text-emerald-400 mt-1 flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" />
                      {couponApplied.code}: -${couponApplied.discount_amount?.toFixed(2)} off
                    </p>
                  )}
                </div>
                
                {/* Addon Pricing Summary */}
                {addonPricing && (
                  <div className="mt-4 p-4 bg-purple-800/30 rounded-lg border border-purple-600">
                    <div className="flex items-center gap-2 mb-3">
                      <DollarSign className="h-5 w-5 text-purple-400" />
                      <h4 className="font-semibold text-purple-300">Price Summary</h4>
                    </div>
                    
                    {/* Class Fee Waived Message */}
                    {addonPricing.class_fee_waived && (
                      <div className="p-3 bg-emerald-900/40 rounded-lg border border-emerald-600 mb-3">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-5 w-5 text-emerald-400" />
                          <div>
                            <p className="text-emerald-300 font-medium">Class Fee Prepaid</p>
                            <p className="text-xs text-emerald-400">{addonPricing.class_fee_waived_message}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="space-y-2 text-sm">
                      {/* Course price - show as waived if class_fee_waived */}
                      <div className="flex justify-between">
                        <span className="text-white">{students.length} student{students.length !== 1 ? 's' : ''} × ${addonPricing.course?.price_per_student?.toFixed(2) || '0.00'}</span>
                        <span className={`font-medium ${addonPricing.class_fee_waived ? 'text-emerald-400' : 'text-purple-200'}`}>
                          {addonPricing.class_fee_waived ? '$0.00 (Prepaid)' : `$${addonPricing.course?.subtotal?.toFixed(2) || '0.00'}`}
                        </span>
                      </div>
                      
                      {/* Rush fee - only show if not waived */}
                      {addonPricing.rush_fee && !addonPricing.class_fee_waived && (
                        <div className="flex justify-between text-red-300">
                          <span>Rush Fee ({addonPricing.rush_fee.student_count} × ${addonPricing.rush_fee.per_student?.toFixed(2)})</span>
                          <span className="font-medium">${addonPricing.rush_fee.amount?.toFixed(2)}</span>
                        </div>
                      )}
                      
                      {/* After-hours fee - only show if not waived */}
                      {addonPricing.afterhours_fee && !addonPricing.class_fee_waived && (
                        <div className="flex justify-between text-amber-300">
                          <span>After-Hours ({addonPricing.afterhours_fee.student_count} × ${addonPricing.afterhours_fee.per_student?.toFixed(2)})</span>
                          <span className="font-medium">${addonPricing.afterhours_fee.amount?.toFixed(2)}</span>
                        </div>
                      )}
                      
                      {/* Add-ons - always show, these are NOT waived */}
                      {addonPricing.addons_subtotal > 0 && (
                        <div className="flex justify-between">
                          <span className="text-white">Add-ons</span>
                          <span className="text-purple-200">${addonPricing.addons_subtotal.toFixed(2)}</span>
                        </div>
                      )}
                      
                      {/* Coupon discount */}
                      {couponApplied && couponApplied.discount_amount > 0 && (
                        <div className="flex justify-between text-emerald-400">
                          <span>Discount ({couponApplied.code})</span>
                          <span className="font-medium">-${couponApplied.discount_amount.toFixed(2)}</span>
                        </div>
                      )}
                      
                      <div className="border-t border-purple-600 pt-2 flex justify-between font-bold">
                        <span className="text-purple-200">Total</span>
                        <span className="text-purple-100">
                          ${couponApplied && couponApplied.discount_amount > 0 
                            ? Math.max(0, (addonPricing.grand_total || 0) - couponApplied.discount_amount).toFixed(2)
                            : (addonPricing.grand_total?.toFixed(2) || '0.00')}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Confirmation Checkbox */}
                <div className="mt-4 p-3 bg-slate-800/50 rounded-lg border border-purple-600">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={addOnConfirmed}
                      onChange={(e) => setAddOnConfirmed(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-purple-600 text-purple-500 focus:ring-purple-500 bg-slate-700"
                      data-testid="addon-confirm-checkbox-sidebar"
                    />
                    <span className="text-sm text-purple-200">
                      Yes, this is my booking and I want to add student(s)
                    </span>
                  </label>
                </div>
                
                {/* Add Student & Pay Button */}
                <div className="mt-4">
                  <Button
                    onClick={handleAddStudentToBooking}
                    disabled={loading || !addOnConfirmed || students.length === 0 || !students.every(s => s.firstName && s.lastName && s.email && s.phone)}
                    className={`w-full h-12 rounded-full font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                      addonPricing?.class_fee_waived && addonPricing?.grand_total === 0
                        ? 'bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400'
                        : 'bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400'
                    } text-white`}
                    data-testid="addon-pay-button-sidebar"
                  >
                    {addonPricing?.class_fee_waived && addonPricing?.grand_total === 0 ? (
                      <>
                        <CheckCircle className="w-5 h-5 mr-2" />
                        {loading ? 'Processing...' : `Add ${students.length} Student${students.length !== 1 ? 's' : ''} (Free)`}
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-5 h-5 mr-2" />
                        {loading ? 'Processing...' : `Add ${students.length} Student${students.length !== 1 ? 's' : ''} & Pay $${addonPricing?.grand_total?.toFixed(2) || '0.00'}`}
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-slate-500 text-center mt-2">
                    {addonPricing?.class_fee_waived 
                      ? (addonPricing?.grand_total > 0 ? 'Class fee prepaid - only add-ons require payment' : 'Class fee prepaid - no payment required')
                      : 'Payment required to complete add-on'}
                  </p>
                </div>
                  </>
                )}
              </Card>
            </div>
            )}
            
            {/* Right Column for Public Class Mode - Student Info & Checkout */}
            {selectedPublicClass && selectedPublicClass !== 'browse' && publicClassConfirmed && (
            <div className="lg:col-span-4">
              <Card className="bg-amber-900/30 rounded-xl border border-amber-700 shadow-sm p-6 sticky top-4" data-testid="public-class-booking-form-card">
                {/* Selected public class summary */}
                <div className="mb-4 p-3 bg-amber-800/30 rounded-lg border border-amber-600">
                  <p className="text-sm text-amber-300 font-medium">{selectedPublicClass.class_type_name}</p>
                  <p className="text-xs text-slate-400">{selectedPublicClass.date}</p>
                  <p className="text-xs text-slate-400">{selectedPublicClass.time_display}</p>
                  <p className="text-xs text-slate-400">{selectedPublicClass.location}</p>
                  {selectedPublicClass.spots_remaining !== undefined && (
                    <p className="text-xs text-amber-400 mt-1">
                      {selectedPublicClass.spots_remaining} spot{selectedPublicClass.spots_remaining !== 1 ? 's' : ''} remaining
                    </p>
                  )}
                </div>
                
                <h3 className="text-xl font-semibold mb-4 font-heading flex items-center gap-2 text-amber-200">
                  <Users className="h-5 w-5 text-amber-400" />
                  Student Information
                  <span className="text-xs font-normal text-red-400 ml-1">* Required</span>
                </h3>
                
                <div className="space-y-4">
                  {students.map((student, index) => (
                    <div key={student.id} className="p-4 bg-slate-800 rounded-lg border border-amber-600">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-semibold text-amber-300">Student {index + 1}</span>
                        {students.length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeStudent(student.id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-900/30 h-8 w-8 p-0"
                            data-testid={`public-remove-student-${index}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <Label className="text-xs text-slate-400">First Name *</Label>
                          <Input
                            value={student.firstName}
                            onChange={(e) => updateStudent(student.id, 'firstName', e.target.value)}
                            placeholder="First name"
                            className="bg-slate-700 border-teal-600 text-white h-9 text-sm"
                            data-testid={`public-student-${index}-firstname`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-400">Last Name *</Label>
                          <Input
                            value={student.lastName}
                            onChange={(e) => updateStudent(student.id, 'lastName', e.target.value)}
                            placeholder="Last name"
                            className="bg-slate-700 border-teal-600 text-white h-9 text-sm"
                            data-testid={`public-student-${index}-lastname`}
                          />
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <Label className="text-xs text-slate-400">Email *</Label>
                          <Input
                            type="email"
                            value={student.email}
                            onChange={(e) => updateStudent(student.id, 'email', e.target.value)}
                            placeholder="email@example.com"
                            className="bg-slate-700 border-teal-600 text-white h-9 text-sm"
                            data-testid={`public-student-${index}-email`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-400">Phone *</Label>
                          <Input
                            type="tel"
                            value={student.phone}
                            onChange={(e) => updateStudent(student.id, 'phone', formatPhoneNumber(e.target.value))}
                            placeholder="(555) 123-4567"
                            className="bg-slate-700 border-teal-600 text-white h-9 text-sm"
                            data-testid={`public-student-${index}-phone`}
                          />
                        </div>
                        
                        {/* Per-Student AHA eCard Verification - Only for Renewal Public Classes */}
                        {isRenewalClass && (
                          <div className="mt-3 p-3 bg-amber-900/30 rounded-lg border border-amber-600/50" data-testid={`public-student-${index}-aha-section`}>
                            <Label className="text-xs font-medium text-amber-300 mb-2 block flex items-center gap-2">
                              <Shield className="h-3.5 w-3.5" />
                              AHA eCard Verification <span className="text-red-400">*</span>
                            </Label>
                            <div className="flex gap-2">
                              <Input
                                placeholder="Enter AHA eCard Code"
                                value={student.ahaEcardCode || ''}
                                onChange={(e) => updateStudent(student.id, 'ahaEcardCode', e.target.value)}
                                className="h-9 text-sm bg-slate-800 border-amber-600/50 text-slate-100 placeholder:text-slate-500 flex-1"
                                data-testid={`public-student-${index}-ecard-code`}
                                disabled={student.ahaVerifying}
                              />
                              <Button
                                type="button"
                                onClick={() => verifyStudentAhaCard(student.id)}
                                disabled={!student.ahaEcardCode || !student.firstName || !student.lastName || student.ahaVerifying}
                                className="h-9 px-3 bg-amber-600 hover:bg-amber-500 text-white text-sm disabled:opacity-50"
                                data-testid={`public-student-${index}-verify-btn`}
                              >
                                {student.ahaVerifying ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  'Verify'
                                )}
                              </Button>
                            </div>
                            
                            {/* Verification Result Display */}
                            {student.ahaVerificationStatus && (
                              <div className={`mt-2 p-2 rounded text-sm flex items-start gap-2 ${
                                student.ahaVerificationStatus.verified 
                                  ? 'bg-green-900/40 border border-green-600/50 text-green-300'
                                  : student.ahaVerificationStatus.match_status === 'AHA ERROR' || student.ahaVerificationStatus.match_status === 'MANUAL VERIFICATION REQUIRED'
                                    ? 'bg-amber-900/40 border border-amber-600/50 text-amber-300'
                                    : student.ahaVerificationStatus.is_expired || student.ahaVerificationStatus.match_status === 'NOT QUALIFIED FOR RENEWAL'
                                      ? 'bg-red-900/50 border-2 border-red-500 text-red-200'
                                      : 'bg-red-900/40 border border-red-600/50 text-red-300'
                              }`} data-testid={`public-student-${index}-aha-result`}>
                                {student.ahaVerificationStatus.verified ? (
                                  <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-green-400" />
                                ) : student.ahaVerificationStatus.match_status === 'AHA ERROR' || student.ahaVerificationStatus.match_status === 'MANUAL VERIFICATION REQUIRED' ? (
                                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-400" />
                                ) : (
                                  <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-red-400" />
                                )}
                                <div className="flex-1">
                                  <div className="font-medium">{student.ahaVerificationStatus.match_status}</div>
                                  {student.ahaVerificationStatus.card_holder_name && !student.ahaVerificationStatus.is_expired && (
                                    <div className="text-xs mt-1">
                                      <span className="opacity-70">Card Issued To:</span> <span className="font-medium">{student.ahaVerificationStatus.card_holder_name}</span>
                                    </div>
                                  )}
                                  {student.ahaVerificationStatus.expiration_date && !student.ahaVerificationStatus.is_expired && (
                                    <div className="text-xs mt-0.5">
                                      <span className="opacity-70">Expires:</span> <span className="font-medium">{student.ahaVerificationStatus.expiration_date}</span>
                                    </div>
                                  )}
                                  {student.ahaVerificationStatus.details && !student.ahaVerificationStatus.verified && (
                                    <div className={`text-xs mt-1 ${student.ahaVerificationStatus.is_expired ? 'font-medium' : 'opacity-80'}`}>
                                      {student.ahaVerificationStatus.details}
                                    </div>
                                  )}
                                  
                                  {/* Manual verification checkbox */}
                                  {student.ahaVerificationStatus.match_status === 'MANUAL VERIFICATION REQUIRED' && (
                                    <div className="mt-2 flex items-start gap-2">
                                      <input
                                        type="checkbox"
                                        id={`public-manual-verify-${student.id}`}
                                        checked={student.manuallyVerified || false}
                                        onChange={(e) => {
                                          setStudents(prev => prev.map(s => 
                                            s.id === student.id 
                                              ? { 
                                                  ...s, 
                                                  manuallyVerified: e.target.checked,
                                                  ahaVerificationStatus: e.target.checked 
                                                    ? { ...s.ahaVerificationStatus, verified: true, match_status: 'MANUALLY VERIFIED' }
                                                    : { ...s.ahaVerificationStatus, verified: false, match_status: 'MANUAL VERIFICATION REQUIRED' }
                                                } 
                                              : s
                                          ));
                                        }}
                                        className="mt-0.5 h-4 w-4 rounded border-amber-500 bg-slate-700 text-amber-500 focus:ring-amber-500"
                                        data-testid={`public-student-${index}-manual-verify-checkbox`}
                                      />
                                      <label htmlFor={`public-manual-verify-${student.id}`} className="text-xs">
                                        I confirm I have verified this eCard at ecards.heart.org
                                      </label>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            <p className="text-xs text-amber-400/70 mt-2">
                              Find your eCard code at{' '}
                              <a href="https://ecards.heart.org/student/myecards" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-300">
                                ecards.heart.org
                              </a>
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  <Button
                    onClick={addStudent}
                    variant="outline"
                    className="w-full h-10 border-dashed border-2 border-amber-600 text-amber-400 hover:bg-amber-900/30 bg-transparent"
                    data-testid="public-add-another-student"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Another Student
                  </Button>
                </div>
                
                {/* Pricing Summary */}
                {publicClassPricing && (
                  <div className="mt-4 p-4 bg-slate-800/80 rounded-lg border border-amber-600">
                    <h4 className="font-semibold text-sm text-amber-300 mb-2">Price Summary</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-white">{publicClassPricing.course.student_count} student{publicClassPricing.course.student_count !== 1 ? 's' : ''} × ${publicClassPricing.course.price_per_student.toFixed(2)}</span>
                        <span className="text-amber-200">${publicClassPricing.course.subtotal.toFixed(2)}</span>
                      </div>
                      {publicClassPricing.rush_fee && (
                        <div className="flex justify-between text-red-300">
                          <span>Rush Fee</span>
                          <span>${publicClassPricing.rush_fee.amount.toFixed(2)}</span>
                        </div>
                      )}
                      {publicClassPricing.afterhours_fee && (
                        <div className="flex justify-between text-amber-300">
                          <span>After-Hours Fee</span>
                          <span>${publicClassPricing.afterhours_fee.amount.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="border-t border-amber-600 pt-2 flex justify-between font-bold">
                        <span className="text-amber-200">Total</span>
                        <span className="text-amber-100">${publicClassPricing.grand_total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Pay Button */}
                <div className="mt-4">
                  <Button
                    onClick={handlePublicClassCheckout}
                    disabled={loading || !publicClassPricing || students.length === 0 || !students.every(s => s.firstName && s.lastName && s.email && s.phone)}
                    className="w-full h-12 rounded-full bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-white font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="public-class-pay-button-sidebar"
                  >
                    <CreditCard className="w-5 h-5 mr-2" />
                    {loading ? 'Processing...' : `Enroll ${students.length} Student${students.length !== 1 ? 's' : ''} & Pay Now`}
                  </Button>
                  <p className="text-xs text-slate-500 text-center mt-2">No instructor approval required for public classes</p>
                </div>
              </Card>
            </div>
            )}
          </div>
        )}

        {/* Stripe Checkout Modal */}
        {showStripeCheckout && (
          <div 
            className="fixed inset-0 bg-black/80 flex items-start justify-center z-50 p-4 overflow-y-auto" 
            data-testid="stripe-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-2xl max-w-md w-full p-6 my-8" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-teal-400" />
                  Complete Payment
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowStripeCheckout(false);
                    setPaymentTermsAccepted(false);
                  }}
                  disabled={paymentProcessing}
                  className="text-white hover:text-white hover:bg-slate-700"
                >
                  ✕
                </Button>
              </div>
              
              {/* Sandbox Mode Disclaimer - Shows ABOVE the payment form */}
              {paymentConfig?.is_sandbox && (
                <div className="bg-amber-500/20 border border-amber-500/50 rounded-lg p-4 mb-4" data-testid="sandbox-disclaimer">
                  <div className="flex items-center gap-2 text-amber-400">
                    <AlertCircle className="h-5 w-5" />
                    <p className="font-semibold text-white">Bookings Currently Unavailable</p>
                  </div>
                  <p className="text-white text-sm mt-1">
                    We are working on our booking system, please check back soon to book your course. <strong>Courses during this time will NOT be booked/approved.</strong>
                  </p>
                </div>
              )}
              
              {/* Booking Summary with Full Pricing Breakdown */}
              <div className={`rounded-lg p-4 mb-4 border ${checkoutData?.booking_type === 'public_class' ? 'bg-gradient-to-r from-amber-900/50 to-slate-800 border-amber-700' : 'bg-gradient-to-r from-teal-900/50 to-slate-800 border-teal-700'}`}>
                <div className="flex items-center gap-2 mb-3">
                  <DollarSign className={`h-5 w-5 ${checkoutData?.booking_type === 'public_class' ? 'text-amber-400' : 'text-teal-400'}`} />
                  <h4 className={`font-semibold ${checkoutData?.booking_type === 'public_class' ? 'text-amber-300' : 'text-teal-300'}`}>Order Summary</h4>
                </div>
                
                <div className="text-white text-sm mb-3 pb-3 border-b border-teal-700/50">
                  <p><strong>Class:</strong> {selectedClass?.name || foundBooking?.class_type_name || selectedPublicClass?.class_type_name}</p>
                  <p><strong>Students:</strong> {checkoutData?.student_count || students.length}</p>
                  {checkoutData?.booking_type === 'public_class' && (
                    <p className="text-amber-300 text-xs mt-1">Public Class Enrollment</p>
                  )}
                </div>
                
                {/* Regular Booking Pricing Breakdown */}
                {pricingPreview && !isAddOnMode && (
                  <div className="space-y-2">
                    {/* Course Cost */}
                    <div className="flex justify-between text-sm">
                      <span className="text-white">
                        {pricingPreview.course.student_count} student{pricingPreview.course.student_count !== 1 ? 's' : ''} × ${pricingPreview.course.price_per_student.toFixed(2)}
                      </span>
                      <span className="text-teal-200 font-medium">${pricingPreview.course.subtotal.toFixed(2)}</span>
                    </div>
                    
                    {/* Rush Fee */}
                    {pricingPreview.rush_fee && (
                      <div className="flex justify-between text-sm">
                        <span className="text-red-300">
                          Rush Fee: {pricingPreview.rush_fee.student_count} × ${pricingPreview.rush_fee.per_student.toFixed(2)}
                        </span>
                        <span className="text-red-300 font-medium">${pricingPreview.rush_fee.amount.toFixed(2)}</span>
                      </div>
                    )}
                    
                    {/* After-Hours Fee */}
                    {pricingPreview.afterhours_fee && (
                      <div className="flex justify-between text-sm">
                        <span className="text-amber-300">
                          After-Hours: {pricingPreview.afterhours_fee.student_count} × ${pricingPreview.afterhours_fee.per_student.toFixed(2)}
                        </span>
                        <span className="text-amber-300 font-medium">${pricingPreview.afterhours_fee.amount.toFixed(2)}</span>
                      </div>
                    )}
                    
                    {/* Travel Fee */}
                    {pricingPreview.mileage && (
                      <div className="flex justify-between text-sm">
                        <span className="text-white">
                          Travel: {pricingPreview.mileage.miles} mi × ${pricingPreview.mileage.rate_per_mile.toFixed(2)}
                        </span>
                        <span className="text-teal-200 font-medium">${pricingPreview.mileage.subtotal.toFixed(2)}</span>
                      </div>
                    )}
                    
                    {/* Add-Ons */}
                    {pricingPreview.addons && pricingPreview.addons.length > 0 && (
                      <>
                        {pricingPreview.addons.map((addon, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-purple-300">
                              {addon.name} × {addon.quantity}
                            </span>
                            <span className="text-purple-300 font-medium">${(addon.subtotal || addon.total || 0).toFixed(2)}</span>
                          </div>
                        ))}
                      </>
                    )}
                    
                    {/* Coupon Discount */}
                    {couponApplied && (
                      <div className="flex justify-between text-sm">
                        <span className="text-green-400 flex items-center gap-1">
                          <Tag className="h-3 w-3" />
                          Coupon: {couponApplied.code.toUpperCase()}
                        </span>
                        <span className="text-green-400 font-medium">-${couponApplied.discount_amount.toFixed(2)}</span>
                      </div>
                    )}
                    
                    {/* Total */}
                    <div className="flex justify-between text-base pt-2 border-t border-teal-600/50 mt-2">
                      <span className="text-teal-100 font-semibold">Total</span>
                      <span className="text-teal-300 font-bold text-lg">
                        ${couponApplied 
                          ? Math.max(0, pricingPreview.grand_total - couponApplied.discount_amount).toFixed(2)
                          : pricingPreview.grand_total.toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Add-on Booking Pricing */}
                {addonPricing && isAddOnMode && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-white">
                        {addonPricing.student_count} new student{addonPricing.student_count !== 1 ? 's' : ''} × ${addonPricing.price_per_student?.toFixed(2) || '0.00'}
                      </span>
                      <span className="text-purple-200 font-medium">${addonPricing.course_subtotal?.toFixed(2) || '0.00'}</span>
                    </div>
                    
                    {addonPricing.rush_fee > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-red-300">Rush Fee</span>
                        <span className="text-red-300 font-medium">${addonPricing.rush_fee.toFixed(2)}</span>
                      </div>
                    )}
                    
                    {addonPricing.afterhours_fee > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-amber-300">After-Hours Fee</span>
                        <span className="text-amber-300 font-medium">${addonPricing.afterhours_fee.toFixed(2)}</span>
                      </div>
                    )}
                    
                    {/* Coupon Discount for Add-on */}
                    {couponApplied && couponApplied.discount_amount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-green-400 flex items-center gap-1">
                          <Tag className="h-3 w-3" />
                          Coupon: {couponApplied.code.toUpperCase()}
                        </span>
                        <span className="text-green-400 font-medium">-${couponApplied.discount_amount.toFixed(2)}</span>
                      </div>
                    )}
                    
                    <div className="flex justify-between text-base pt-2 border-t border-purple-600/50 mt-2">
                      <span className="text-purple-100 font-semibold">Total</span>
                      <span className="text-purple-300 font-bold text-lg">
                        ${couponApplied && couponApplied.discount_amount > 0 
                          ? Math.max(0, addonPricing.grand_total - couponApplied.discount_amount).toFixed(2)
                          : addonPricing.grand_total.toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Public Class Pricing */}
                {publicClassPricing && selectedPublicClass && selectedPublicClass !== 'browse' && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-white">
                        {publicClassPricing.course.student_count} student{publicClassPricing.course.student_count !== 1 ? 's' : ''} × ${publicClassPricing.course.price_per_student.toFixed(2)}
                      </span>
                      <span className="text-amber-200 font-medium">${publicClassPricing.course.subtotal.toFixed(2)}</span>
                    </div>
                    
                    {publicClassPricing.rush_fee && (
                      <div className="flex justify-between text-sm">
                        <span className="text-red-300">Rush Fee</span>
                        <span className="text-red-300 font-medium">${publicClassPricing.rush_fee.amount.toFixed(2)}</span>
                      </div>
                    )}
                    
                    {publicClassPricing.afterhours_fee && (
                      <div className="flex justify-between text-sm">
                        <span className="text-yellow-300">After-Hours Fee</span>
                        <span className="text-yellow-300 font-medium">${publicClassPricing.afterhours_fee.amount.toFixed(2)}</span>
                      </div>
                    )}
                    
                    {publicClassPricing.addons && publicClassPricing.addons.length > 0 && (
                      <>
                        {publicClassPricing.addons.map((addon, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-purple-300">{addon.name} × {addon.quantity}</span>
                            <span className="text-purple-300 font-medium">${(addon.subtotal || addon.total || 0).toFixed(2)}</span>
                          </div>
                        ))}
                      </>
                    )}
                    
                    <div className="flex justify-between text-base pt-2 border-t border-amber-600/50 mt-2">
                      <span className="text-amber-100 font-semibold">Total</span>
                      <span className="text-amber-300 font-bold text-lg">${publicClassPricing.grand_total.toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Coupon/Promo Code Input */}
              <div className="pt-3 border-t border-slate-600/50">
                <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  Promo Code
                </h4>
                {couponApplied ? (
                  <div className="flex items-center justify-between bg-green-900/30 rounded-lg px-3 py-2 border border-green-700/50">
                    <div className="flex items-center gap-2">
                      <Tag className="h-4 w-4 text-green-400" />
                      <span className="text-green-300 text-sm font-medium">{couponApplied.code.toUpperCase()}</span>
                      <span className="text-green-400 text-sm">-${couponApplied.discount_amount.toFixed(2)}</span>
                    </div>
                    <Button
                      onClick={removeCoupon}
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-green-400 hover:text-red-400 hover:bg-transparent"
                      data-testid="remove-coupon-payment-modal"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Enter promo code"
                        value={couponCode}
                        onChange={(e) => {
                          setCouponCode(e.target.value);
                          setCouponError('');
                        }}
                        className="flex-1 h-9 bg-slate-700/50 border-slate-500 text-slate-100 placeholder:text-slate-500"
                        data-testid="coupon-input-payment-modal"
                        onKeyDown={(e) => e.key === 'Enter' && applyCoupon()}
                      />
                      <Button
                        onClick={applyCoupon}
                        disabled={couponLoading || !couponCode.trim()}
                        size="sm"
                        className="h-9 bg-teal-600 hover:bg-teal-500 text-white px-4"
                        data-testid="apply-coupon-payment-modal"
                      >
                        {couponLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
                      </Button>
                    </div>
                    {couponError && (
                      <p className="text-red-400 text-xs flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {couponError}
                      </p>
                    )}
                  </div>
                )}
              </div>
              
              {/* Payment Form - Stripe Inline Card Entry */}
              <div className="min-h-[280px]">
                {paymentProcessing ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-teal-400" />
                    <span className="ml-3 text-white">Processing payment...</span>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Booking Policies Section */}
                    <div className="p-3 bg-amber-900/30 border border-amber-700 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-semibold text-amber-300">Booking Policies</span>
                      </div>
                      
                      <div className="text-xs text-amber-200 space-y-2 mb-3">
                        <p>
                          Once your booking is approved a confirmation email with full class details will be sent to all students.
                        </p>
                        <p>
                          Bookings are based on availability and are subject to change. Our instructors try to approve all bookings within 24 hours from submission.
                        </p>
                        <p>
                          See our full terms and conditions{' '}
                          <button 
                            onClick={openTermsModal}
                            className="text-teal-400 hover:text-teal-300 underline font-medium"
                            data-testid="view-terms-link-payment-policies"
                          >
                            HERE
                          </button>
                        </p>
                      </div>
                      
                      <div className="flex items-start gap-3 py-1">
                        <Checkbox 
                          id="payment-terms"
                          checked={paymentTermsAccepted}
                          onCheckedChange={setPaymentTermsAccepted}
                          className="mt-0.5 border-amber-600 data-[state=checked]:bg-teal-600 data-[state=checked]:border-teal-600"
                        />
                        <label htmlFor="payment-terms" className="text-xs text-amber-300 font-medium leading-snug cursor-pointer">
                          I have read and agree to the booking policies and{' '}
                          <button 
                            onClick={openTermsModal}
                            className="text-teal-400 hover:text-teal-300 underline inline-flex items-center gap-1"
                            data-testid="view-terms-link-payment"
                          >
                            Terms and Conditions
                          </button>
                        </label>
                      </div>
                    </div>
                    
                    {/* Renewal Reminder Opt-in */}
                    <label className="flex items-start gap-3 cursor-pointer p-3 bg-teal-900/30 border border-teal-700/50 rounded-lg">
                      <input
                        type="checkbox"
                        checked={renewalReminderOptIn}
                        onChange={(e) => setRenewalReminderOptIn(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-teal-600 text-teal-500 focus:ring-teal-500 bg-slate-800"
                        data-testid="renewal-reminder-checkbox-payment"
                      />
                      <div>
                        <span className="text-sm text-teal-300 font-medium">
                          Send me certification renewal reminders
                        </span>
                        <p className="text-xs text-slate-400 mt-1">
                          We'll email you 60, 30, and 7 days before your certification expires.
                        </p>
                      </div>
                    </label>
                    
                    {/* Stripe Inline Payment Form */}
                    <StripePaymentForm
                      amount={checkoutData?.total_price || 0}
                      description={`${checkoutData?.class_type_name || 'CPR Class'} - ${checkoutData?.student_count || 1} student(s)`}
                      customerEmail={checkoutData?.students?.[0]?.email || ''}
                      customerName={checkoutData?.students?.[0] ? `${checkoutData.students[0].first_name || ''} ${checkoutData.students[0].last_name || ''}`.trim() : ''}
                      customerPhone={checkoutData?.students?.[0]?.phone || ''}
                      metadata={{
                        class_type_id: checkoutData?.class_type_id || '',
                        student_count: String(checkoutData?.student_count || 1),
                        booking_type: checkoutData?.booking_type || 'regular'
                      }}
                      onSuccess={handleInlinePaymentSuccess}
                      onError={onPaymentError}
                      disabled={paymentProcessing}
                      termsAccepted={paymentTermsAccepted}
                    />
                  </div>
                )}
              </div>
              
              <p className="text-xs text-white/70 text-center mt-4 pb-4 flex items-center justify-center gap-1">
                <Lock className="h-3 w-3" />
                Secure payment powered by Stripe
              </p>
            </div>
          </div>
        )}
      </main>

      {/* Class Details Modal */}
      <Dialog open={showClassDetails} onOpenChange={setShowClassDetails}>
        <DialogContent className="bg-slate-800 border-slate-700 text-slate-100 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Info className="h-5 w-5 text-teal-400" />
              {classDetails?.woo_product?.name || classDetails?.class_type?.name || 'Class Details'}
            </DialogTitle>
            {classDetails?.class_type && (
              <DialogDescription className="text-slate-400">
                Duration: {classDetails.class_type.duration_hours} hours • SKU: {classDetails.class_type.sku}
              </DialogDescription>
            )}
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {classDetails?.woo_product ? (
              <>
                {/* Product Image */}
                {classDetails.woo_product.images?.[0] && (
                  <div className="rounded-lg overflow-hidden">
                    <img 
                      src={classDetails.woo_product.images[0]} 
                      alt={classDetails.woo_product.name}
                      className="w-full h-48 object-cover"
                    />
                  </div>
                )}

                {/* Price - Prefer internal base_price over WooCommerce */}
                {(classDetails.class_type?.base_price || classDetails.woo_product.price) && (
                  <div className="flex items-center gap-2 p-3 bg-teal-900/30 rounded-lg border border-teal-700">
                    <DollarSign className="h-5 w-5 text-teal-400" />
                    <span className="text-lg font-bold text-teal-300">
                      ${classDetails.class_type?.base_price || classDetails.woo_product.price}
                    </span>
                    {classDetails.class_type?.additional_student_price && 
                     classDetails.class_type.additional_student_price !== classDetails.class_type.base_price && (
                      <span className="text-sm text-slate-400">
                        (${classDetails.class_type.additional_student_price} /additional student)
                      </span>
                    )}
                    <span className="text-sm text-slate-400">per person</span>
                  </div>
                )}

                {/* Description */}
                <div className="space-y-2">
                  <h4 className="font-semibold text-slate-200">Description</h4>
                  <div 
                    className="prose prose-invert prose-sm max-w-none text-white"
                    dangerouslySetInnerHTML={{ 
                      __html: classDetails.woo_product.description || classDetails.woo_product.short_description || 'No description available.' 
                    }}
                  />
                </div>

                {/* Link to Website */}
                {classDetails.woo_product.permalink && (
                  <div className="pt-4 border-t border-slate-700">
                    <a
                      href={classDetails.woo_product.permalink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-lg transition-colors"
                    >
                      <ExternalLink className="h-4 w-4" />
                      View on Website
                    </a>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <AlertCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Product details not available from our store.</p>
                <p className="text-sm mt-1">This class may not be linked to a product yet.</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowClassDetails(false)}
              className="border-teal-600 text-white hover:bg-slate-700"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Terms and Conditions Modal */}
      <Dialog open={showTermsModal} onOpenChange={setShowTermsModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-slate-100 max-w-4xl w-[90vw] max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Shield className="h-5 w-5 text-teal-500" />
              {termsTitle}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Please review our terms and conditions before proceeding with your booking.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto pr-2 mt-4">
            {termsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
                <span className="ml-3 text-slate-400">Loading terms and conditions...</span>
              </div>
            ) : (
              <div 
                className="prose prose-invert prose-sm max-w-none
                  prose-headings:text-slate-100 prose-headings:font-semibold
                  prose-p:text-white prose-p:leading-relaxed
                  prose-a:text-teal-400 prose-a:no-underline hover:prose-a:underline
                  prose-strong:text-slate-200
                  prose-ul:text-white prose-ol:text-white
                  prose-li:marker:text-teal-500
                  [&_h1]:text-2xl [&_h1]:mb-4 [&_h1]:mt-6
                  [&_h2]:text-xl [&_h2]:mb-3 [&_h2]:mt-5
                  [&_h3]:text-lg [&_h3]:mb-2 [&_h3]:mt-4
                  [&_p]:mb-3
                  [&_ul]:mb-4 [&_ol]:mb-4
                  [&_li]:mb-1"
                dangerouslySetInnerHTML={{ __html: termsContent }}
                data-testid="terms-content"
              />
            )}
          </div>
          
          <DialogFooter className="mt-4 pt-4 border-t border-slate-700">
            <Button 
              variant="outline" 
              onClick={() => setShowTermsModal(false)}
              className="border-teal-600 text-white hover:bg-slate-700"
              data-testid="close-terms-modal"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Instructors Dropdown - Bottom Left */}
      <div className="fixed bottom-4 left-4 z-50">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="outline" 
              size="sm"
              className="bg-slate-800/90 border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white backdrop-blur-sm"
              data-testid="instructors-dropdown"
            >
              <Users className="h-4 w-4 mr-2" />
              Instructors
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="bg-slate-800 border-slate-700">
            <DropdownMenuItem 
              onClick={() => window.location.href = '/instructor-ecards'}
              className="text-slate-200 hover:bg-slate-700 cursor-pointer"
              data-testid="instructor-ecards-link"
            >
              <CreditCard className="h-4 w-4 mr-2 text-teal-400" />
              Instructor eCards
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => window.location.href = '/rentals'}
              className="text-slate-200 hover:bg-slate-700 cursor-pointer"
              data-testid="rentals-link"
            >
              <Tag className="h-4 w-4 mr-2 text-purple-400" />
              Equipment Rentals
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* AHA Name Mismatch Warning Modal */}
      <Dialog open={ahaMismatchModal.show} onOpenChange={(open) => !open && handleAhaMismatchReject()}>
        <DialogContent className="sm:max-w-md bg-amber-950 border-2 border-amber-500 shadow-2xl shadow-amber-500/20">
          <DialogHeader className="text-center">
            <div className="mx-auto mb-3 w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center">
              <AlertTriangle className="h-10 w-10 text-amber-400" />
            </div>
            <DialogTitle className="text-xl font-bold text-amber-300 text-center">
              ⚠️ Name Mismatch Warning
            </DialogTitle>
            <DialogDescription className="text-slate-300 text-center pt-2">
              The name you entered does not exactly match the AHA records.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-slate-400 text-sm">You entered:</span>
                <span className="text-white font-semibold">{ahaMismatchModal.enteredName}</span>
              </div>
              <div className="border-t border-slate-600/50"></div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400 text-sm">AHA shows:</span>
                <span className="text-amber-300 font-semibold">{ahaMismatchModal.ahaName}</span>
              </div>
            </div>
            
            <p className="text-sm text-slate-400 text-center">
              You can accept this student with the name difference (will be marked for review), 
              or go back to correct the name.
            </p>
          </div>
          
          <DialogFooter className="flex gap-3 sm:justify-center">
            <Button
              variant="outline"
              onClick={handleAhaMismatchReject}
              className="flex-1 border-slate-500 text-slate-300 hover:bg-slate-700 hover:text-white"
            >
              Change Name
            </Button>
            <Button
              onClick={handleAhaMismatchAccept}
              className="flex-1 bg-amber-600 hover:bg-amber-500 text-white font-semibold"
            >
              Accept Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default BookingPage;